import { isLicensed, licenseChipText } from '@/utils/licenseStatus';
import { catalogIdForHwBoard, CATALOG_ID_BY_BOARD_KIND } from '@/utils/deviceCatalogBoard';
import { todayTotal, ownRevenue, money } from '@/lib/deviceToday';
import { effectiveRouterVersion } from '@/utils/routerVersion';
import { useDeviceCatalog } from '@/hooks/useDeviceCatalog';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Skeleton, SkeletonRows } from '@/components/Admin/UI/Skeleton';
import { DeviceDetailsSheet, type DetailSectionData } from '@/components/Admin/DeviceDetailsSheet';
import styled, { keyframes, css } from 'styled-components';
import { toast } from 'react-toastify';
import { runSessionControl } from '@/pages/Admin/sessionControls';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { canHostVlans } from '@/utils/vlanHosts';
import type { RawSys } from '@/utils/systemInfo';
import DeviceCardFlip from '@/components/Admin/Prompt/DeviceCardFlip';
import SystemInfoFace from '@/components/Admin/Prompt/SystemInfoFace';
import { licenseBanner, coinBoxFirstState } from '@/utils/firstHeartbeat';
import { isOrangePiBoard, isOrangePiDeviceId } from '@/utils/orangePi';
import { openPortal } from '@/utils/portalPreview';

import Pagination from '@/components/Admin/UI/Pagination';
import RevenueAreaChart, { vendoChartOptions } from '@/components/Admin/UI/RevenueAreaChart';
import { uptime, type Uptime } from '@/utils/uptime';
import { CoinTicker } from '@/components/Admin/CoinTicker';
import { currencyFormat } from '@/utils/currencyFormatter';
import {
   kickSession as apiKickSession,
   setSessionNoteApi,
   setUnlimitedNoteApi,
   setSessionPauseLeftApi,
   resetDeviceStats as apiResetDeviceStats,
   getCredits,
   lookupTransferRecipientApi,
   transferCreditApi,
   setWhitelistScope,
   listDhcpLeases,
   getOwnershipClaims,
   grantOwnershipClaim,
   denyOwnershipClaim,
   type OwnershipClaim,
   ApiVendoDevice,
   ApiVendoSession,
   WhitelistEntry,
   type PortalConfig,
   type SessionEntry,
} from '@/api/vendo';
import {
   extendableSessionIds,
   isWhitelistRowId,
   parseWhitelistRowId,
   computeAwaitingDevices,
   friendlyDeviceName,
   phoneNamesFromLeases,
   vendoNameForLease,
   normalizeMac,
   type DeviceLeases,
   type AwaitingDevice,
} from '@/utils/sessionActions';
import { useUIStore } from '@/store/useUIStore';
import { takeSetupDraft } from '@/utils/setupDraft';
import { getSetupDraft } from '@/api/setupDraft';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import { useReflashingStore } from '@/store/useReflashingStore';
import { useVendoSessionsStore } from '@/store/useVendoSessionsStore';
import useDeviceListPrefsStore, {
   DeviceSort,
   DeviceOwnershipFilter,
   DeviceHealthFilter,
   DeviceBoardFilter,
} from '@/store/useDeviceListPrefsStore';
import { formatRelativeDate } from '@/utils/formatRelativeDate';
import FreePromoPanel from '@/components/Admin/FreePromoPanel';
import AnnouncementPanel from '@/components/Admin/AnnouncementPanel';
import { formatPauseLeft } from '@/utils/pauseLeft';
import DurationUnitInput from '@/components/Admin/UI/DurationUnitInput';
import { toLocalDeadline } from '@/utils/serverClock';
import {
   isSendAmountValid,
   blocksDistributorRecipient,
} from '@/utils/creditGates';
import RouterPill from '@/components/Admin/Vendo/RouterPill';
import DeviceStatusBadge, {
   compositeStatus,
} from '@/components/Admin/Vendo/DeviceStatusBadge';
import UptimeCell from '@/components/Admin/UptimeCell';
import {
   signal,
   rejoinRate,
   antenna,
   type Signal,
   type RejoinRate,
   type Antenna,
} from '@/utils/linkHealth';
import {
   SignalCell,
   RejoinCell,
   AntennaCell,
} from '@/components/Admin/LinkHealthCells';
import CreditTransfersTable from '@/components/Admin/CreditTransfersTable';
import IconsLib from '@/utils/IconsLib';
import PendingTransfers, {
   usePendingOutgoingDeviceIds,
} from '@/components/Admin/Vendo/PendingTransfers';
import { useFocusFromQuery } from '@/hooks/useFocusFromQuery';

// Portal lives on its own subdomain (portal.mysmartfi.ph), NOT on the
// admin client (app.smartfi.ph). The relative `/portal/<slug>` form
// would resolve against window.location and 404 against the admin SPA
// router. Match the pattern used by QrSticker.tsx so the env override
// works the same way for local dev.
const PORTAL_BASE =
   import.meta.env.VITE_PORTAL_BASE_URL || (window.location.hostname === 'app-staging.smartfi.ph' ? 'https://portal-staging.mysmartfi.ph' : 'https://portal.mysmartfi.ph');

/* ─── types ────────────────────────────────────────────────── */

type DeviceType = 'main' | 'sub';
type ViewMode = 'card' | 'list';

export type LicenseStatus = 'unactivated' | 'trial' | 'active' | 'expired' | 'lifetime';

export interface VendoDevice {
   /** Mongo _id — needed for /vendo/devices/:id/router-script */
   _id: string;
   id: string;
   name: string;
   /** Captive-portal slug — used for QR sticker URL */
   portalSlug: string;
   /** Captive-portal theme — drives the customer-facing portal appearance */
   portalTheme: 'gaming' | 'midnight' | 'clean' | 'sunset' | 'ocean' | 'custom';
   /** Customizable-theme config — only meaningful when portalTheme === 'custom'. */
   portalConfig?: PortalConfig | null;
   /** MikroTik router model — drives the model badge + sub-vendo gating. */
   model?: 'hex-rb750gr3' | 'rb4011' | 'ccr2004' | 'ccr2116';
   /** Router platform. 'openwrt' devices keep the default hEX model, so the
    *  badge label comes from platform + hwBoard instead. */
   platform?: 'mikrotik' | 'openwrt';
   /** OpenWRT board id (e.g. "ruijie,..."), mapped to a hardware badge. */
   hwBoard?: string;
   /** Portal mode from Device Settings → Portal Theme. 'voucher' = no NodeMCU needed. */
   portalMode?: 'coin' | 'voucher' | 'both';
   /** Product type. Missing means cloud — hybrid came later and legacy rows
    *  predate it, the same rule the Firmware panel uses for legacy images. */
   variant?: 'cloud' | 'hybrid';
   /** How a MikroTik RUNS: true = the hEX is the portal (a sub-vendo carries its main's). */
   localMode?: boolean;
   /** Firmware waiting for this device, computed server-side against ITS OWN
    *  product type and only when strictly newer. count 0 = nothing to do. */
   updatesAvailable?: {
      router: string | null;
      coinBox: string | null;
      count: number;
   };
   type: DeviceType;
   parentId?: string;
   status: 'online' | 'offline';
   /** Server-derived: 'connected' | 'agent-down' | 'disconnected' | 'unprovisioned'.
    *  Falls back to status-derived value if server hasn't shipped Phase 2 yet. */
   routerStatus: 'connected' | 'agent-down' | 'disconnected' | 'unprovisioned';
   /** How long it has been up, or down — computed once in the mapper so every
    *  view shows the same number. Null when it cannot be anchored (a box on
    *  firmware older than 1.0.24 reports no history, and lastSeenAt alone would
    *  read as "up just now" forever). */
   uptime: Uptime | null;
   /** What the router says about its own condition — the System Info face.
    *  Null on a router that has never reported any of it (2.0.x). Every field
    *  inside is nullable and null means "did not say", never zero. */
   sys?: RawSys | null;
   /** The self-report. Carries free space and restarts since 2.1.6, which is
    *  most of the fleet — the System Info face falls back to it. */
   diag?: {
      freeKb?: number | null;
      reboots24h?: number;
      outage?: string;
   } | null;
   /** When the ROUTER last spoke. Drives "reported N min ago" on the System
    *  Info face — freshness must never come from the reading's own timestamp,
    *  because a stale report always looks healthy to itself. */
   routerLastSeenAt?: string | null;
   /** Coin-box radio strength with its plain-English band. Null when the box is
    *  wired — a wired box reports 0 dBm, which must never read as "Excellent". */
   signal: Signal | null;
   /** Reconnects per hour of uptime. Null under 30 minutes of uptime: the raw
    *  counter resets on every reboot, so a young box has no honest rate. */
   rejoins: RejoinRate | null;
   /** Which antenna the NodeMCU is on, and whether it is its own router's. */
   antenna: Antenna | null;
   /** NodeMCU liveness, independent of the router. 'none' = no
    *  NodeMCU paired (e.g. a voucher-only Lite); 'online'/'offline' from the
    *  NodeMCU's own heartbeat freshness. Surfaces a dead NodeMCU on a Lite
    *  router that the router agent otherwise keeps "online". */
   coinbox: 'none' | 'online' | 'offline';
   /** A NodeMCU was named at registration but never flashed for this vendo
    *  and never reported (BBLuv RJE-676CAE18, 2026-10-30): the next step is the
    *  flash, not a power socket. */
   nodeMcuNeverFlashed?: boolean;
   /** The vendo has checked in at least once (router agent, REST probe or
    *  NodeMCU). A license can only move onto one that has (A46, 2026-10-30). */
   everCheckedIn?: boolean;
   /** VLAN identity for sub-vendos on a VLAN-enabled cluster. Empty on
    *  mains and on pre-VLAN subs (flat-LAN deployments). */
   vlanId?: number;
   vlanSubnet?: string;
   vlanPort?: string;
   /** False on devices the user previously sold but still surfaces in
    *  the dashboard for audit-trail visibility. Drives the SOLD badge
    *  + finance-field redaction. */
   isCurrentOwner: boolean;
   soldTransfer?: {
      toEmail: string;
      acceptedAt: string | null;
      fullCashAmount: number | null;
   };
   lastHeartbeat: string;
   todayCoins: number;
   totalCoins: number;
   /** Lifetime — survives Reset Stats. */
   lifetimeCoins: number;
   /** Printed voucher sales today / all-time, and GCash all-time (Ray 2026-09-15: revenue includes vouchers). */
   todayVouchers: number;
   voucherRevenue: number;
   gcashRevenue: number;
   /** Online sales today, net (what the operator receives). */
   todayOnline: number;
   activeSessions: number;
   totalSessions: number;
   router: string;
   firmware: string;
   /** Router firmware/overlay version (e.g. "1.0.12-72661b0"). */
   routerFirmware: string;
   /** No-reboot update installed on top of the image ("2.6.23"), or ''. */
   routerBundle: string;
   localIp: string;
   mac: string;
   licenseStatus: LicenseStatus;
   /** When the one-shot 14-day trial actually began — i.e. the first heartbeat.
    *  null on a device whose hardware has never checked in, which is what tells
    *  "the trial has not started" apart from "the trial was already used". */
   trialStartedAt?: string | null;
   /** Days remaining until trial end (when licenseStatus = 'trial') */
   trialDaysLeft?: number;
   /** ISO end date of an old 1-year licence ('active'); none on 'lifetime' */
   licenseExpiresAt?: string;
   /** ISO date the license was last moved onto this device — drives the
    *  24-hour move-cooldown indicator in Manage License. */
   licenseMovedAt?: string | null;
   /** When true, the license-expiry cron auto-deducts 1 credit at
    *  trial/license end (only if owner has credit + device is online). */
   autoRenewLicense: boolean;
   /** Set on the original owner's device when its hardware was claimed away by
    *  another account: 'sold' (granted) or 'stolen' (denied). Drives the
    *  "🔌 No router" banner. */
   hardwareReleasedReason?: 'sold' | 'stolen' | null;
   /** ISO time a reflash finished; the router restarts its REST for 1-2 min.
    *  Drives the "⏳ Setting up the router" card banner. */
   reflashFinishingAt?: string | null;
   /** Why this device is ownership-locked (claimant side): 'pending' (awaiting
    *  the real owner) or 'stolen' (denied → blocked). */
   ownershipLockReason?: 'pending' | 'stolen' | null;
   /** Owner flagged THEIR OWN device lost/stolen (Phase 6) — portal is locked
    *  until they remove the flag in Settings. */
   markedStolen?: boolean;
   /** True when the router looks online but the cloud's probe can NOT reach
    *  it inbound — instant grants/kicks and the Awaiting tab silently
    *  degrade in that state. Drives the orange reach-warning pill. */
   inboundUnreachable: boolean;
   /** Live Lite OTA progress from the router agent (cleared once on target).
    *  Drives the firmware-update pill on the card. */
   otaStatus?: {
      state: string;
      version: string;
      attempts: number;
      error: string;
   } | null;
   /** Server-derived update/reboot state — drives the "Updating/Rebooting"
    *  card pill (2d), covering the reboot gap where otaStatus is cleared. */
   updateState?: 'idle' | 'updating' | 'rebooting';
   /** Partner label if bought via retailer — optional */
   partner?: string;
   /** Wizard progress — drives the "Setup in progress" section */
   setupStatus: 'flashing' | 'configuring-router' | 'connecting-portal' | 'done';
   /** Partner/host devices granted free permanent internet (whitelist). */
   whitelistedMacs: WhitelistEntry[];
   /** ISO datetime of registration — used for newest-first sorting */
   createdAt: string;
}

// Short model label + sub-vendo cap for the dashboard. Mirrors the server's
// utils/routerModels.ts (server is source of truth); this client copy drives
// the model badge on device cards/rows and the sub-vendo "not supported" guard.
const ROUTER_MODEL_META: Record<
   NonNullable<VendoDevice['model']>,
   { label: string; subvendoCap: number }
> = {
   'hex-rb750gr3': { label: 'hEX', subvendoCap: 20 },
   'rb4011': { label: 'RB4011', subvendoCap: 100 },
   'ccr2004': { label: 'CCR2004', subvendoCap: 500 },
   'ccr2116': { label: 'CCR2116', subvendoCap: 500 },
};

// Friendly hardware label from an OpenWRT board id (/tmp/sysinfo/board_name the
// agent reports). The badge names the actual hardware — operators already know
// the tier, they want to know which box. Falls back to "Ruijie" (the only board
// shipped to date) when a device hasn't reported its board yet.
const OPENWRT_BOARD_LABELS: ReadonlyArray<readonly [RegExp, string]> = [
   [/ruijie/i, 'Ruijie'],
   // OrangePi PC and Zero 3 = their own images: exact ids, BEFORE the generic row.
   [/^xunlong,orangepi-pc$/i, 'OrangePi PC'],
   [/^xunlong,orangepi-zero3$/i, 'OrangePi Zero 3'],
   [/orangepi|xunlong/i, 'OrangePi'],
   [/cf-ew71/i, 'Comfast'],
   [/cf-ew72/i, 'Comfast EW72'],
   // Matched on the OpenWRT model, never the vendor — 'creatlentem' and 'edup'
   // each ship other boards on different silicon.
   [/clt-r30b1/i, 'SmartFi AX3000'],
   [/ep-rt2983|netis,n6/i, 'SmartFi AX1800'],
   [/newifi-d2/i, 'Newifi D2'],
   // 360 T6M = Wi-Fi 6 AX1800 (MT7621 + MT7915, 128MB NAND) — its OWN image,
   // never the Netis N6 / EDUP RT2983 one. The router reports EXACTLY one of two
   // board ids: 360,t6m (our own device tree) or 360,360t6m (the ImmortalWrt port).
   [/^360,(360)?t6m$/i, '360 T6M (AX1800)'],
   // The agent tags an x86 router with its OpenWrt target, because the BIOS name
   // behind it varies per unit and is blank on a no-name mini PC.
   [/^x86-64,/i, 'Mini PC'],
   [/raspberry|bcm27|rpi|raspberrypi/i, 'Raspberry Pi'],
   [/nanopi|friendlyarm/i, 'NanoPi'],
   [/xiaomi|redmi/i, 'Xiaomi'],
   [/(^|[^a-z])x86([^a-z]|$)|generic/i, 'x86'],
];
const openwrtBoardLabel = (board?: string): string => {
   if (board) {
      for (const [re, label] of OPENWRT_BOARD_LABELS) if (re.test(board)) return label;
   }
   return 'Ruijie';
};

// Firmware OTA pill (Lite). The agent reports otaStatus only while a flash is
// in-flight or has failed, so the pill appears just during those windows.
type OtaStatus = { state: string; version: string; attempts: number; error: string };
const otaPillLabel = (o: OtaStatus): string =>
   o.state === 'failed'
      ? '⚠ update failed'
      : o.state === 'flashing'
        ? `⟳ flashing ${o.version}`
        : `⬇ updating ${o.version}`;
const otaPillTitle = (o: OtaStatus): string =>
   o.state === 'failed'
      ? `Firmware update to ${o.version} failed after ${o.attempts} attempt(s)` +
        `${o.error ? ` (${o.error})` : ''} — retries on next reboot`
      : `Updating firmware to ${o.version} (attempt ${o.attempts})`;

// Badge label for a device's router. OpenWRT devices keep the server's default
// hEX model, so name the hardware from platform + board id; MikroTik devices use
// the model label (hEX / RB4011 / CCR…).
/* Cloud or hybrid, for the badge next to the board name. A device with no
   recorded type IS cloud: hybrid was introduced later and every device predating
   it is cloud. Never renders blank — "no badge" would read as missing data. */
const deviceVariantLabel = (variant?: string | null): 'Cloud' | 'Hybrid' =>
   variant === 'hybrid' ? 'Hybrid' : 'Cloud';
/**
 * The badge says how the device RUNS. A MikroTik's variant flips the moment
 * the Switch button is pressed, before the switch completes, and a sub-vendo
 * has no variant of its own -- it runs the way its main runs (router.localMode
 * is copied onto it). Every OpenWrt board's variant is the truth. Ray,
 * 2026-10-07: "a pill badge on per device card ... hybrid or cloud".
 */
const runningModeLabel = (d: { platform?: string; variant?: string | null; localMode?: boolean }): 'Cloud' | 'Hybrid' =>
   d.platform === 'mikrotik' ? (d.localMode ? 'Hybrid' : 'Cloud') : deviceVariantLabel(d.variant);

const modelBadgeLabel = (
   d: Pick<VendoDevice, 'platform' | 'model' | 'hwBoard'>,
   /** The device catalog's name for this hardware, when it has one. The
    *  built-in map below stays the answer until it loads, and if it never
    *  loads — a badge must never come out blank. */
   catalogLabel?: (id: string | undefined, fallback: string) => string
): string | null => {
   if (d.platform === 'openwrt') {
      const built = openwrtBoardLabel(d.hwBoard);
      return catalogLabel ? catalogLabel(catalogIdForHwBoard(d.hwBoard), built) : built;
   }
   return d.model && ROUTER_MODEL_META[d.model]
      ? ROUTER_MODEL_META[d.model].label
      : null;
};

/** The hardware badge, with the device catalog's name when it has one.
 *  Its own small component so the three places that draw it share one read
 *  (react-query answers the rest from cache) and none of them can forget the
 *  fallback. */
const ModelBadgeFor: React.FC<{
   d: Pick<VendoDevice, 'platform' | 'model' | 'hwBoard'>;
}> = ({ d }) => {
   const { labelFor } = useDeviceCatalog();
   const label = modelBadgeLabel(d, labelFor);
   return label ? <ModelBadge>{label}</ModelBadge> : null;
};

/** The same name as plain text, for the table column. */
const ModelNameFor: React.FC<{
   d: Pick<VendoDevice, 'platform' | 'model' | 'hwBoard'>;
}> = ({ d }) => {
   const { labelFor } = useDeviceCatalog();
   return <>{modelBadgeLabel(d, labelFor) ?? '—'}</>;
};

/**
 * Health + board filters for the device list. One predicate, used by BOTH
 * the Dashboard and the Devices tab — the ownership filter had already
 * drifted into two copies, and a third pair would drift too.
 *
 * Health is the COMPOSITE pill the card actually shows (NodeMCU × router),
 * not `device.status`. Filtering on the raw status would hide the case the
 * filter exists for: a vendo whose router is fine and whose NodeMCU is dead
 * reads "Degraded" on screen but "online" in the field.
 *
 * Board is named the way Ray names it — Ruijie, OrangePi, MikroTik. Both
 * OpenWRT boards are internally "Lite"; that word is never a filter option
 * because it does not tell him which hardware he is looking at.
 */
export const matchesDeviceFilters = (
   d: VendoDevice,
   health: DeviceHealthFilter,
   board: DeviceBoardFilter
): boolean => {
   if (health === 'online-or-degraded') {
      // "Everything that is still taking coins or could be" — a Degraded vendo
      // has a live router and a dead NodeMCU, and belongs beside Online here.
      const c = compositeStatus(d.status, d.routerStatus);
      if (c !== 'online' && c !== 'degraded') return false;
   } else if (health !== 'all' && compositeStatus(d.status, d.routerStatus) !== health) {
      return false;
   }
   if (board !== 'all') {
      // The option is a catalog id (or 'mikrotik'); a pre-2026-09-29 saved
      // value like 'ruijie' / 'orangepi' is mapped to its catalog id.
      const wanted = CATALOG_ID_BY_BOARD_KIND[board] ?? board;
      const matched =
         wanted === 'mikrotik'
            ? d.platform === 'mikrotik'
            : d.platform === 'openwrt' && catalogIdForHwBoard(d.hwBoard) === wanted;
      if (!matched) return false;
   }
   return true;
};

// NodeMCU liveness from its own heartbeat. The mac is only set once
// a NodeMCU has reported, so no mac = none paired. 6 min ≈ a couple missed
// beats — a dead NodeMCU on a Lite router stays "online" via the router agent,
// so this is the only signal an operator has that coin acceptance died.
const COINBOX_FRESH_MS = 6 * 60 * 1000;
const deriveCoinbox = (
   mac?: string,
   lastHeartbeat?: string | null
): VendoDevice['coinbox'] => {
   if (!mac) return 'none';
   if (!lastHeartbeat) return 'offline';
   return Date.now() - new Date(lastHeartbeat).getTime() < COINBOX_FRESH_MS
      ? 'online'
      : 'offline';
};

/** Is this vendo the one in focus? No focus = every vendo is. */
export const inVendoFocus = (focusId: string, deviceId: string): boolean =>
   !focusId || deviceId === focusId;

/** A session belongs to the vendo it ran on; an Unlimited row belongs to
 *  EVERY vendo the phone is Unlimited on. */
export const sessionInVendoFocus = (
   focusId: string,
   s: { vendoDevice?: string | null; vendoDevices?: string[] }
): boolean =>
   !focusId || s.vendoDevice === focusId || (s.vendoDevices ?? []).includes(focusId);

/**
 * The search box over the device list. Matches the name, the device id
 * (ESP-…), the portal address and the LAN IP — the four things an operator
 * has in front of them when they ask "where is this one". Case and spacing
 * do not matter. An empty box matches everything.
 */
export const matchesDeviceSearch = (
   d: { name?: string; id?: string; portalSlug?: string; localIp?: string },
   search: string
): boolean => {
   const needle = search.trim().toLowerCase();
   if (!needle) return true;
   return [d.name, d.id, d.portalSlug, d.localIp].some((v) =>
      (v || '').toLowerCase().includes(needle)
   );
};

/**
 * The hardware dropdown's options: MikroTik plus every board in the device
 * catalog (Admin Panel → Settings → Devices), in the catalog's own order and
 * with the admin's names. Until 2026-09-29 this was a hard-coded three —
 * Comfast, AX1800, AX3000, Newifi D2 and the mini PC could not be filtered
 * for at all. Before the catalog answers, the built-in boards stand in.
 */
const BUILT_IN_BOARD_OPTIONS: { value: string; label: string }[] = [
   { value: 'mikrotik', label: 'MikroTik' },
   { value: 'ruijie-rg-ew1200g-pro-v1.1', label: 'Ruijie EW1200G Pro' },
   { value: 'orangepi-one', label: 'OrangePi One' },
   { value: 'orangepi-pc', label: 'OrangePi PC' },
   { value: 'orangepi-zero3', label: 'OrangePi Zero 3' },
   { value: 'comfast-cf-ew71-v2', label: 'Comfast EW71' },
   { value: 'comfast-cf-ew72-v2', label: 'Comfast EW72' },
   { value: 'rt2980', label: 'SmartFi AX3000' },
   { value: 'rt2983', label: 'SmartFi AX1800' },
   { value: 'newifi-d2', label: 'Newifi D2' },
   { value: 'x86-64', label: 'Mini PC' },
];
export const boardFilterOptions = (
   entries: { id: string; label: string }[]
): { value: string; label: string }[] => {
   if (entries.length === 0) return BUILT_IN_BOARD_OPTIONS;
   const fromCatalog = entries.map((e) => ({ value: e.id, label: e.label }));
   return fromCatalog.some((o) => o.value === 'mikrotik')
      ? fromCatalog
      : [{ value: 'mikrotik', label: 'MikroTik' }, ...fromCatalog];
};
const useBoardFilterOptions = () => {
   const { entries } = useDeviceCatalog();
   return useMemo(() => boardFilterOptions(entries), [entries]);
};

const DEVICES_VIEW_STORAGE_KEY = 'smartfi-vendo-devices-view';
const SESSIONS_FILTER_STORAGE_KEY = 'smartfi-vendo-sessions-filter';

type SessionsFilterState = {
   statusFilter: string;
   deviceFilter: string;
};

// Tab labels that aren't just the capitalised key.
const FILTER_LABELS: Record<string, string> = {
   active_paused: 'Active & Paused',
   unlimited: 'Unlimited',
};

const loadSessionsFilter = (): SessionsFilterState => {
   try {
      const saved = localStorage.getItem(SESSIONS_FILTER_STORAGE_KEY);
      if (saved) {
         const parsed = JSON.parse(saved) as Partial<SessionsFilterState>;
         return {
            statusFilter: typeof parsed.statusFilter === 'string' ? parsed.statusFilter : 'all',
            deviceFilter: typeof parsed.deviceFilter === 'string' ? parsed.deviceFilter : 'all',
         };
      }
   } catch {}
   return { statusFilter: 'all', deviceFilter: 'all' };
};

type DevicesViewState = {
   expanded: string[];
};

// Only `expanded` lives here now. View mode + sort + ownership filter
// + showAllSubs moved into useDeviceListPrefsStore so the Dashboard and
// the Devices tab share them — see the store's header for the why.
const loadDevicesView = (): DevicesViewState => {
   try {
      const saved = localStorage.getItem(DEVICES_VIEW_STORAGE_KEY);
      if (saved) {
         const parsed = JSON.parse(saved) as Partial<DevicesViewState>;
         return {
            expanded: Array.isArray(parsed.expanded) ? parsed.expanded : [],
         };
      }
   } catch {}
   return { expanded: [] };
};

/* ─── real → display mapper ────────────────────────────────── */

const timeAgo = (iso: string | null | undefined): string => {
   if (!iso) return 'never';
   const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
   if (seconds < 10) return 'just now';
   if (seconds < 60) return `${seconds}s ago`;
   if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
   if (seconds < 86400) return `${Math.floor(seconds / 3600)} hrs ago`;
   return `${Math.floor(seconds / 86400)} days ago`;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Firmware "base" version — drops the CI build suffix ("1.0.12-<sha>" → "1.0.12").
const fwBase = (v?: string | null): string => String(v ?? '').split('-')[0];

// A firmware value worth showing: present and not a placeholder/unknown.
const hasVer = (v?: string | null): boolean =>
   !!v && v !== '—' && v.toLowerCase() !== 'unknown';

// Wizard steps that mean "still in progress" — listed in setupStatus
// order so the indicator can highlight the right dot. Step 5 (done)
// isn't here because done devices don't show in this section.
const INCOMPLETE_STEP_ORDER = [
   'flashing',
   'configuring-router',
   'connecting-portal',
] as const;

const SETUP_STATUS_LABEL: Record<string, string> = {
   flashing: 'Flash NodeMCU',
   'configuring-router': 'Configure Router',
   'connecting-portal': 'Connect Portal',
   done: 'Done',
};

// Exported for the admin fleet table — it opens the same device-settings
// modal and needs the identical API→display mapping.
export const mapApiToDisplay = (d: ApiVendoDevice): VendoDevice => {
   const trialDaysLeft = d.trialEndsAt
      ? Math.max(0, Math.ceil((new Date(d.trialEndsAt).getTime() - Date.now()) / MS_PER_DAY))
      : undefined;
   // NodeMCU liveness from its own heartbeat.
   const coinbox = deriveCoinbox(d.nodeMcu?.mac, d.nodeMcu?.lastHeartbeat);
   // On Lite (openwrt) the device's "heartbeat" signal IS the NodeMCU — same as
   // MikroTik, where device.status = the NodeMCU heartbeat. The router's own
   // liveness is the separate routerStatus pill. So the composite badge shows
   // 🟡 Degraded when the router is up but the NodeMCU is down — exactly the
   // MikroTik behavior. (Server device.status, which the router agent also sets,
   // is left untouched — this is display-only.)
   const isOpenwrt = d.router?.platform === 'openwrt';
   // OrangePi has an ON-BOARD coin reader (no NodeMCU) and a wired uplink — there
   // is no separate NodeMCU to check in, so its coin liveness IS the router's.
   // Without this it reads coinbox='none'→'offline'→a permanent false Degraded.
   // Ruijie/MikroTik keep the NodeMCU-heartbeat signal (a missing NodeMCU there
   // IS a real problem).
   const isOrangePi = isOrangePiBoard(d.router?.hwBoard);
   const routerStatusResolved =
      d.routerStatus ??
      (d.status === 'online' ? 'connected' : 'disconnected');
   const openwrtCoinStatus = isOrangePi
      ? routerStatusResolved === 'connected'
         ? 'online'
         : 'offline'
      : coinbox === 'online'
        ? 'online'
        : 'offline';
   return {
      _id: d._id,
      id: d.deviceId,
      name: d.name,
      portalSlug: d.portalSlug,
      portalTheme: d.portalTheme || 'gaming',
      portalConfig: d.portalConfig ?? null,
      model: d.router?.model,
      platform: d.router?.platform,
      hwBoard: d.router?.hwBoard,
      portalMode: d.settings?.portalMode,
      variant: d.router?.variant,
      localMode: d.router?.localMode === true,
      updatesAvailable: (d as any).updatesAvailable,
      coinbox,
      everCheckedIn: !!(d.router?.lastSeenAt || d.router?.lastRestOkAt || d.lastHeartbeat || d.nodeMcu?.lastHeartbeat),
      nodeMcuNeverFlashed:
         !isOrangePi &&
         !!d.nodeMcu?.mac &&
         coinBoxFirstState({ lastFlashedAt: d.nodeMcu?.lastFlashedAt, lastHeartbeat: d.nodeMcu?.lastHeartbeat }) === 'never-flashed',
      // Parent linkage drives main/sub display. ApiVendoDevice.parent is
      // null for main vendos, the parent's _id (string) for subs.
      type: d.parent ? 'sub' : 'main',
      parentId: d.parent ?? undefined,
      status: isOpenwrt
         ? openwrtCoinStatus
         : d.status === 'error'
           ? 'offline'
           : d.status,
      routerStatus: routerStatusResolved,
      // How long it has been up, or down. Computed HERE because this is where
      // the raw API device is in scope; the card and the list both read the
      // finished value so they can never disagree. Null when we cannot anchor
      // it — see utils/uptime.ts for why that matters.
      uptime: uptime({
         lastSeenAt: d.router?.lastSeenAt,
         events: d.router?.diag?.events,
         // Without this the clock counts straight through a power cut —
         // 80 vendos did on 2026-08-23. See utils/uptime.ts.
         outage: d.router?.diag?.outage,
      }),
      // Passed through untouched. Every "did not say" decision belongs to
      // utils/systemInfo, in one place — a default applied here would silently
      // turn silence into a reading before the card ever sees it, which is
      // exactly the mistake that made 5D ALCARDE look out of disk space.
      sys: d.router?.sys ?? null,
      diag: d.router?.diag ?? null,
      // A MikroTik runs no agent, so it never "speaks" — its readings come
      // from the 5-minute REST probe. Date the card by that probe instead.
      routerLastSeenAt: d.router?.lastSeenAt ?? d.router?.lastRestOkAt ?? null,
      // Both derived here so the operator's list and the admin fleet list read
      // identically — an operator quoting "-72, Weak" must mean what support sees.
      signal: signal(d.nodeMcu?.wifiRssi, d.nodeMcu?.connMode),
      rejoins: rejoinRate(
         d.nodeMcu?.wifiReconnects,
         d.nodeMcu?.uptimeSec,
         d.nodeMcu?.connMode
      ),
      antenna: antenna(d.nodeMcu, d.router),
      // VLAN id for the list pill: redesign subs carry top-level vlanRef
      // (attached to a parent zone); legacy subs carry router.vlan.id.
      vlanId: d.vlanRef ?? d.router?.vlan?.id ?? undefined,
      vlanSubnet: d.router?.vlan?.subnet ?? undefined,
      vlanPort: d.router?.vlan?.port ?? undefined,
      isCurrentOwner: d.isCurrentOwner ?? true,
      soldTransfer: d.soldTransfer,
      // OrangePi has no NodeMCU — the coin reader, firmware, and heartbeat all
      // live on the router itself. Show the router's values instead of the empty
      // NodeMCU fields (blank firmware/MAC + "never" heartbeat on a healthy box).
      lastHeartbeat: isOrangePi
         ? timeAgo(d.router?.lastSeenAt ?? null)
         : timeAgo(d.lastHeartbeat),
      todayCoins: d.stats?.todayCoins ?? 0,
      totalCoins: d.stats?.totalCoins ?? 0,
      lifetimeCoins: d.stats?.lifetimeCoins ?? 0,
      todayVouchers: d.stats?.todayVouchers ?? 0,
      voucherRevenue: d.stats?.voucherRevenue ?? 0,
      gcashRevenue: d.stats?.gcashRevenue ?? 0,
      todayOnline: d.stats?.todayOnline ?? 0,
      activeSessions: d.stats?.activeSessions ?? 0,
      totalSessions: d.stats?.totalSessions ?? 0,
      router: d.router?.ip || '—',
      firmware: isOrangePi
         ? d.router?.firmwareVersion || '—'
         : d.nodeMcu?.firmwareVersion || '—',
      routerFirmware: d.router?.firmwareVersion || '—',
      routerBundle: d.router?.bundleVersion || '',
      localIp: '—',
      mac: isOrangePi ? 'on-board' : d.nodeMcu?.mac || '—',
      licenseStatus: d.licenseStatus,
      trialStartedAt: d.trialStartedAt ?? null,
      trialDaysLeft,
      licenseExpiresAt: d.licenseEndsAt ?? undefined,
      licenseMovedAt: d.licenseMovedAt ?? null,
      autoRenewLicense: d.autoRenewLicense ?? false,
      hardwareReleasedReason: d.hardwareReleasedReason ?? null,
      reflashFinishingAt: d.reflashFinishingAt ?? null,
      ownershipLockReason: d.ownershipLockReason ?? null,
      markedStolen: d.markedStolen ?? false,
      // Warn ONLY while the router itself reads connected — an offline box
      // being unreachable is expected, not a finding.
      // ORP (hybrid) boxes only for now: the first fleet sweep found ~50
      // RUIJIE boxes in this state, all from the known missing firewall
      // rule in their older firmware — a reboot can't fix those, so a
      // fleet-wide pill would only trigger pointless reboots + support
      // load. Re-widen when the Ruijie firmware fix ships. (Data is still
      // recorded for every box — the admin can query it.)
      inboundUnreachable:
         (isOrangePiDeviceId(d.deviceId) || isOrangePiBoard(d.router?.hwBoard)) &&
         routerStatusResolved === 'connected' &&
         d.router?.inboundReachable === false,
      otaStatus: d.router?.otaStatus
         ? {
              state: d.router.otaStatus.state,
              version: d.router.otaStatus.version,
              attempts: d.router.otaStatus.attempts,
              error: d.router.otaStatus.error,
           }
         : null,
      updateState: d.updateState ?? 'idle',
      setupStatus: d.setupStatus ?? 'done',
      whitelistedMacs: d.whitelistedMacs ?? [],
      createdAt: d.createdAt,
   };
};

// True for ~3 min after a reflash finishes — the router restarts its REST/www
// while config applies, so the card briefly looks offline. Drives the
// "Setting up the router" banner so that window doesn't read as a failure.
const REFLASH_WINDOW_MS = 3 * 60 * 1000;
const isReflashing = (d: VendoDevice): boolean => {
   if (!d.reflashFinishingAt) return false;
   const t = new Date(d.reflashFinishingAt).getTime();
   return Number.isFinite(t) && Date.now() - t < REFLASH_WINDOW_MS;
};

/* ─── display types ────────────────────────────────────────── */

type SessionMethod = 'coin' | 'gcash' | 'online' | 'voucher';
type SessionStatus = 'active' | 'expired' | 'paused';

export interface DisplaySession {
   id: string;
   mac: string;
   ip: string;
   method: SessionMethod;
   amount: number;
   plan: string;
   purchased: number;
   remaining: number;
   expiresAt: string | null;
   /** TOTAL time this session has spent paused, in ms, across every pause. */
   pausedTotalMs: number;
   /** When the current pause started, if it is paused right now. */
   pausedAt: string | null;
   status: SessionStatus;
   device: string;
   /** The name the phone gave when the session was made; usually blank (see phoneNames). */
   hostname?: string;
   // vendoDevice ObjectId (null if the session has no device link) — needed
   // to target the per-device whitelist endpoint from a session row.
   deviceId: string | null;
   createdAt: string;
   // Data-rate marker — set when payment.dataPlan is populated
   // server-side. Switches the Time column to a data-usage bar.
   isDataSession: boolean;
   capMb: number | null;
   // Virtual whitelist row (server-synthesized, id = wl:<deviceId>:<MAC>).
   // Renders as "Unlimited". Edit changes which vendos it is free on; Kick
   // removes it from ALL of them.
   isWhitelist: boolean;
   /** Whitelist rows: every vendo this phone is Unlimited on. One row covers
    *  them all, so the Device column shows a count once there is more than one. */
   whitelistDeviceIds: string[];
   /** Whitelist rows: the phone has collected its pass, so Unlimited follows it
    *  to the other ticked vendos. False = it only works where it was granted. */
   carried: boolean;
   /** Voucher sessions: the code that was redeemed. Shown under the Method badge. */
   voucherCode: string | null;
   /** Operator's own note on the row. */
   note: string;
   /** A free-time promo claim (method 'operator', shown as "free promo"). */
   isPromo?: boolean;
}

export const mapApiSessionToDisplay = (
   s: ApiVendoSession,
   deviceNameById: Map<string, string>
): DisplaySession => ({
   id: s._id,
   mac: s.device?.mac || '—',
   ip: s.device?.ip || '—',
   method: s.payment?.method ?? 'coin',
   amount: s.payment?.amount ?? 0,
   plan: s.bandwidth?.plan || '—',
   purchased: s.time?.purchased ?? 0,
   remaining: s.time?.remaining ?? 0,
   expiresAt: s.time?.expiresAt ?? null,
   pausedTotalMs: s.time?.pausedTotalMs ?? 0,
   pausedAt: s.time?.pausedAt ?? null,
   status: s.status === 'kicked' ? 'expired' : s.status,
   device: (() => {
      const one = s.vendoDevice
         ? deviceNameById.get(s.vendoDevice) ?? 'Unknown'
         : 'Unknown';
      // One Unlimited phone can be free on many vendos. Naming just the first
      // one would be a lie the operator acts on, so say how many instead.
      const n = s.vendoCount ?? 0;
      return s.isWhitelist && n > 1 ? `${n} vendos` : one;
   })(),
   hostname: s.device?.hostname || '',
   deviceId: s.vendoDevice ?? null,
   createdAt: s.createdAt,
   isDataSession: !!s.payment?.dataPlan,
   capMb: s.payment?.capMb ?? null,
   isWhitelist: !!s.isWhitelist,
   whitelistDeviceIds: s.vendoDevices ?? (s.vendoDevice ? [s.vendoDevice] : []),
   carried: !!s.carried,
   voucherCode: s.payment?.voucherCode ?? null,
   note: s.note ?? '',
   isPromo: !!s.payment?.promo,
});

const formatHMS = (totalSec: number): string => {
   const s = Math.max(0, Math.floor(totalSec));
   const h = Math.floor(s / 3600);
   const m = Math.floor((s % 3600) / 60);
   const sec = s % 60;
   if (h > 0) return `${h}h ${m}m ${sec}s`;
   if (m > 0) return `${m}m ${sec}s`;
   return `${sec}s`;
};

const formatHM = (totalMin: number): string => {
   const m = Math.max(0, Math.floor(totalMin));
   const h = Math.floor(m / 60);
   const mm = m % 60;
   if (h > 0) return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
   return `${m}m`;
};

// formatPauseLeft lives in utils/pauseLeft (tested).

// Smart-format bytes for the data-usage column. Mirrors the captive
// portal's formatter so customer + owner see the same units.
// Bytes → MB is binary (the router caps at capMb × 1024 × 1024), but MB → GB
// is 1000 like the plan's own labels: a 2000 MB voucher reads "2 GB", not
// "1.95 GB" (Ray 2026-09-15). Same rule as formatMb in api/dataPlans.
const formatBytes = (bytes: number): string => {
   if (!bytes || bytes < 1024) return `${Math.max(0, Math.round(bytes))} B`;
   const kb = bytes / 1024;
   if (kb < 1024) return `${Math.round(kb)} KB`;
   const mb = kb / 1024;
   if (mb < 1000) return `${mb >= 100 ? Math.round(mb) : mb.toFixed(1)} MB`;
   const gb = mb / 1000;
   if (Number.isInteger(Math.round(gb * 100) / 100) ) return `${Math.round(gb)} GB`;
   return gb >= 10 ? `${gb.toFixed(1)} GB` : `${gb.toFixed(2)} GB`;
};

// Format throughput in bps to a short human-readable string. Used in
// the Sessions table's Usage column to show live up/down rates.
const formatBps = (bps: number): string => {
   if (!bps || bps < 1000) return `${Math.max(0, Math.round(bps))} bps`;
   const kbps = bps / 1000;
   if (kbps < 1000) return `${kbps >= 100 ? Math.round(kbps) : kbps.toFixed(1)} Kbps`;
   const mbps = kbps / 1000;
   return mbps >= 10 ? `${Math.round(mbps)} Mbps` : `${mbps.toFixed(1)} Mbps`;
};

// Exported for tests: this is the function the sessions table renders from,
// so a test that drives it with a skewed clock covers the real path rather
// than the clock helper in isolation.
export const computeSecondsRemaining = (s: DisplaySession, nowMs: number): number => {
   if (s.status === 'active' && s.expiresAt) {
      // toLocalDeadline, not a raw parse: expiresAt is in the SERVER's
      // frame, and this table is also rendered by the device sub-login on
      // whatever phone or tablet the shop uses. Translating the deadline
      // into this device's frame first is what makes diffing it against
      // the local clock safe — otherwise a tablet with the wrong date
      // reports a time-left the customer's portal disagrees with.
      const expMs = toLocalDeadline(s.expiresAt);
      return Math.max(0, Math.floor((expMs - nowMs) / 1000));
   }
   // Paused rows carry server-computed minutes, already clock-independent.
   if (s.status === 'paused') return s.remaining * 60;
   return 0;
};

/* ─── shared data hook ─────────────────────────────────────── */

/**
 * Drives the 10-second devices+sessions refresh. Each /wifi-* page mounts
 * this so it works standalone. The Zustand stores are global, so multiple
 * mounts on the same page (shouldn't happen but safe) just re-arm the
 * interval — no duplicate fetches.
 */
export const useVendoData = ({ sessions: withSessions = true }: { sessions?: boolean } = {}) => {
   const fetchDevices = useVendoDevicesStore((s) => s.fetch);
   const fetchSessions = useVendoSessionsStore((s) => s.fetch);
   const devicesError = useVendoDevicesStore((s) => s.error);
   const sessionsError = useVendoSessionsStore((s) => s.error);
   const deviceCount = useVendoDevicesStore((s) => s.devices.length);
   const sessionCount = useVendoSessionsStore((s) => s.sessions?.length ?? 0);

   useEffect(() => {
      // The sessions list is the heaviest read on api.smartfi.ph (one operator
      // has 700+ live sessions). Only pages that SHOW sessions load it — the
      // Devices, Networks, Settings and Sold pages never read it, yet were
      // half of all its downloads (web log 2026-10-26).
      const refresh = () => {
         fetchDevices();
         if (withSessions) fetchSessions();
      };
      refresh();
      const id = setInterval(() => {
         // A hidden tab shows nothing, so it asks for nothing; onVisible below
         // refreshes the moment the operator comes back.
         if (document.visibilityState === 'hidden') return;
         refresh();
      }, 10000);
      // Browsers throttle/pause background-tab timers, so the 10s poll
      // stalls while the dashboard isn't focused — coming back to the tab
      // could show device status up to a minute stale (looked like the
      // liveness pill "not updating"). Refresh immediately on re-focus so
      // the cards are always current the instant the operator returns.
      const onVisible = () => {
         if (document.visibilityState === 'visible') refresh();
      };
      document.addEventListener('visibilitychange', onVisible);
      return () => {
         clearInterval(id);
         document.removeEventListener('visibilitychange', onVisible);
      };
   }, [fetchDevices, fetchSessions, withSessions]);

   // Only surface a load error when there's nothing to show. A failed
   // background refresh (e.g. while a router restarts its REST for 1-2 min
   // after a reflash) keeps the last-known list, so a scary toast every 10s is
   // just confusing noise — the data is still on screen.
   useEffect(() => {
      if (devicesError && deviceCount === 0) toast.error(`Devices: ${devicesError}`);
   }, [devicesError, deviceCount]);

   useEffect(() => {
      if (sessionsError && sessionCount === 0)
         toast.error(`Sessions: ${sessionsError}`);
   }, [sessionsError, sessionCount]);
};

/* ─── Overview Tab ─────────────────────────────────────────── */

interface AnimatedStatProps {
   label: string;
   value: string;
   prefix?: string;
   color: string;
}

const TWEEN_DURATION_MS = 800;

// Odometer-style tween for numeric stat values. On value change, counts
// through intermediate integers with ease-out-cubic so the reader catches
// the movement. Non-numeric values (e.g. "5 / 2", "—") swap instantly.
const AnimatedStat = ({ label, value, prefix, color }: AnimatedStatProps) => {
   const parsed = /^-?\d+$/.test(value) ? parseInt(value, 10) : null;
   const [displayed, setDisplayed] = useState<string>(value);
   const [flash, setFlash] = useState(false);
   const prevNumRef = useRef<number | null>(parsed);
   const rafRef = useRef<number | null>(null);

   useEffect(() => {
      // Non-numeric → snap to new value, nothing to tween.
      if (parsed === null) {
         setDisplayed(value);
         prevNumRef.current = null;
         return;
      }
      const from = prevNumRef.current;
      const to = parsed;
      // First mount or no real change → no tween, no flash.
      if (from === null || from === to) {
         setDisplayed(String(to));
         prevNumRef.current = to;
         return;
      }
      setFlash(true);
      const flashId = setTimeout(() => setFlash(false), TWEEN_DURATION_MS);
      const start = performance.now();
      const tick = (now: number) => {
         const t = Math.min(1, (now - start) / TWEEN_DURATION_MS);
         const eased = 1 - Math.pow(1 - t, 3);
         const current = Math.round(from + (to - from) * eased);
         setDisplayed(String(current));
         if (t < 1) {
            rafRef.current = requestAnimationFrame(tick);
         } else {
            prevNumRef.current = to;
         }
      };
      rafRef.current = requestAnimationFrame(tick);
      return () => {
         if (rafRef.current) cancelAnimationFrame(rafRef.current);
         clearTimeout(flashId);
      };
   }, [value, parsed]);

   return (
      <StatCard $flash={flash}>
         <StatLabel>{label}</StatLabel>
         <StatValue $color={color} $flash={flash}>
            {prefix || ''}{displayed}
         </StatValue>
      </StatCard>
   );
};

export const OverviewTab = () => {
   const apiDevices = useVendoDevicesStore((s) => s.devices);
   const apiSessions = useVendoSessionsStore((s) => s.sessions);
   const dataUsageBySessionId = useVendoSessionsStore((s) => s.dataUsageBySessionId);
   const timeUsageBySessionId = useVendoSessionsStore((s) => s.timeUsageBySessionId);
   const pauseWindowBySessionId = useVendoSessionsStore((s) => s.pauseWindowBySessionId);
   // Filter to setupStatus='done' devices, same as DevicesTab — pending
   // wizard registrations don't belong on the at-a-glance dashboard.
   const devices = useMemo(
      () =>
         apiDevices
            .filter((d) => (d.setupStatus ?? 'done') === 'done')
            .map(mapApiToDisplay),
      [apiDevices]
   );
   // Shared view+filter+sort prefs (lives in useDeviceListPrefsStore so
   // changes here also affect the Devices tab and vice versa, and they
   // survive reload). Expanded set stays per-surface — that's transient
   // UI state, not a preference.
   const viewMode = useDeviceListPrefsStore((s) => s.viewMode);
   const setViewMode = useDeviceListPrefsStore((s) => s.setViewMode);
   const sortBy = useDeviceListPrefsStore((s) => s.sortBy);
   const setSortBy = useDeviceListPrefsStore((s) => s.setSortBy);
   const ownershipFilter = useDeviceListPrefsStore((s) => s.ownershipFilter);
   const setOwnershipFilter = useDeviceListPrefsStore((s) => s.setOwnershipFilter);
   const healthFilter = useDeviceListPrefsStore((s) => s.healthFilter);
   const search = useDeviceListPrefsStore((s) => s.search);
   const setSearch = useDeviceListPrefsStore((s) => s.setSearch);
   const boardOptions = useBoardFilterOptions();
   const setHealthFilter = useDeviceListPrefsStore((s) => s.setHealthFilter);
   const boardFilter = useDeviceListPrefsStore((s) => s.boardFilter);
   const setBoardFilter = useDeviceListPrefsStore((s) => s.setBoardFilter);
   const showAllSubs = useDeviceListPrefsStore((s) => s.showAllSubs);
   const setShowAllSubs = useDeviceListPrefsStore((s) => s.setShowAllSubs);
   const [overviewExpanded, setOverviewExpanded] = useState<Set<string>>(new Set());
   const toggleOverviewExpand = (id: string) => {
      setOverviewExpanded((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   };

   const pendingOutgoingIds = usePendingOutgoingDeviceIds();

   // Mirror DevicesTab: ownership-filter the mains, then sort. Pending
   // outgoing transfers don't show here — they live in their own
   // dedicated section in DevicesTab and the dashboard is a summary,
   // not a transfers-management view.
   const mains = useMemo(() => {
      const onlyMains = devices.filter((d) => d.type === 'main');
      const visible = onlyMains.filter((d) => {
         if (pendingOutgoingIds.has(d._id)) return false;
         if (!matchesDeviceFilters(d, healthFilter, boardFilter)) return false;
         // A main stays when one of ITS sub-vendos matches — the sub is drawn
         // under its main, so hiding the main would hide the match.
         if (
            !matchesDeviceSearch(d, search) &&
            !devices.some((sub) => sub.parentId === d._id && matchesDeviceSearch(sub, search))
         )
            return false;
         if (ownershipFilter === 'owned') return d.isCurrentOwner !== false;
         if (ownershipFilter === 'sold') return d.isCurrentOwner === false;
         return true;
      });
      const sorted = [...visible];
      switch (sortBy) {
         case 'newest':
            sorted.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
            break;
         case 'status':
            sorted.sort((a, b) => {
               if (a.status !== b.status) return a.status === 'online' ? -1 : 1;
               return a.name.localeCompare(b.name);
            });
            break;
         case 'active':
            sorted.sort((a, b) => b.activeSessions - a.activeSessions);
            break;
         case 'revenue':
            sorted.sort((a, b) => b.totalCoins - a.totalCoins);
            break;
      }
      return sorted;
   }, [devices, sortBy, pendingOutgoingIds, ownershipFilter, healthFilter, boardFilter, search]);

   const ownedCount = useMemo(
      () =>
         devices.filter(
            (d) =>
               d.type === 'main' &&
               d.isCurrentOwner !== false &&
               !pendingOutgoingIds.has(d._id)
         ).length,
      [devices, pendingOutgoingIds]
   );
   const soldCount = useMemo(
      () =>
         devices.filter(
            (d) => d.type === 'main' && d.isCurrentOwner === false
         ).length,
      [devices]
   );

   const getSubs = (mainId: string) =>
      devices.filter((d) => d.parentId === mainId);

   const {
      openSettings,
      openAddSub,
      openActivate,
      openManageLicense,
      handleRefresh,
      handleResetStats,
      openDelete,
      refreshingId,
   } = useDeviceActions();

   const deviceNameById = useMemo(() => {
      const map = new Map<string, string>();
      apiDevices.forEach((d) => map.set(d._id, d.name));
      return map;
   }, [apiDevices]);

   const sessions = useMemo(
      () => apiSessions.map((s) => mapApiSessionToDisplay(s, deviceNameById)),
      [apiSessions, deviceNameById]
   );

   // Sold devices stay visible in the list for audit, but they don't
   // count toward the operator's online/finance totals — the buyer is
   // earning that revenue now. Drop them from any aggregation that
   // represents "what I'm earning".
   //
   // ONE vendo, or the whole account (Ray 2026-09-30): the choice made on the
   // revenue chart drives the stat cards and Recent Sessions too, so every
   // number on the screen is about the same vendo.
   const [focusId, setFocusId] = useState('');
   const focusOptions = useMemo(
      () => vendoChartOptions(apiDevices.filter((d) => d.isCurrentOwner !== false)),
      [apiDevices]
   );
   const focusName = focusOptions.find((o) => o.value === focusId)?.label.replace(/^└ /, '');
   // A vendo that was deleted or sold while selected falls back to all.
   useEffect(() => {
      if (focusId && !focusOptions.some((o) => o.value === focusId)) setFocusId('');
   }, [focusId, focusOptions]);
   const ownedApiDevices = apiDevices.filter(
      (d) => d.isCurrentOwner !== false && inVendoFocus(focusId, d._id)
   );
   const ownedDevices = devices.filter(
      (d) => d.isCurrentOwner !== false && inVendoFocus(focusId, d._id)
   );
   const focusSessions = sessions.filter((s) => inVendoFocus(focusId, s.deviceId ?? ''));

   const onlineCount = ownedDevices.filter((d) => d.status === 'online').length;
   const activeSessionsCount = focusSessions.filter((s) => s.status === 'active').length;
   const pausedSessionsCount = focusSessions.filter((s) => s.status === 'paused').length;

   const todayCoinsTotal = ownedApiDevices.reduce((sum, d) => sum + (d.stats?.todayCoins ?? 0), 0);
   const totalCoinsAllTime = ownedApiDevices.reduce((sum, d) => sum + (d.stats?.totalCoins ?? 0), 0);
   const lifetimeCoinsTotal = ownedApiDevices.reduce((sum, d) => sum + (d.stats?.lifetimeCoins ?? 0), 0);
   const gcashRevenueTotal = ownedApiDevices.reduce((sum, d) => sum + (d.stats?.gcashRevenue ?? 0), 0);
   // Printed (over-the-counter) vouchers — the third revenue stream (Ray 2026-09-15).
   const voucherRevenueTotal = ownedApiDevices.reduce((sum, d) => sum + (d.stats?.voucherRevenue ?? 0), 0);
   const todayVouchersTotal = ownedApiDevices.reduce((sum, d) => sum + (d.stats?.todayVouchers ?? 0), 0);
   // Online sales today, net of fees — the same figure Transaction History prints.
   const todayOnlineTotal = ownedApiDevices.reduce((sum, d) => sum + (d.stats?.todayOnline ?? 0), 0);

   // Counted on the server: the live list no longer carries today's finished
   // sessions (and the old list stopped at the last 100 of them anyway).
   const todayByVendo = useVendoSessionsStore((s) => s.todayByVendo);
   const sessionsTodayCount = Object.entries(todayByVendo)
      .filter(([vendoId]) => inVendoFocus(focusId, vendoId))
      .reduce((sum, [, n]) => sum + n, 0);

   const stats: { label: string; value: string; prefix?: string; color: string }[] = [
      { label: 'Devices Online', value: `${onlineCount} / ${ownedDevices.length}`, color: colors.SUCCESS },
      { label: 'Active Sessions', value: String(activeSessionsCount), color: colors.PRIMARY },
      { label: "Today's Coins", value: currencyFormat(todayCoinsTotal, false), color: colors.SECONDARY90 },
      { label: "Today's Vouchers Redeemed", value: currencyFormat(todayVouchersTotal, false), color: colors.PINK },
      { label: "Today's Online Payments", value: currencyFormat(todayOnlineTotal, false), color: colors.TEAL },
      { label: 'Total Revenue (Coins)', value: currencyFormat(totalCoinsAllTime, false), color: colors.BLUE },
      // Maya, QR Ph, GCash… every e-wallet payment lands here (the field keeps
      // its old name; the label must not, or Maya sales read as GCash).
      { label: 'Total Revenue (Online Payments, net)', value: currencyFormat(gcashRevenueTotal, false), color: colors.TEAL },
      { label: 'Total Revenue (Vouchers Redeemed)', value: currencyFormat(voucherRevenueTotal, false), color: colors.PINK },
      // "Lifetime" survives the Reset Stats button, so even after an
      // operator zeroes the period counters they still see what each
      // device has earned since first activation. Coins + GCash + printed
      // vouchers (Ray 2026-09-15).
      { label: 'Lifetime Revenue', value: currencyFormat(lifetimeCoinsTotal + gcashRevenueTotal + voucherRevenueTotal, false), color: colors.PURPLE },
      { label: 'Sessions Today', value: String(sessionsTodayCount), color: colors.ORANGE },
      { label: 'Paused Sessions', value: String(pausedSessionsCount), color: colors.WARNING },
   ];

   const recentSessions = focusSessions.filter((s) => s.status !== 'expired').slice(0, 3);

   return (
      <TabContent>
         {/* Free time promo — a "do it now" action across vendos (Ray 2026-10-06). */}
         <FreePromoPanel />
         {/* Owner's scrolling portal announcement (Ray 2026-10-23). */}
         <AnnouncementPanel />
         {focusOptions.length > 1 && (
            <FocusBar>
               <FocusLabel>Showing</FocusLabel>
               <FocusSelect
                  value={focusId}
                  onChange={(e) => setFocusId(e.target.value)}
                  aria-label="Show figures for"
               >
                  <option value="">All vendos</option>
                  {focusOptions.map((o) => (
                     <option key={o.value} value={o.value}>
                        {o.label}
                     </option>
                  ))}
               </FocusSelect>
               {focusId && (
                  <>
                     <FocusNote>
                        Cards, chart and recent sessions are for <strong>{focusName}</strong> only.
                     </FocusNote>
                     <FocusClear type="button" onClick={() => setFocusId('')}>
                        Show all vendos
                     </FocusClear>
                  </>
               )}
            </FocusBar>
         )}
         <StatsGrid>
            {stats.map((s) => (
               <AnimatedStat key={s.label} label={s.label} value={s.value} prefix={s.prefix} color={s.color} />
            ))}
         </StatsGrid>

         <CoinTicker />

         <RevenueAreaChart deviceId={focusId} onDeviceChange={setFocusId} />

         <RecentSessionsBlock>
            <SectionHeader>Recent Sessions</SectionHeader>
            {recentSessions.length === 0 ? (
               <EmptyNote>No active sessions yet.</EmptyNote>
            ) : (
               <SessionTable
                  sessions={recentSessions}
                  dataUsageBySessionId={dataUsageBySessionId}
                  timeUsageBySessionId={timeUsageBySessionId}
                  pauseWindowBySessionId={pauseWindowBySessionId}
               />
            )}
         </RecentSessionsBlock>

         <SectionRow>
            <SectionHeader>Device Status</SectionHeader>
            <ViewToggle>
               <ToggleBtn
                  $active={viewMode === 'card'}
                  onClick={() => setViewMode('card')}
               >
                  Cards
               </ToggleBtn>
               <ToggleBtn
                  $active={viewMode === 'list'}
                  onClick={() => setViewMode('list')}
               >
                  List
               </ToggleBtn>
            </ViewToggle>
         </SectionRow>
         <HeaderActions>
            <DeviceSearchInput
               type="search"
               value={search}
               onChange={(e) => setSearch(e.target.value)}
               placeholder="Search name, ID, IP…"
               aria-label="Search devices"
            />
            <SortSelect
               value={sortBy}
               onChange={(e) => setSortBy(e.target.value as DeviceSort)}
               title="Sort fleet"
            >
               <option value="newest">Newest first</option>
               <option value="status">Status</option>
               <option value="active">Active sessions</option>
               <option value="revenue">Total revenue</option>
            </SortSelect>
            <SortSelect
               value={ownershipFilter}
               onChange={(e) =>
                  setOwnershipFilter(e.target.value as DeviceOwnershipFilter)
               }
               title="Filter by ownership"
            >
               <option value="owned">Owned ({ownedCount})</option>
               <option value="sold" disabled={soldCount === 0}>
                  Sold ({soldCount})
               </option>
               <option value="all">All ({ownedCount + soldCount})</option>
            </SortSelect>
            {/* The pill on the card, not device.status — a vendo with a
                dead NodeMCU and a healthy router is Degraded here too. */}
            <SortSelect
               value={healthFilter}
               onChange={(e) =>
                  setHealthFilter(e.target.value as DeviceHealthFilter)
               }
               title="Filter by status"
            >
               <option value="all">Any status</option>
               <option value="online">Online</option>
               <option value="degraded">Degraded</option>
               <option value="online-or-degraded">Online + Degraded</option>
               <option value="offline">Offline</option>
            </SortSelect>
            {/* Named by BOARD, never "Lite" — that word does not say which
                hardware he is looking at. */}
            <SortSelect
               value={boardFilter}
               onChange={(e) =>
                  setBoardFilter(e.target.value as DeviceBoardFilter)
               }
               title="Filter by router hardware"
            >
               <option value="all">Any hardware</option>
               {boardOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                     {o.label}
                  </option>
               ))}
            </SortSelect>
            <SubsToggleLabel title="Force-show sub-vendos under every main">
               <input
                  type="checkbox"
                  checked={showAllSubs}
                  onChange={(e) => setShowAllSubs(e.target.checked)}
               />
               Show sub-vendos
            </SubsToggleLabel>
         </HeaderActions>
         {mains.length === 0 ? (
            <EmptyNote>No devices yet. Register one from the Devices tab.</EmptyNote>
         ) : viewMode === 'card' ? (
            <MainCardGrid>
               {mains.map((d) => {
                  const subs = getSubs(d._id);
                  // showAllSubs is a global override — when on, every
                  // sub renders regardless of the per-row toggle state.
                  const isOpen = showAllSubs || overviewExpanded.has(d.id);
                  return (
                     <MainColumn key={d.id}>
                        <CompactDeviceCard
                           device={d}
                           subs={subs}
                           isOpen={isOpen}
                           onToggleExpand={toggleOverviewExpand}
                           onOpenSettings={openSettings}
                           onOpenAddSub={openAddSub}
                           onOpenActivate={openActivate}
                           onManageLicense={openManageLicense}
                           onRefresh={handleRefresh}
                           refreshingId={refreshingId}
                           onResetStats={handleResetStats}
                           onOpenDelete={openDelete}
                        />
                        {isOpen && (
                           <SubStack>
                              {subs.map((s) => (
                                 <SubDeviceCard key={s.id} $online={s.status === 'online'}>
                                    {/* Same Activate / Renew banner as the main card — a
                                        sub-vendo has its own licence and used to hide it
                                        behind a small pill (Ray, 2026-09-29). */}
                                    {showLicenseBanner(s) && (
                                       <DeviceLicenseBanner
                                          licenseStatus={s.licenseStatus}
                                          trialStartedAt={s.trialStartedAt ?? null}
                                          trialDaysLeft={s.trialDaysLeft}
                                          autoRenewLicense={s.autoRenewLicense}
                                          soldToEmail={s.isCurrentOwner === false ? s.soldTransfer?.toEmail : undefined}
                                          onActivate={() => openActivate(s)}
                                          hwBoard={s.hwBoard}
                                       />
                                    )}
                                    <SubCardHeader>
                                       <DeviceHeaderLeft>
                                          <SubName>{s.name}</SubName>
                                          <TypePill>Sub</TypePill>
                                          {s.vlanId != null && (
                                             <VlanChip
                                                title={
                                                   s.vlanSubnet
                                                      ? `VLAN ${s.vlanId} · ${s.vlanSubnet}${
                                                           s.vlanPort ? ` · ${s.vlanPort}` : ''
                                                        }`
                                                      : `VLAN ${s.vlanId}`
                                                }
                                             >
                                                VLAN {s.vlanId}
                                             </VlanChip>
                                          )}
                                       </DeviceHeaderLeft>
                                       <CardHeaderRight>
                                          <RouterPill status={s.routerStatus} />
                                          <DeviceStatusBadge
                                             status={s.status}
                                             routerStatus={s.routerStatus}
                                          />
                                          {/* Gear opens on a SOLD vendo too — see the main card above. */}
                                          <SettingsIconBtn
                                             label={s.name}
                                             onClick={() => openSettings(s)}
                                          />
                                          {s.isCurrentOwner && (
                                             <DeviceKebab
                                                id={s._id}
                                                onMoveLicense={
                                                   isLicensed(s.licenseStatus) &&
                                                   s.isCurrentOwner
                                                      ? () => openManageLicense(s)
                                                      : undefined
                                                }
                                                onPreviewPortal={() =>
                                                   openPortal(s.portalSlug || d.portalSlug)
                                                }
                                                onResetStats={() => handleResetStats(s)}
                                                onDelete={() => openDelete(s)}
                                             />
                                          )}
                                       </CardHeaderRight>
                                    </SubCardHeader>
                                    <MetaLine>
                                       <MetaChip>{s.id}</MetaChip>
                                    </MetaLine>
                                    <SubCardBody>
                                       <SubStat>
                                          <DeviceStatLabel>Today</DeviceStatLabel>
                                          <SubStatValue>₱{money(todayTotal(s))}</SubStatValue>
                                       </SubStat>
                                       <SubStat>
                                          <DeviceStatLabel>Revenue</DeviceStatLabel>
                                          <SubStatValue>
                                             ₱{money(ownRevenue(s))}
                                          </SubStatValue>
                                       </SubStat>
                                       <SubStat>
                                          <DeviceStatLabel>Active</DeviceStatLabel>
                                          <SubStatValue>{s.activeSessions}</SubStatValue>
                                       </SubStat>
                                    </SubCardBody>
                                    <CompactIp>
                                       <NetworkLabel>IP</NetworkLabel>
                                       <NetworkValue>{s.localIp || '—'}</NetworkValue>
                                       <NetworkLabel style={{ marginLeft: 'auto' }}>
                                          Heartbeat
                                       </NetworkLabel>
                                       <NetworkValue>{s.lastHeartbeat}</NetworkValue>
                                    </CompactIp>
                                 </SubDeviceCard>
                              ))}
                              <AddSubBtn onClick={() => openAddSub(d)}>
                                 + Add Sub-Vendo
                              </AddSubBtn>
                           </SubStack>
                        )}
                     </MainColumn>
                  );
               })}
            </MainCardGrid>
         ) : (
            <ListView
               mains={mains}
               getSubs={getSubs}
               expanded={overviewExpanded}
               onToggleExpand={toggleOverviewExpand}
               showAllSubs={showAllSubs}
               onOpenSettings={openSettings}
               onOpenAddSub={openAddSub}
               onOpenActivate={openActivate}
               onManageLicense={openManageLicense}
               onRefresh={handleRefresh}
               refreshingId={refreshingId}
               onResetStats={handleResetStats}
               onOpenDelete={openDelete}
            />
         )}
      </TabContent>
   );
};

/* ─── Shared device-card action handlers ───────────────────── */
/* Used by both DevicesTab and OverviewTab so the card actions
   (Settings, Refresh, QR, Reset Stats, Activate, Add Sub-Vendo)
   behave identically across tabs. Hook-form so each consumer gets
   its own refreshingId state without sharing it across tabs. */

function useDeviceActions() {
   const { handleShow, handleClose } = useUIStore();
   const navigate = useNavigate();
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const refetchSessions = useVendoSessionsStore((s) => s.fetch);
   const [refreshingId, setRefreshingId] = useState<string | null>(null);

   const openSettings = (device: VendoDevice) => {
      handleShow({
         template: 'device-settings',
         title: `Settings — ${device.name}`,
         windowType: 'desktop',
         data: device,
      });
   };

   const openAddSub = (parent: VendoDevice) => {
      // An OpenWRT router hosts sub-vendos only on the boards that can carry VLANs
      // (OrangePi, mini PC) - the same rule as the Networks page, from the same
      // function. The server refuses the rest; say so up front, and say what
      // DOES work instead of a bare "not supported".
      if (!canHostVlans(parent.platform, parent.hwBoard)) {
         toast.info(
            "This router model can't host sub-vendos. An OrangePi, a mini PC or a MikroTik main vendo can.",
            { autoClose: 5000 }
         );
         return;
      }
      // Stand-alone models (subvendoCap 0, e.g. hAP lite) can't host sub-vendos
      // — the server rejects it with a 409. Tell the operator up front instead
      // of opening a form that's doomed to fail.
      const meta = parent.model ? ROUTER_MODEL_META[parent.model] : undefined;
      if (meta && meta.subvendoCap === 0) {
         toast.info(
            `${meta.label} is a stand-alone device — it doesn't support sub-vendos.`,
            { autoClose: 3500 }
         );
         return;
      }
      handleShow({
         template: 'sub-vendo-add',
         title: `Add Sub-Vendo under ${parent.name}`,
         windowType: 'desktop',
         data: parent,
      });
   };

   const openActivate = (device: VendoDevice) => {
      handleShow({
         template: 'activate-license',
         title: `Activate License — ${device.name}`,
         windowType: 'desktop',
         data: { device, onBrowsePartners: () => navigate('/partners') },
      });
   };

   const openManageLicense = (device: VendoDevice) => {
      handleShow({
         template: 'manage-license',
         title: `Manage License — ${device.name}`,
         windowType: 'desktop',
         data: { device },
      });
   };

   const handleRefresh = async (device: VendoDevice) => {
      if (refreshingId) return;
      setRefreshingId(device._id);
      try {
         await refetchDevices();
         await refetchSessions();
      } finally {
         setTimeout(() => setRefreshingId(null), 400);
      }
   };

   const handleResetStats = (device: VendoDevice) => {
      handleShow({
         template: 'verification',
         title: `Reset Sales — ${device.name}`,
         windowType: 'desktop',
         data: {
            data: { id: device._id },
            msg: `Reset sales for ${device.name}? Today's coins, total coins and Lifetime Revenue for this router start again from ₱0. Customers who are online keep their time. Cannot be undone.`,
         },
         actions: {
            onConfirm: async () => {
               try {
                  const res = await apiResetDeviceStats(device._id);
                  toast.success(
                     res.keptLiveSessions > 0
                        ? `Sales reset — ${res.keptLiveSessions} active session${
                             res.keptLiveSessions > 1 ? 's' : ''
                          } kept`
                        : 'Sales reset to ₱0'
                  );
                  refetchDevices();
                  refetchSessions();
               } catch (err: any) {
                  toast.error(err?.response?.data?.error || 'Failed to reset sales');
               } finally {
                  handleClose();
               }
            },
         },
      });
   };

   // Shared delete entry-point. Accepts either the display VendoDevice
   // or a raw ApiVendoDevice (structural — only _id + name are read).
   // Reuses the DeviceSettings modal's Hardware tab Danger Zone for the
   // typed-name confirm flow; no separate confirm prompt.
   const openDelete = (device: { _id: string; name: string }) => {
      const liveDevice =
         useVendoDevicesStore
            .getState()
            .devices.find((d) => d._id === device._id) || device;
      handleShow({
         template: 'device-settings',
         title: `Settings — ${device.name}`,
         windowType: 'desktop',
         data: { ...liveDevice, _initialTab: 'hardware' },
      });
   };

   return {
      openSettings,
      openAddSub,
      openActivate,
      openManageLicense,
      handleRefresh,
      handleResetStats,
      openDelete,
      refreshingId,
   };
}

/* ─── Devices Tab ──────────────────────────────────────────── */

/**
 * Transfer credits to another account by email. Look up the recipient first
 * (to confirm the name), then send. No loyalty points are earned on a transfer
 * — it's a pure credit-moving utility.
 *
 * An ADMIN sender ISSUES credits here instead of moving their own: no balance
 * needed, no limit, and distributors are allowed as recipients. The server
 * decides that from the account, not from anything this screen sends — the
 * flag below only stops the UI disabling buttons that would in fact work.
 */
export const TransferCreditModal: React.FC<{
   balance: number;
   onClose: () => void;
   /** Filled in and looked up at once — the chat's "Transfer credits" shortcut (Ray 2026-10-27). */
   toEmail?: string;
}> = ({ balance, onClose, toEmail }) => {
   const qc = useQueryClient();
   const { data: creditsData } = useQuery({
      queryKey: ['credits'],
      queryFn: getCredits,
   });
   const unlimited = creditsData?.unlimited ?? false;
   const [email, setEmail] = useState(toEmail ?? '');
   const [recipient, setRecipient] = useState<{
      name?: string;
      email: string;
      isDistributor?: boolean;
   } | null>(null);
   // Free-typed string so the user can clear the field and type any
   // multi-digit amount. We only turn it into a number for validation
   // and sending — never clamp mid-keystroke (that was the "stuck on
   // your balance" bug where a balance of 3 forced every entry to 3).
   const [amount, setAmount] = useState('1');
   const [note, setNote] = useState('');
   const amountNum = Math.floor(Number(amount)) || 0;

   const lookup = useMutation({
      mutationFn: () => lookupTransferRecipientApi(email.trim()),
      onSuccess: (d) => setRecipient(d.recipient),
      onError: (e: any) => {
         setRecipient(null);
         toast.error(e?.response?.data?.error || 'No account with that email');
      },
   });
   // From the chat: the member is already known — look them up right away.
   useEffect(() => {
      if (toEmail) lookup.mutate();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
   const transfer = useMutation({
      mutationFn: () =>
         transferCreditApi({
            toEmail: email.trim(),
            amount: amountNum,
            note: note.trim() || undefined,
         }),
      onSuccess: (d) => {
         const who = recipient?.name || recipient?.email;
         const n = `${amountNum} credit${amountNum === 1 ? '' : 's'}`;
         toast.success(
            d.issued ? `Gave ${n} to ${who} 🎉` : `Sent ${n} to ${who} 🎉`
         );
         qc.invalidateQueries({ queryKey: ['credits'] });
         qc.invalidateQueries({ queryKey: ['vendo', 'my-transfers'] });
         onClose();
      },
      onError: (e: any) =>
         toast.error(e?.response?.data?.error || 'Transfer failed'),
   });

   // An admin issues rather than transfers, so neither the balance ceiling nor
   // the distributor rule applies to them (see @/utils/creditGates).
   const recipientIsDistributor = blocksDistributorRecipient(
      !!recipient?.isDistributor,
      unlimited
   );
   const amountValid = isSendAmountValid(amountNum, balance, unlimited);
   const canSend =
      !!recipient &&
      !recipientIsDistributor &&
      amountValid &&
      !transfer.isPending;

   return (
      // A click outside does NOT close this one (Ray, 2026-09-29): a typed
      // email and amount lost to a stray click is a re-typed transfer. Cancel
      // is the way out.
      <TModalOverlay>
         <TModalCard onClick={(e) => e.stopPropagation()}>
            <TModalTitle>{unlimited ? 'Give credits' : 'Transfer credits'}</TModalTitle>
            <TModalSub>
               {unlimited ? (
                  <>
                     Give license credits to any SmartFi account. You have{' '}
                     <strong>{balance}</strong> credit{balance === 1 ? '' : 's'} —
                     those are used first, and anything beyond them is created, so
                     you are never short. Left out of the sales figures.
                  </>
               ) : (
                  <>
                     Send license credits to another SmartFi account. You have{' '}
                     <strong>{balance}</strong> credit{balance === 1 ? '' : 's'}. No
                     points are earned on a transfer.
                  </>
               )}
            </TModalSub>

            <TModalLabel>Recipient email</TModalLabel>
            <TModalRow>
               <TModalInput
                  value={email}
                  onChange={(e) => {
                     setEmail(e.target.value);
                     setRecipient(null);
                  }}
                  placeholder="friend@example.com"
                  type="email"
               />
               <TModalCheckBtn
                  onClick={() => email.trim() && lookup.mutate()}
                  disabled={!email.trim() || lookup.isPending}
               >
                  {lookup.isPending ? '…' : 'Find'}
               </TModalCheckBtn>
            </TModalRow>
            {recipient && !recipientIsDistributor && (
               <TModalFound>
                  ✓ {unlimited ? 'Giving to' : 'Sending to'}{' '}
                  <strong>{recipient.name || recipient.email}</strong>
                  {recipient.name ? ` (${recipient.email})` : ''}
                  {unlimited && recipient.isDistributor ? ' · distributor' : ''}
               </TModalFound>
            )}
            {recipient && recipientIsDistributor && (
               <TModalErr>
                  {recipient.name || recipient.email} is a distributor account.
                  Credits can't be transferred to distributors — they get credits
                  by buying packages.
               </TModalErr>
            )}

            <TModalLabel>Amount</TModalLabel>
            <TModalInput
               type="number"
               inputMode="numeric"
               min={1}
               max={unlimited ? undefined : balance}
               value={amount}
               onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
            />
            {!amountValid && (
               <TModalErr>
                  {unlimited
                     ? 'Enter 1 or more credits.'
                     : `Enter 1–${balance} credits.`}
               </TModalErr>
            )}

            <TModalLabel>Note (optional)</TModalLabel>
            <TModalInput
               value={note}
               onChange={(e) => setNote(e.target.value)}
               placeholder="e.g. for your new vendo"
            />

            <TModalFooter>
               <TModalCancel onClick={onClose} disabled={transfer.isPending}>
                  Cancel
               </TModalCancel>
               <TModalSend disabled={!canSend} onClick={() => transfer.mutate()}>
                  {transfer.isPending
                     ? unlimited
                        ? 'Giving…'
                        : 'Sending…'
                     : `${unlimited ? 'Give' : 'Send'} ${amountNum} credit${
                          amountNum === 1 ? '' : 's'
                       }`}
               </TModalSend>
            </TModalFooter>
         </TModalCard>
      </TModalOverlay>
   );
};

export const DevicesTab = () => {
   // ?device=<id> from a notification: scroll to and flash that card.
   useFocusFromQuery('device');
   const { handleShow, handleClose } = useUIStore();
   const navigate = useNavigate();

   // smartfi.ph "Download & Setup" → sign in → here. The note left on the
   // sign-in screen names the draft; read it once, fetch it, and open the
   // register wizard prefilled. An expired draft opens the wizard blank
   // rather than showing an error the operator cannot act on.
   useEffect(() => {
      const draftId = takeSetupDraft();
      if (!draftId) return;
      let cancelled = false;
      getSetupDraft(draftId)
         .then((draft) => {
            if (cancelled) return;
            handleShow({
               template: 'device-register',
               title: 'Finish setting up your vendo',
               windowType: 'desktop',
               data: { setupDraft: draft },
            });
         })
         .catch(() => {
            if (cancelled) return;
            handleShow({
               template: 'device-register',
               title: 'Register New Vendo Device',
               windowType: 'desktop',
               data: null,
            });
         });
      return () => {
         cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
   const { data: creditsData } = useQuery({
      queryKey: ['credits'],
      queryFn: getCredits,
   });
   const credits = creditsData?.credits ?? 0;
   const usedCredits = creditsData?.used ?? 0;
   const [showTransfer, setShowTransfer] = useState(false);
   const [showTransferHistory, setShowTransferHistory] = useState(false);

   const apiDevices = useVendoDevicesStore((s) => s.devices);
   const loading = useVendoDevicesStore((s) => s.loading);
   const fetched = useVendoDevicesStore((s) => s.fetched);
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const refetchSessions = useVendoSessionsStore((s) => s.fetch);

   // Devices the reflash modal just finished — drives the "Setting up the
   // router" banner immediately (the device-list refresh fails mid-restart, so
   // we can't wait on the server's reflashFinishingAt to surface it).
   const reflashFinishedAt = useReflashingStore((s) => s.finishedAt);
   // Tick so the time-based window below re-evaluates and the banner clears
   // itself (~3 min) without a page refresh.
   const [reflashTick, setReflashTick] = useState(0);
   useEffect(() => {
      const id = setInterval(() => setReflashTick((n) => n + 1), 15000);
      return () => clearInterval(id);
   }, []);
   const reflashingIds = useMemo(() => {
      const ids = new Set<string>();
      for (const [id, t] of Object.entries(reflashFinishedAt)) {
         if (Date.now() - t < 3 * 60 * 1000) ids.add(id);
      }
      return ids;
      // reflashTick forces re-eval of the Date.now() window on a timer.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [reflashFinishedAt, reflashTick]);

   // Phase 6 — pending ownership claims (someone registered my hardware on
   // another account). Drives the "suspected stolen" banner on the matching
   // device card. Keyed by the owner's device _id stamped on the claim.
   const { data: ownershipData } = useQuery({
      queryKey: ['ownership-claims'],
      queryFn: getOwnershipClaims,
      refetchInterval: 30000,
   });
   const claimByDevice = useMemo(() => {
      const m = new Map<string, OwnershipClaim>();
      (ownershipData?.claims ?? []).forEach((c) => {
         if (c.originalDevice) m.set(c.originalDevice, c);
      });
      return m;
   }, [ownershipData]);
   const onOwnershipAction = (d: VendoDevice, claim: OwnershipClaim) =>
      handleShow({
         template: 'ownership-action',
         title: 'Suspected stolen device',
         data: { device: d, claim },
      });
   // The card banner is the PRIMARY surface for a claim whose device still
   // exists. The table is the SECONDARY catch-all for claims whose device was
   // deleted (no card to show the banner) — so it lists only those, never
   // duplicating a card banner.
   const orphanClaims = useMemo(() => {
      const existing = new Set(apiDevices.map((d) => d._id));
      return (ownershipData?.claims ?? []).filter(
         (c) => !c.originalDevice || !existing.has(c.originalDevice)
      );
   }, [ownershipData, apiDevices]);
   const qcRoot = useQueryClient();
   const [claimBusy, setClaimBusy] = useState<string | null>(null);
   const actOnClaim = async (
      claim: OwnershipClaim,
      action: 'grant' | 'deny'
   ) => {
      if (claimBusy) return;
      setClaimBusy(claim._id + action);
      try {
         if (action === 'grant') await grantOwnershipClaim(claim._id);
         else await denyOwnershipClaim(claim._id);
         await qcRoot.invalidateQueries({ queryKey: ['ownership-claims'] });
         refetchDevices();
         toast.success(
            action === 'grant'
               ? `Granted — ${claim.serial || 'device'} transferred to the new account`
               : `Blocked — ${claim.serial || 'device'} stays locked on the other account`,
            { autoClose: 2500 }
         );
      } catch (err) {
         const e = err as { response?: { data?: { error?: string } } };
         toast.error(e?.response?.data?.error || `Could not ${action}`);
      } finally {
         setClaimBusy(null);
      }
   };
   // Devices that haven't finished the wizard. Rendered in their own
   // section above the main grid so half-set-up units stand out.
   const incompleteDevices = useMemo(
      () => apiDevices.filter((d) => (d.setupStatus ?? 'done') !== 'done'),
      [apiDevices]
   );
   // Main grid shows only finished devices — incomplete ones live in the
   // "Setup in progress" section above and shouldn't double-render here.
   const devices = useMemo(
      () =>
         apiDevices
            .filter((d) => (d.setupStatus ?? 'done') === 'done')
            .map(mapApiToDisplay),
      [apiDevices]
   );
   const initial = useMemo(() => loadDevicesView(), []);
   const viewMode = useDeviceListPrefsStore((s) => s.viewMode);
   const setViewMode = useDeviceListPrefsStore((s) => s.setViewMode);
   const [expanded, setExpanded] = useState<Set<string>>(new Set(initial.expanded));

   const openResume = async (device: ApiVendoDevice) => {
      // Refresh first so the modal reads the latest setupStatus, not
      // the value cached in the store. Without this, a recent step-
      // advance (server PATCH) might not be reflected here yet, and
      // Resume could open at the previous step.
      await useVendoDevicesStore.getState().fetch();
      const fresh = useVendoDevicesStore.getState().devices.find((d) => d._id === device._id);
      const target = fresh ?? device;
      // Sub-vendo? Open the sub-vendo wizard, not the main-vendo
      // wizard — main has hardware/router steps that don't apply to
      // subs (they share the parent's router). The sub-vendo wizard
      // needs the PARENT device record as `data`, not the sub.
      if (target.parent) {
         const parent = useVendoDevicesStore
            .getState()
            .devices.find((d) => d._id === target.parent);
         if (parent) {
            handleShow({
               template: 'sub-vendo-add',
               title: `Resume Sub-Vendo Setup — ${target.name}`,
               windowType: 'desktop',
               // Tag the parent payload with the sub's _id so the
               // SubVendoAdd wizard knows to skip Step 1 (already
               // registered) and jump to the flashing step. Without
               // this, Resume would treat it as a fresh add and try
               // to register a second sub with the same parent.
               data: { ...parent, _resumeForSubId: target._id },
            });
            return;
         }
         // Parent not in store (shouldn't normally happen) — fall
         // through to the main wizard, which at least shows them
         // *something* to act on. Loud console so we notice.
         console.warn('[openResume] sub-vendo missing parent in store', target);
      }
      handleShow({
         template: 'device-register',
         title: `Resume Setup — ${device.name}`,
         windowType: 'desktop',
         data: target,
      });
   };


   useEffect(() => {
      try {
         localStorage.setItem(
            DEVICES_VIEW_STORAGE_KEY,
            JSON.stringify({ expanded: Array.from(expanded) })
         );
      } catch {}
   }, [expanded]);

   // Sort/filter/showAllSubs live in the shared store so the Dashboard
   // and the Devices tab stay in lockstep. See store header for the
   // motivation. Sort happens after the type filter so subs ride on
   // their parent's order; sort key only applies to mains.
   const sortBy = useDeviceListPrefsStore((s) => s.sortBy);
   const setSortBy = useDeviceListPrefsStore((s) => s.setSortBy);
   const showAllSubs = useDeviceListPrefsStore((s) => s.showAllSubs);
   const setShowAllSubs = useDeviceListPrefsStore((s) => s.setShowAllSubs);
   const ownershipFilter = useDeviceListPrefsStore((s) => s.ownershipFilter);
   const setOwnershipFilter = useDeviceListPrefsStore((s) => s.setOwnershipFilter);
   const healthFilter = useDeviceListPrefsStore((s) => s.healthFilter);
   const search = useDeviceListPrefsStore((s) => s.search);
   const setSearch = useDeviceListPrefsStore((s) => s.setSearch);
   const boardOptions = useBoardFilterOptions();
   const setHealthFilter = useDeviceListPrefsStore((s) => s.setHealthFilter);
   const boardFilter = useDeviceListPrefsStore((s) => s.boardFilter);
   const setBoardFilter = useDeviceListPrefsStore((s) => s.setBoardFilter);

   const pendingOutgoingIds = usePendingOutgoingDeviceIds();

   const mains = useMemo(() => {
      const onlyMains = devices.filter((d) => d.type === 'main');
      const visible = onlyMains.filter((d) => {
         if (pendingOutgoingIds.has(d._id)) return false;
         if (!matchesDeviceFilters(d, healthFilter, boardFilter)) return false;
         // A main stays when one of ITS sub-vendos matches — the sub is drawn
         // under its main, so hiding the main would hide the match.
         if (
            !matchesDeviceSearch(d, search) &&
            !devices.some((sub) => sub.parentId === d._id && matchesDeviceSearch(sub, search))
         )
            return false;
         if (ownershipFilter === 'owned') return d.isCurrentOwner !== false;
         if (ownershipFilter === 'sold') return d.isCurrentOwner === false;
         return true; // 'all'
      });
      const sorted = [...visible];
      switch (sortBy) {
         case 'newest':
            sorted.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
            break;
         case 'status':
            // Online first, then offline; tie-break by name
            sorted.sort((a, b) => {
               if (a.status !== b.status) return a.status === 'online' ? -1 : 1;
               return a.name.localeCompare(b.name);
            });
            break;
         case 'active':
            sorted.sort((a, b) => b.activeSessions - a.activeSessions);
            break;
         case 'revenue':
            sorted.sort((a, b) => b.totalCoins - a.totalCoins);
            break;
      }
      return sorted;
   }, [devices, sortBy, pendingOutgoingIds, ownershipFilter, healthFilter, boardFilter, search]);

   const ownedCount = useMemo(
      () =>
         devices.filter(
            (d) =>
               d.type === 'main' &&
               d.isCurrentOwner !== false &&
               !pendingOutgoingIds.has(d._id)
         ).length,
      [devices, pendingOutgoingIds]
   );
   const soldCount = useMemo(
      () =>
         devices.filter(
            (d) => d.type === 'main' && d.isCurrentOwner === false
         ).length,
      [devices]
   );

   const getSubs = (mainId: string) =>
      devices.filter((d) => d.parentId === mainId);

   const toggleExpand = (id: string) => {
      setExpanded((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   };

   const {
      openSettings,
      openAddSub,
      openActivate,
      openManageLicense,
      handleRefresh,
      handleResetStats,
      openDelete,
      refreshingId,
   } = useDeviceActions();

   const openRegister = () => {
      handleShow({
         template: 'device-register',
         title: 'Register New Vendo Device',
         windowType: 'desktop',
         data: null,
      });
   };

   return (
      <TabContent>
         <CreditsBar>
            <CreditsLeft>
               <CreditsIcon>🎟️</CreditsIcon>
               <CreditsBody>
                  <CreditsLabel>License Credits</CreditsLabel>
                  <CreditsValue>
                     <CreditsBigNum>{credits}</CreditsBigNum>
                     <CreditsUnit>credit{credits === 1 ? '' : 's'}</CreditsUnit>
                     <CreditsUsed>· {usedCredits} used</CreditsUsed>
                  </CreditsValue>
                  <CreditsHint>
                     1 credit = 1 vendo device, lifetime license (main or sub)
                  </CreditsHint>
               </CreditsBody>
            </CreditsLeft>
            <CreditsActions>
               <SmallBtn onClick={() => navigate('/partners')}>Buy Credits</SmallBtn>
               <SmallBtn
                  onClick={() => setShowTransfer(true)}
                  disabled={credits < 1}
                  title={credits < 1 ? 'No credits to transfer' : 'Send credits to another account'}
               >
                  Transfer
               </SmallBtn>
               <SmallBtn
                  onClick={() => setShowTransferHistory(true)}
                  title="All credit transfers you sent or received"
               >
                  View all transfers
               </SmallBtn>
            </CreditsActions>
         </CreditsBar>

         {showTransfer && (
            <TransferCreditModal
               balance={credits}
               onClose={() => setShowTransfer(false)}
            />
         )}

         {/* Same shared table as My Points / Distributor — read-only history
             of sent + received credit transfers, wider card than the
             transfer form so the 5 columns breathe. */}
         {showTransferHistory && (
            <TModalOverlay onClick={() => setShowTransferHistory(false)}>
               <TModalCard
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: 640, position: 'relative' }}
               >
                  <TModalCloseX
                     type="button"
                     aria-label="Close"
                     onClick={() => setShowTransferHistory(false)}
                  >
                     ✕
                  </TModalCloseX>
                  <TModalTitle>Credit transfers</TModalTitle>
                  <TModalSub>
                     Everything you sent or received. Completed distributor
                     sales live on the Distributor page under Sold Credits.
                  </TModalSub>
                  <CreditTransfersTable />
               </TModalCard>
            </TModalOverlay>
         )}

         {orphanClaims.length > 0 && (
            <OwnershipSection>
               <OwnershipHeader>
                  <span>⚠ Ownership requests</span>
                  <OwnershipCount>
                     {orphanClaims.length} pending
                  </OwnershipCount>
               </OwnershipHeader>
               <OwnershipHint>
                  Someone registered one of your devices on a different SmartFi
                  account. <b>Grant</b> if you sold or gave it away, or{' '}
                  <b>Block</b> if it was lost or stolen (it stays locked on the
                  other account).
               </OwnershipHint>
               {orphanClaims.map((c) => (
                  <OwnershipRow key={c._id}>
                     <OwnershipMain>
                        <OwnershipSerial>
                           {c.serial || c.hardwareKey}
                        </OwnershipSerial>
                        <OwnershipMeta>
                           Registered by {c.claimantDeviceId}
                           {c.publicIp ? ` · from ${c.publicIp}` : ''}
                           {c.isp ? ` (${c.isp})` : ''} ·{' '}
                           {new Date(c.createdAt).toLocaleString()}
                        </OwnershipMeta>
                     </OwnershipMain>
                     <OwnershipActions>
                        <OwnershipDeny
                           disabled={!!claimBusy}
                           onClick={() => actOnClaim(c, 'deny')}
                        >
                           {claimBusy === c._id + 'deny' ? 'Blocking…' : 'Block (stolen)'}
                        </OwnershipDeny>
                        <OwnershipGrant
                           disabled={!!claimBusy}
                           onClick={() => actOnClaim(c, 'grant')}
                        >
                           {claimBusy === c._id + 'grant' ? 'Granting…' : 'Grant (I sold it)'}
                        </OwnershipGrant>
                     </OwnershipActions>
                  </OwnershipRow>
               ))}
            </OwnershipSection>
         )}

         {incompleteDevices.length > 0 && (
            <IncompleteSection>
               <IncompleteHeader>
                  <span>Setup in progress</span>
                  <IncompleteCount>
                     {incompleteDevices.length} device
                     {incompleteDevices.length === 1 ? '' : 's'}
                  </IncompleteCount>
               </IncompleteHeader>
               <IncompleteHint>
                  These devices started the Register wizard but didn't finish. Resume to pick up
                  where you left off, or delete to abandon. No license credit was charged
                  yet — Option B activates on first heartbeat.
               </IncompleteHint>
               {incompleteDevices.map((d) => (
                  <IncompleteRow key={d._id}>
                     <IncompleteRowName>
                        {d.name}
                        {d.parent ? ' — sub-vendo (holds its VLAN until deleted)' : ''}
                     </IncompleteRowName>
                     <SetupSteps>
                        {INCOMPLETE_STEP_ORDER.map((s, i) => {
                           // d.setupStatus !== 'done' is filtered upstream,
                           // but TS can't narrow through the readonly tuple
                           // type — cast to any for the indexOf lookup.
                           const currentIndex = (
                              INCOMPLETE_STEP_ORDER as readonly string[]
                           ).indexOf(d.setupStatus);
                           const status: 'done' | 'current' | 'pending' =
                              i < currentIndex
                                 ? 'done'
                                 : i === currentIndex
                                 ? 'current'
                                 : 'pending';
                           return <SetupDot key={s} $status={status} />;
                        })}
                        <SetupLabel>
                           Step{' '}
                           {(INCOMPLETE_STEP_ORDER as readonly string[]).indexOf(
                              d.setupStatus
                           ) + 3}{' '}
                           of 5 — {SETUP_STATUS_LABEL[d.setupStatus]}
                        </SetupLabel>
                     </SetupSteps>
                     <IncompleteActions>
                        <SmallBtn onClick={() => openResume(d)}>Resume</SmallBtn>
                        <SmallBtn $danger onClick={() => openDelete(d)}>
                           Delete
                        </SmallBtn>
                     </IncompleteActions>
                  </IncompleteRow>
               ))}
            </IncompleteSection>
         )}

         <PendingTransfers />

         <SectionRow>
            <SectionHeader>Vendo Devices</SectionHeader>
         </SectionRow>
         {/* Row 1: how to look at the list, and the one action. Row 2: every
             filter. (Ray, 2026-09-30 — one row of eight controls squeezed and
             wrapped unpredictably.) */}
         <ToolbarTop>
            <ViewToggle>
               <ToggleBtn
                  $active={viewMode === 'card'}
                  onClick={() => setViewMode('card')}
               >
                  Cards
               </ToggleBtn>
               <ToggleBtn
                  $active={viewMode === 'list'}
                  onClick={() => setViewMode('list')}
               >
                  List
               </ToggleBtn>
            </ViewToggle>
            <ActionBtn onClick={openRegister}>+ Register Device</ActionBtn>
         </ToolbarTop>
         <HeaderActions>
            <DeviceSearchInput
               type="search"
               value={search}
               onChange={(e) => setSearch(e.target.value)}
               placeholder="Search name, ID, IP…"
               aria-label="Search devices"
            />
            <SortSelect
               value={sortBy}
               onChange={(e) => setSortBy(e.target.value as DeviceSort)}
               title="Sort fleet"
            >
               <option value="newest">Newest first</option>
               <option value="status">Status</option>
               <option value="active">Active sessions</option>
               <option value="revenue">Total revenue</option>
            </SortSelect>
            <SortSelect
               value={ownershipFilter}
               onChange={(e) =>
                  setOwnershipFilter(e.target.value as DeviceOwnershipFilter)
               }
               title="Filter by ownership"
            >
               <option value="owned">Owned ({ownedCount})</option>
               <option value="sold" disabled={soldCount === 0}>
                  Sold ({soldCount})
               </option>
               <option value="all">All ({ownedCount + soldCount})</option>
            </SortSelect>
            {/* The pill on the card, not device.status — a vendo with a
                dead NodeMCU and a healthy router is Degraded here too. */}
            <SortSelect
               value={healthFilter}
               onChange={(e) =>
                  setHealthFilter(e.target.value as DeviceHealthFilter)
               }
               title="Filter by status"
            >
               <option value="all">Any status</option>
               <option value="online">Online</option>
               <option value="degraded">Degraded</option>
               <option value="online-or-degraded">Online + Degraded</option>
               <option value="offline">Offline</option>
            </SortSelect>
            {/* Named by BOARD, never "Lite" — that word does not say which
                hardware he is looking at. */}
            <SortSelect
               value={boardFilter}
               onChange={(e) =>
                  setBoardFilter(e.target.value as DeviceBoardFilter)
               }
               title="Filter by router hardware"
            >
               <option value="all">Any hardware</option>
               {boardOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                     {o.label}
                  </option>
               ))}
            </SortSelect>
            <SubsToggleLabel title="Force-show sub-vendos under every main">
               <input
                  type="checkbox"
                  checked={showAllSubs}
                  onChange={(e) => setShowAllSubs(e.target.checked)}
               />
               Show sub-vendos
            </SubsToggleLabel>
         </HeaderActions>

         {!fetched && loading ? (
            // Grey placeholders shaped like the view being loaded (Ray 2026-10-18).
            viewMode === 'card' ? <DeviceCardsSkeleton /> : <TableSkeleton cols={10} />
         ) : mains.length === 0 ? (
            <EmptyNote>
               No vendo devices yet. Click <strong>+ Register Device</strong> to add your
               first one.
            </EmptyNote>
         ) : viewMode === 'card' ? (
            <CardView
               mains={mains}
               getSubs={getSubs}
               expanded={expanded}
               onToggleExpand={toggleExpand}
               showAllSubs={showAllSubs}
               onOpenSettings={openSettings}
               onOpenAddSub={openAddSub}
               onOpenActivate={openActivate}
               onManageLicense={openManageLicense}
               onRefresh={handleRefresh}
               refreshingId={refreshingId}
               onResetStats={handleResetStats}
               onOpenDelete={openDelete}
               claimByDevice={claimByDevice}
               onOwnershipAction={onOwnershipAction}
               reflashingIds={reflashingIds}
            />
         ) : (
            <ListView
               mains={mains}
               getSubs={getSubs}
               expanded={expanded}
               onToggleExpand={toggleExpand}
               showAllSubs={showAllSubs}
               onOpenSettings={openSettings}
               onOpenAddSub={openAddSub}
               onOpenActivate={openActivate}
               onManageLicense={openManageLicense}
               onRefresh={handleRefresh}
               refreshingId={refreshingId}
               onResetStats={handleResetStats}
               onOpenDelete={openDelete}
               claimByDevice={claimByDevice}
               onOwnershipAction={onOwnershipAction}
               reflashingIds={reflashingIds}
            />
         )}
      </TabContent>
   );
};

/* ─── Card View ────────────────────────────────────────────── */

interface DeviceViewProps {
   mains: VendoDevice[];
   getSubs: (mainId: string) => VendoDevice[];
   expanded: Set<string>;
   onToggleExpand: (id: string) => void;
   /** Global force-show: when true, every main shows its subs regardless of `expanded`. */
   showAllSubs: boolean;
   onOpenSettings: (d: VendoDevice) => void;
   onOpenAddSub: (parent: VendoDevice) => void;
   onOpenActivate: (d: VendoDevice) => void;
   onManageLicense?: (d: VendoDevice) => void;
   onRefresh: (d: VendoDevice) => void;
   refreshingId: string | null;
   onResetStats: (d: VendoDevice) => void;
   onOpenDelete: (d: VendoDevice) => void;
   /** Phase 6 — pending ownership claims keyed by the owner's device _id. */
   claimByDevice?: Map<string, OwnershipClaim>;
   onOwnershipAction?: (d: VendoDevice, claim: OwnershipClaim) => void;
   /** Device _ids the reflash modal just finished — drives the "Setting up the
    *  router" banner client-side. */
   reflashingIds?: Set<string>;
}

/* ─── Compact card UI bits (Vendo Devices card view) ──────────── */

/**
 * Three-dot menu for destructive/secondary device actions (Reset Stats,
 * Delete). Keeps the card chrome compact — the buttons used to live
 * inline at the bottom of every card, which doubled the card height.
 * Click-outside closes; the open click is stopPropagation'd so the
 * document listener doesn't immediately re-close it.
 */
const DeviceKebab: React.FC<{
   id: string;
   /** Opens this vendo's captive portal in a new tab, as a customer sees it. */
   onPreviewPortal?: () => void;
   onMoveLicense?: () => void;
   onResetStats?: () => void;
   onDelete?: () => void;
}> = ({ id, onPreviewPortal, onMoveLicense, onResetStats, onDelete }) => {
   const [open, setOpen] = useState(false);
   useEffect(() => {
      if (!open) return;
      const handler = () => setOpen(false);
      const t = window.setTimeout(
         () => document.addEventListener('click', handler),
         0
      );
      return () => {
         window.clearTimeout(t);
         document.removeEventListener('click', handler);
      };
   }, [open]);
   if (!onResetStats && !onDelete && !onMoveLicense && !onPreviewPortal) return null;
   return (
      <KebabWrap>
         <KebabBtn
            type="button"
            title="More actions"
            aria-label={`More actions for ${id}`}
            onClick={(e) => {
               e.stopPropagation();
               setOpen((o) => !o);
            }}
         >
            <IconsLib icon="more-2-fill" size="16px" color={colors.GRAY3} />
         </KebabBtn>
         {open && (
            <KebabMenu>
               {onPreviewPortal && (
                  <KebabItem
                     type="button"
                     onClick={() => {
                        setOpen(false);
                        onPreviewPortal();
                     }}
                  >
                     Open portal
                  </KebabItem>
               )}
               {onMoveLicense && (
                  <KebabItem
                     type="button"
                     onClick={() => {
                        setOpen(false);
                        onMoveLicense();
                     }}
                  >
                     Move License
                  </KebabItem>
               )}
               {onResetStats && (
                  <KebabItem
                     type="button"
                     onClick={() => {
                        setOpen(false);
                        onResetStats();
                     }}
                  >
                     Reset Sales
                  </KebabItem>
               )}
               {onDelete && (
                  <KebabItem
                     type="button"
                     $danger
                     onClick={() => {
                        setOpen(false);
                        onDelete();
                     }}
                  >
                     Delete
                  </KebabItem>
               )}
            </KebabMenu>
         )}
      </KebabWrap>
   );
};

const SettingsIconBtn: React.FC<{ onClick: () => void; label: string }> = ({
   onClick,
   label,
}) => (
   <IconActionBtn
      type="button"
      title="Settings"
      aria-label={`Settings for ${label}`}
      onClick={onClick}
   >
      <IconsLib icon="settings-3-line" size="16px" color={colors.GRAY3} />
   </IconActionBtn>
);

export const CardView = ({ mains, getSubs, expanded, onToggleExpand, showAllSubs, onOpenSettings, onOpenAddSub, onOpenActivate, onManageLicense, onRefresh: _onRefresh, refreshingId: _refreshingId, onResetStats, onOpenDelete, claimByDevice, onOwnershipAction, reflashingIds }: DeviceViewProps) => (
   <MainCardGrid>
      {mains.map((d) => {
         // getSubs filters by Mongo _id (sub.parentId is the parent's
         // ObjectId string). d.id is the human-readable deviceId
         // (ESP-XXXX) — wrong field for this lookup. Caught 2026-05-15
         // when sub-vendos appeared in DB with the right parent but
         // never nested in the dashboard.
         const subs = getSubs(d._id);
         const isOpen = showAllSubs || expanded.has(d.id);
         // The main card's money is the MAIN vendo's own; each sub-vendo
         // card under it shows its own. Active stays the whole router.
         const totalToday = todayTotal(d);
         const totalActive = d.activeSessions + subs.reduce((sum, s) => sum + s.activeSessions, 0);
         const up = d.uptime;
         const totalRevenue = ownRevenue(d);
         return (
            <MainColumn key={d.id} data-focus-id={d._id}>
               <DeviceCardFlip
                  $online={d.status === 'online'}
                  back={(unflip, active) => (
                     <SystemInfoFace
                        deviceId={d.id}
                        sys={d.sys}
                        diag={d.diag}
                        lastSeenAt={d.routerLastSeenAt}
                        platform={d.platform}
                        onBack={unflip}
                        active={active}
                     />
                  )}
               >
                  {(flip) => (
                     <>
                  {/* Just reflashed — the router restarts its REST for 1-2 min
                     while config applies, so it briefly looks offline. Reassure
                     the operator instead of letting it read as a failure. */}
                  {(isReflashing(d) || reflashingIds?.has(d._id)) && (
                     <SettingUpBanner>
                        ⏳ Setting up the router — it restarts its services for a
                        minute or two. This is normal; no action needed.
                     </SettingUpBanner>
                  )}
                  {/* Phase 6 — suspected-stolen banner: this hardware was
                     registered on another account. The action modal lets the
                     owner allow it (sold) or block it (stolen). */}
                  {(() => {
                     const claim = claimByDevice?.get(d._id);
                     return claim ? (
                        <StolenBanner>
                           <StolenBannerText>
                              ⚠ Suspected stolen — registered on another account
                           </StolenBannerText>
                           <StolenBannerBtn
                              onClick={() => onOwnershipAction?.(d, claim)}
                           >
                              Take action
                           </StolenBannerBtn>
                        </StolenBanner>
                     ) : null;
                  })()}
                  {/* Hardware was claimed away by another account — this device
                     no longer controls a router. */}
                  {d.hardwareReleasedReason && (
                     <NoRouterBanner>
                        🔌 No router —{' '}
                        {d.hardwareReleasedReason === 'sold'
                           ? 'sold to the new owner'
                           : 'reported stolen'}
                     </NoRouterBanner>
                  )}
                  {/* Ownership-lock banner takes over the license banner: this
                     hardware belongs to another account, so "Activate" would be
                     misleading. Explain WHY it's locked (pending vs stolen). */}
                  {d.ownershipLockReason === 'pending' && (
                     <OwnershipLockBanner $stolen={false}>
                        ⏳ Pending ownership check — this router is registered to
                        another SmartFi account. Awaiting the owner's decision.
                     </OwnershipLockBanner>
                  )}
                  {d.ownershipLockReason === 'stolen' && (
                     <OwnershipLockBanner $stolen={true}>
                        🚫 Reported stolen — this hardware is blocked. Contact the
                        seller.
                     </OwnershipLockBanner>
                  )}
                  {/* Owner marked THEIR OWN device lost/stolen → portal is
                     locked. Takes over the license banner (Activate would be
                     misleading while it's blocked). Un-mark lives in Settings. */}
                  {d.isCurrentOwner && d.markedStolen && (
                     <OwnershipLockBanner $stolen={true}>
                        🚫 Marked lost/stolen — portal is locked. Remove the flag
                        in Settings to re-enable.
                     </OwnershipLockBanner>
                  )}
                  {/* Licence banner on OWNED cards and on cards the
                     viewer SOLD: sellers transfer a vendo and forget to
                     activate the licence (Ray, 2026-09-16). The seller
                     pays with their own credits — the server charges the
                     caller, never the buyer. */}
                  {showLicenseBanner(d) && !(isWaitingForPower(d) && showCoinBoxBanner(d)) && (
                     <DeviceLicenseBanner
                        licenseStatus={d.licenseStatus}
                        trialStartedAt={d.trialStartedAt ?? null}
                        trialDaysLeft={d.trialDaysLeft}
                        autoRenewLicense={d.autoRenewLicense}
                        soldToEmail={d.isCurrentOwner === false ? d.soldTransfer?.toEmail : undefined}
                        onActivate={() => onOpenActivate(d)}
                        voucherOnly={d.portalMode === 'voucher'}
                        hwBoard={d.hwBoard}
                        nodeMcuOnline={d.coinbox === 'online'}
                        routerOnline={d.status === 'online'}
                     />
                  )}
                  {showVoucherOnlyNote(d) && <VoucherOnlyNote />}
                  {showCoinBoxBanner(d) && (
                     <CoinBoxBanner
                        onFlash={() => onOpenSettings({ ...d, _initialTab: 'hardware' } as VendoDevice)}
                        trialWaiting={showLicenseBanner(d) && isWaitingForPower(d)}
                     />
                  )}
                  <DeviceCardHeader>
                     <DeviceHeaderLeft>
                        <DeviceName>{d.name}</DeviceName>
                        <TypePill $main>Main</TypePill>
                        <ModelBadgeFor d={d} />
                        <VariantBadge
                           $hybrid={runningModeLabel(d) === 'Hybrid'}
                        >
                           {runningModeLabel(d)}
                        </VariantBadge>
                        {!d.isCurrentOwner && (
                           <SoldPill
                              title={
                                 d.soldTransfer?.toEmail
                                    ? `Sold to ${d.soldTransfer.toEmail}`
                                    : 'Sold'
                              }
                           >
                              SOLD
                           </SoldPill>
                        )}
                        {isLicensed(d.licenseStatus) && d.isCurrentOwner && (
                           <ActiveLicensePill
                              title="Manage license — reclaim or move"
                              style={{ cursor: 'pointer' }}
                              onClick={() => onManageLicense?.(d)}
                           >
                              ✓ Active
                           </ActiveLicensePill>
                        )}
                        {d.updateState && d.updateState !== 'idle' && (
                           <UpdatePill
                              $rebooting={d.updateState === 'rebooting'}
                              title={
                                 d.updateState === 'rebooting'
                                    ? 'Router is rebooting to finish an update — it will be offline briefly, then come back on its own.'
                                    : 'A software update is in progress.'
                              }
                           >
                              {d.updateState === 'rebooting'
                                 ? '🔄 Rebooting'
                                 : '⏳ Updating'}
                           </UpdatePill>
                        )}
                        {d.otaStatus?.state === 'failed' && (
                           <OtaPill
                              $failed
                              title={otaPillTitle(d.otaStatus)}
                           >
                              {otaPillLabel(d.otaStatus)}
                           </OtaPill>
                        )}
                        {d.inboundUnreachable && (
                           <ReachPill title="The device is online, but our server cannot reach into it. Coins and the portal still work for customers. Affected: instant grants/kicks are slower (up to ~30s) and the Awaiting tab may be empty. Usually a firewall or tunnel problem on the device — a reboot often fixes it; if not, contact support.">
                              ⚠ Reach issue
                           </ReachPill>
                        )}
                     </DeviceHeaderLeft>
                     <CardHeaderRight>
                        {/* Only on a box that can actually act on it: an
                            offline device cannot fetch an update, so a badge
                            there is a call to action with nothing behind it. */}
                        {(d.updatesAvailable?.count ?? 0) > 0 &&
                           d.routerStatus === 'connected' && (
                           <UpdateCountBadge
                              title={[
                                 d.updatesAvailable?.router
                                    ? `Router update available: ${d.updatesAvailable.router}`
                                    : null,
                                 d.updatesAvailable?.coinBox
                                    ? `NodeMCU update available: ${d.updatesAvailable.coinBox}`
                                    : null,
                              ]
                                 .filter(Boolean)
                                 .join('\n')}
                           >
                              {d.updatesAvailable?.count}
                           </UpdateCountBadge>
                        )}
                        <RouterPill status={d.routerStatus} />                        <DeviceStatusBadge
                           status={d.status}
                           routerStatus={d.routerStatus}
                        />
                        {/* The gear opens on a SOLD vendo too (Ray, 2026-08-30):
                            the buyer is usually not technical, so the seller
                            keeps setup and updates. The modal hides Ownership
                            and the Danger Zone, and the server refuses those
                            routes for a seller regardless.
                            The kebab does NOT come with it — every item on it
                            (move licence, reset sales, delete) is owner-only. */}
                        <SettingsIconBtn
                           label={d.name}
                           onClick={() => onOpenSettings(d)}
                        />
                        {d.isCurrentOwner && (
                           <DeviceKebab
                              id={d._id}
                              onMoveLicense={
                                 isLicensed(d.licenseStatus) && d.isCurrentOwner
                                    ? () => onManageLicense?.(d)
                                    : undefined
                              }
                              onPreviewPortal={() => openPortal(d.portalSlug)}
                              onResetStats={() => onResetStats(d)}
                              onDelete={() => onOpenDelete(d)}
                           />
                        )}
                     </CardHeaderRight>
                  </DeviceCardHeader>
                  <MetaLine>
                     <MetaChip>{d.id}</MetaChip>
                     <MetaDot>·</MetaDot>
                     <MetaChip>{d.lastHeartbeat}</MetaChip>
                     {d.createdAt && (
                        <>
                           <MetaDot>·</MetaDot>
                           <MetaChip title={`Registered ${new Date(d.createdAt).toLocaleString('en-PH')}`}>
                              Registered {formatRelativeDate(d.createdAt)}
                           </MetaChip>
                        </>
                     )}
                     {hasVer(d.routerFirmware) && (
                        <VerChip
                           title={
                              effectiveRouterVersion(d.routerFirmware, d.routerBundle).detail ??
                              'Router firmware / config version'
                           }
                        >
                           Router {effectiveRouterVersion(d.routerFirmware, d.routerBundle).shown}
                        </VerChip>
                     )}
                     {hasVer(d.firmware) && (
                        <VerChip title="NodeMCU firmware version">
                           NodeMCU {fwBase(d.firmware)}
                        </VerChip>
                     )}
                  </MetaLine>
                  <DeviceStatsRow $cols={3 + (up ? 1 : 0) + (d.signal ? 1 : 0)}>
                     <DeviceStat>
                        <DeviceStatLabel>Total Today</DeviceStatLabel>
                        {d.isCurrentOwner ? (
                           <DeviceStatValue>₱{money(totalToday)}</DeviceStatValue>
                        ) : (
                           <ActivityBadge $active={totalToday > 0}>
                              <ActivityDot $active={totalToday > 0} />
                              {totalToday > 0 ? 'Earning' : 'Quiet'}
                           </ActivityBadge>
                        )}
                     </DeviceStat>
                     <DeviceStat>
                        <DeviceStatLabel>Revenue</DeviceStatLabel>
                        {d.isCurrentOwner ? (
                           <DeviceStatValue>
                              ₱{money(totalRevenue)}
                           </DeviceStatValue>
                        ) : (
                           <Redacted>—</Redacted>
                        )}
                     </DeviceStat>
                     <DeviceStat>
                        <DeviceStatLabel>Active</DeviceStatLabel>
                        {d.isCurrentOwner ? (
                           <DeviceStatValue>{totalActive}</DeviceStatValue>
                        ) : (
                           <ActivityBadge $active={totalActive > 0}>
                              <ActivityDot $active={totalActive > 0} />
                              {totalActive > 0 ? 'In use' : 'Idle'}
                           </ActivityBadge>
                        )}
                     </DeviceStat>
                     {/* How long it has been in its current state. The card
                         showed money and sessions but never "has this been down
                         long?" — the question an operator actually opens the
                         dashboard with. Hidden entirely when we cannot anchor
                         it: "up 0m" on a box we know nothing about would be a
                         confident lie, and this whole area exists to stop the
                         guessing. */}
                     {up && (
                        <DeviceStat>
                           <DeviceStatLabel>
                              {up.down ? 'Downtime' : 'Uptime'}
                           </DeviceStatLabel>
                           <DeviceStatValue
                              title={up.title}
                              style={up.down ? { color: '#c0392b' } : undefined}
                           >
                              {up.duration}
                           </DeviceStatValue>
                        </DeviceStat>
                     )}
                     {/* Coin-box signal. Ray, 2026-08-23: "in the device card
                         view, can you include to show the signal. its very
                         important". It is the first thing looked at when a box
                         misbehaves, and it lived only in the list until now.

                         Hidden for a wired box, which reports 0 dBm — printing
                         that would read as the strongest possible signal on a
                         board with no radio at all. */}
                     {d.signal && (
                        <DeviceStat>
                           <DeviceStatLabel>Signal</DeviceStatLabel>
                           <DeviceStatValue title={d.signal.title}>
                              <SignalCell signal={d.signal} compact />
                           </DeviceStatValue>
                        </DeviceStat>
                     )}
                  </DeviceStatsRow>
                  <DeviceDetailRow>
                     <SubsPill onClick={() => onToggleExpand(d.id)} $open={isOpen}>
                        {subs.length} Sub-Vendo{subs.length === 1 ? '' : 's'}
                        <SubsArrow $open={isOpen}>▾</SubsArrow>
                     </SubsPill>
                     {/* Turns the card over. Shown on every router: the face
                         behind explains ITSELF when a router is too old to
                         report, which is a more useful answer than a button
                         that is missing for a reason nobody can see. */}
                     <SubsPill
                        as="button"
                        onClick={flip}
                        $open={false}
                        title="What this router says about its own condition"
                     >
                        System Info ›
                     </SubsPill>
                  </DeviceDetailRow>
                     </>
                  )}
               </DeviceCardFlip>

               {isOpen && (
                  <SubStack>
                     {subs.map((s) => (
                        <SubDeviceCard key={s.id} $online={s.status === 'online'}>
                           {/* Same Activate / Renew banner as the main card — a
                               sub-vendo has its own licence and used to hide it
                               behind a small pill (Ray, 2026-09-29). */}
                           {showLicenseBanner(s) && !(isWaitingForPower(s) && showCoinBoxBanner(s)) && (
                              <DeviceLicenseBanner
                                 licenseStatus={s.licenseStatus}
                                 trialStartedAt={s.trialStartedAt ?? null}
                                 trialDaysLeft={s.trialDaysLeft}
                                 autoRenewLicense={s.autoRenewLicense}
                                 soldToEmail={s.isCurrentOwner === false ? s.soldTransfer?.toEmail : undefined}
                                 onActivate={() => onOpenActivate(s)}
                                 voucherOnly={s.portalMode === 'voucher'}
                                 hwBoard={s.hwBoard}
                                 nodeMcuOnline={s.coinbox === 'online'}
                                 routerOnline={s.status === 'online'}
                              />
                           )}
                           {showVoucherOnlyNote(s) && <VoucherOnlyNote />}
                           {showCoinBoxBanner(s) && (
                              <CoinBoxBanner
                                 onFlash={() => onOpenSettings({ ...s, _initialTab: 'hardware' } as VendoDevice)}
                                 trialWaiting={showLicenseBanner(s) && isWaitingForPower(s)}
                              />
                           )}
                           {/* Compact sub-vendo card (Ray approved the preview 2026-10-09):
                               name row, then status pills with ⚙ and ⋮ side by side,
                               then ID + money + heartbeat on one line. */}
                           <SubCardHeader>
                              <DeviceHeaderLeft>
                                 <SubName>{s.name}</SubName>
                                 <TypePill>Sub</TypePill>
                                 {s.vlanId != null && (
                                    <VlanChip
                                       title={
                                          s.vlanSubnet
                                             ? `VLAN ${s.vlanId} · ${s.vlanSubnet}${
                                                  s.vlanPort ? ` · ${s.vlanPort}` : ''
                                               }`
                                             : `VLAN ${s.vlanId}`
                                       }
                                    >
                                       VLAN {s.vlanId}
                                    </VlanChip>
                                 )}
                              </DeviceHeaderLeft>
                           </SubCardHeader>
                           <SubPillRow>
                                 <RouterPill status={s.routerStatus} />
                                 <DeviceStatusBadge
                                    status={s.status}
                                    routerStatus={s.routerStatus}
                                 />
                                 {/* The yellow bar above already says "Trial · N days left". */}
                                 {!showLicenseBanner(s) && (
                                 <LicensePill
                                    $status={s.licenseStatus}
                                    onClick={
                                       s.isCurrentOwner && !isLicensed(s.licenseStatus)
                                          ? () => onOpenActivate(s)
                                          : undefined
                                    }
                                 >
                                    {licenseChipText(s.licenseStatus, s.trialDaysLeft)}
                                 </LicensePill>
                                 )}
                                 <SubSpacer />
                                 {/* Gear opens on a SOLD vendo too — see the main card above. */}
                                 <SettingsIconBtn
                                    label={s.name}
                                    onClick={() => onOpenSettings(s)}
                                 />
                                 {s.isCurrentOwner && (
                                    <DeviceKebab
                                       id={s._id}
                                       onMoveLicense={
                                          isLicensed(s.licenseStatus) &&
                                          s.isCurrentOwner
                                             ? () => onManageLicense?.(s)
                                             : undefined
                                       }
                                       onPreviewPortal={() =>
                                          openPortal(s.portalSlug || d.portalSlug)
                                       }
                                       onResetStats={() => onResetStats(s)}
                                       onDelete={() => onOpenDelete(s)}
                                    />
                                 )}
                           </SubPillRow>
                           <SubStatsRow>
                              <MetaChip>{s.id}</MetaChip>
                              <SubInlineStat>Today <b>₱{money(todayTotal(s))}</b></SubInlineStat>
                              <SubInlineStat>Revenue <b>₱{money(ownRevenue(s))}</b></SubInlineStat>
                              <SubInlineStat>Active <b>{s.activeSessions}</b></SubInlineStat>
                              <SubSpacer />
                              {s.localIp && (
                                 <SubInlineStat>IP <b>{s.localIp}</b></SubInlineStat>
                              )}
                              <SubInlineStat>Heartbeat <b>{s.lastHeartbeat}</b></SubInlineStat>
                              {s.createdAt && (
                                 <SubInlineStat>Registered <b>{formatRelativeDate(s.createdAt)}</b></SubInlineStat>
                              )}
                           </SubStatsRow>
                        </SubDeviceCard>
                     ))}
                     {d.isCurrentOwner && (
                        <AddSubBtn onClick={() => onOpenAddSub(d)}>
                           + Add Sub-Vendo
                        </AddSubBtn>
                     )}
                  </SubStack>
               )}
            </MainColumn>
         );
      })}
   </MainCardGrid>
);

/* ─── List View ────────────────────────────────────────────── */

/** Device name in the list: click = every column of the row in one compact
 *  sheet, same window as the admin Fleet Device Status (Ray 2026-10-18). */
function DeviceNameDetails({
   d,
   subs,
   onOpenSettings,
   isSub = false,
}: {
   d: VendoDevice;
   subs: VendoDevice[];
   onOpenSettings: (d: VendoDevice) => void;
   isSub?: boolean;
}) {
   const [open, setOpen] = React.useState(false);
   const close = React.useCallback(() => setOpen(false), []);
   const owner = d.isCurrentOwner;
   const all = [d, ...subs];
   const sum = (k: 'todayCoins' | 'lifetimeCoins' | 'totalSessions' | 'activeSessions') => all.reduce((n, x) => n + (x[k] ?? 0), 0);
   const sections: DetailSectionData[] = [
      {
         title: 'Device',
         items: [
            // Same pills as the device card, so the sheet reads at a glance.
            ['Type', <TypePill $main={!isSub}>{isSub ? 'Sub-vendo' : 'Main'}</TypePill>],
            ['License', <LicensePill $status={d.licenseStatus}>{licenseChipText(d.licenseStatus, d.trialDaysLeft)}</LicensePill>],
            ['Board', <ModelNameFor d={d} />],
            ['Product', <VariantBadge $hybrid={runningModeLabel(d) === 'Hybrid'}>{runningModeLabel(d)}</VariantBadge>],
            ['Local IP', d.localIp || '—'],
            ['MAC', d.mac || '—'],
            [
               'Portal',
               <PortalLink href={`${PORTAL_BASE}/${encodeURIComponent(d.portalSlug)}`} target="_blank" rel="noreferrer">
                  Open ↗
               </PortalLink>,
            ],
         ],
      },
      {
         title: 'Status',
         items: [
            ['Router', <OnlineBadge status={d.routerStatus} />],
            ['Connection', <UptimeCell up={d.uptime} />],
            ['Heartbeat', d.lastHeartbeat || '—'],
            ['Router firmware', hasVer(d.routerFirmware) ? effectiveRouterVersion(d.routerFirmware, d.routerBundle).shown : '—'],
         ],
      },
      {
         title: 'NodeMCU',
         items: [
            ['Signal', <SignalCell signal={d.signal} />],
            ['Rejoins', <RejoinCell rate={d.rejoins} />],
            ['Antenna', <AntennaCell antenna={d.antenna} />],
            ['NodeMCU firmware', d.firmware || '—'],
         ],
      },
      {
         // The card's own System Info face, as is (rings + rows).
         title: 'System info',
         content: (
            <SystemInfoFace
               deviceId={d.id}
               sys={d.sys}
               diag={d.diag}
               lastSeenAt={d.routerLastSeenAt}
               platform={d.platform}
               onBack={() => {}}
               active
               embedded
            />
         ),
      },
      {
         title: subs.length ? 'Sales (with sub-vendos)' : 'Sales',
         items: owner
            ? [
                 ["Today's coins", `₱${money(sum('todayCoins'))}`],
                 ['Active sessions', String(sum('activeSessions'))],
                 ['Total sessions', String(sum('totalSessions'))],
                 ['Lifetime ₱', `₱${money(sum('lifetimeCoins'))}`],
              ]
            : [],
      },
   ];
   return (
      <>
         <NameBtn type="button" title="Show every detail of this vendo" onClick={() => setOpen(true)}>
            {d.name}
         </NameBtn>
         {open && (
            <DeviceDetailsSheet
               title={d.name}
               deviceId={d.id ?? ''}
               badge={<DeviceStatusBadge status={d.status} routerStatus={d.routerStatus} />}
               sections={sections}
               onClose={close}
               settingsLabel={owner ? 'Device Settings' : 'View'}
               onSettings={() => {
                  setOpen(false);
                  onOpenSettings(d);
               }}
            />
         )}
      </>
   );
}

export const ListView = ({ mains, getSubs, expanded, onToggleExpand, showAllSubs, onOpenSettings, onOpenAddSub, onOpenActivate, onManageLicense, onRefresh, refreshingId, onResetStats, onOpenDelete, claimByDevice, onOwnershipAction, reflashingIds }: DeviceViewProps) => (
   <TableWrapper>
      <StyledTable>
         <thead>
            <tr>
               <Th style={{ width: 30 }}></Th>
               <Th>Name</Th>
               <Th>Type</Th>
               <Th>Status</Th>
               <Th style={{ minWidth: 108, whiteSpace: 'nowrap' }}>Connection</Th>
               <Th>License</Th>
               <Th>Today</Th>
               <Th>Active</Th>
               <Th>Total sessions</Th>
               <Th>Lifetime ₱</Th>
               <Th>Portal</Th>
               <Th>Local IP</Th>
               <Th>MAC</Th>
               <Th>Board</Th>
               <Th>Product</Th>
               <Th>Router</Th>
               <Th>Heartbeat</Th>
               <Th style={{ whiteSpace: 'nowrap' }}>Registered</Th>
               {/* Beside Heartbeat because all three describe the COIN BOX, not
                   the router. Fixed widths so neither wraps. */}
               <Th style={{ minWidth: 132, whiteSpace: 'nowrap' }}>Signal</Th>
               <Th style={{ minWidth: 96, whiteSpace: 'nowrap' }}>Rejoins</Th>
               <Th style={{ minWidth: 120, whiteSpace: 'nowrap' }}>Antenna</Th>
               <Th>Router FW</Th>
               <Th>Firmware</Th>
               {/* Only Settings + Add Sub are pinned (Ray 2026-10-18): the
                   pinned column stays narrow, the rest scroll with the row. */}
               <Th>More</Th>
               <Th $stickyRight>Actions</Th>
            </tr>
         </thead>
         <tbody>
            {mains.map((d) => {
               const subs = getSubs(d._id);
               const isOpen = showAllSubs || expanded.has(d.id);
               // Own money on the main row; each sub-vendo row below shows its own.
               const totalToday = todayTotal(d);
               const totalActive = d.activeSessions + subs.reduce((sum, s) => sum + s.activeSessions, 0);
               return (
                  <React.Fragment key={d.id}>
                     <tr>
                        <Td>
                           <ExpandBtn onClick={() => onToggleExpand(d.id)}>
                              {isOpen ? '▾' : '▸'}
                           </ExpandBtn>
                        </Td>
                        <Td $bold>
                           <DeviceNameDetails d={d} subs={subs} onOpenSettings={onOpenSettings} />
                           {(isReflashing(d) || reflashingIds?.has(d._id)) && (
                              <SettingUpChip title="Router is restarting its services after a reflash (~1-2 min)">
                                 ⏳ setting up
                              </SettingUpChip>
                           )}
                           {claimByDevice?.get(d._id) && (
                              <StolenChip
                                 title="Registered on another account — take action"
                                 onClick={() =>
                                    onOwnershipAction?.(
                                       d,
                                       claimByDevice.get(d._id)!
                                    )
                                 }
                              >
                                 ⚠ stolen?
                              </StolenChip>
                           )}
                           {d.hardwareReleasedReason && (
                              <NoRouterChip
                                 title={
                                    d.hardwareReleasedReason === 'sold'
                                       ? 'Sold to the new owner'
                                       : 'Reported stolen'
                                 }
                              >
                                 🔌 no router
                              </NoRouterChip>
                           )}
                           {!d.isCurrentOwner && (
                              <SoldHint title={d.soldTransfer?.toEmail
                                 ? `Sold to ${d.soldTransfer.toEmail}${
                                      d.soldTransfer.acceptedAt
                                         ? ` · ${new Date(d.soldTransfer.acceptedAt).toLocaleDateString()}`
                                         : ''
                                   }`
                                 : 'Sold'}>
                                 to {d.soldTransfer?.toEmail || 'buyer'}
                              </SoldHint>
                           )}
                        </Td>
                        <Td>
                           <TypeCell>
                              <TypePill $main>Main</TypePill>
                              {!d.isCurrentOwner && <SoldPill>SOLD</SoldPill>}
                           </TypeCell>
                        </Td>
                        <Td>
                           <DeviceStatusBadge
                              status={d.status}
                              routerStatus={d.routerStatus}
                           />
                        </Td>
                        {/* How long it has held its current state. "Offline" on
                            its own does not tell an operator whether to drive
                            out there; "Offline 3d" does. Em dash when the box
                            reports no history — never a made-up zero. */}
                        <Td>
                           <UptimeCell up={d.uptime} />
                        </Td>
                        <Td>
                           <LicensePill
                              $status={d.licenseStatus}
                              onClick={!isLicensed(d.licenseStatus) ? () => onOpenActivate(d) : undefined}
                           >
                              {licenseChipText(d.licenseStatus, d.trialDaysLeft)}
                           </LicensePill>
                        </Td>
                        <Td>
                           {d.isCurrentOwner ? (
                              <>
                                 ₱{money(totalToday)}
                                 {subs.length > 0 && (
                                    <TdSubtext> (₱{money(d.todayCoins)}+₱{money(totalToday - d.todayCoins)})</TdSubtext>
                                 )}
                              </>
                           ) : (
                              <ActivityBadge
                                 $active={totalToday > 0}
                                 title="Buyer's earnings hidden — pulse signals whether transactions are happening today"
                              >
                                 <ActivityDot $active={totalToday > 0} />
                                 {totalToday > 0 ? 'Earning' : 'Quiet'}
                              </ActivityBadge>
                           )}
                        </Td>
                        <Td>
                           {d.isCurrentOwner ? (
                              <>
                                 {totalActive}
                                 {subs.length > 0 && (
                                    <TdSubtext> ({d.activeSessions}+{totalActive - d.activeSessions})</TdSubtext>
                                 )}
                              </>
                           ) : (
                              <ActivityBadge
                                 $active={totalActive > 0}
                                 title="Buyer's session count hidden — pulse signals if there's a live session"
                              >
                                 <ActivityDot $active={totalActive > 0} />
                                 {totalActive > 0 ? 'In use' : 'Idle'}
                              </ActivityBadge>
                           )}
                        </Td>
                        <Td>
                           {d.isCurrentOwner
                              ? d.totalSessions + subs.reduce((s2, sub) => s2 + sub.totalSessions, 0)
                              : <Redacted>—</Redacted>}
                        </Td>
                        <Td>
                           {d.isCurrentOwner
                              ? `₱${money(d.lifetimeCoins + subs.reduce((s2, sub) => s2 + sub.lifetimeCoins, 0))}`
                              : <Redacted>—</Redacted>}
                        </Td>
                        <Td>
                           <PortalLink
                              href={`${PORTAL_BASE}/${encodeURIComponent(d.portalSlug)}`}
                              target="_blank"
                              rel="noreferrer"
                              title="Open the captive portal in a new tab"
                           >
                              Open ↗
                           </PortalLink>
                        </Td>
                        <Td $mono>{d.localIp}</Td>
                        <Td $mono>{d.mac}</Td>
                        <Td><ModelNameFor d={d} /></Td>
                        <Td>
                           <VariantBadge
                              $hybrid={runningModeLabel(d) === 'Hybrid'}
                           >
                              {runningModeLabel(d)}
                           </VariantBadge>
                        </Td>
                        <Td><OnlineBadge status={d.routerStatus} /></Td>
                        <Td $muted>{d.lastHeartbeat}</Td>
                        <Td $muted style={{ whiteSpace: 'nowrap' }}>{d.createdAt ? formatRelativeDate(d.createdAt) : '—'}</Td>
                        <Td><SignalCell signal={d.signal} /></Td>
                        <Td><RejoinCell rate={d.rejoins} /></Td>
                        <Td><AntennaCell antenna={d.antenna} /></Td>
                        <Td $mono>{hasVer(d.routerFirmware) ? effectiveRouterVersion(d.routerFirmware, d.routerBundle).shown : '—'}</Td>
                        <Td $mono>{d.firmware}</Td>
                        <Td>
                           {d.isCurrentOwner && (
                              <TableActions>
                                 {isLicensed(d.licenseStatus) && (
                                    <SmallBtn onClick={() => onManageLicense?.(d)}>
                                       Move License
                                    </SmallBtn>
                                 )}
                                 <SmallBtn $danger onClick={() => onResetStats(d)}>
                                    Reset Sales
                                 </SmallBtn>
                              </TableActions>
                           )}
                        </Td>
                        <Td $stickyRight>
                           <TableActions>
                              <SmallBtn onClick={() => onOpenSettings(d)}>
                                 {d.isCurrentOwner ? 'Settings' : 'View'}
                              </SmallBtn>
                              {d.isCurrentOwner && (
                                 <ListAddSubBtn onClick={() => onOpenAddSub(d)}>
                                    + Add Sub
                                 </ListAddSubBtn>
                              )}
                           </TableActions>
                        </Td>
                     </tr>
                     {isOpen &&
                        subs.map((s) => (
                           <tr key={s.id}>
                              <Td />
                              <Td $indent>
                                 <SubIndent>↳ <DeviceNameDetails d={s} subs={[]} onOpenSettings={onOpenSettings} isSub /></SubIndent>
                              </Td>
                              <Td>
                                 <TypeCell>
                                    <TypePill>Sub</TypePill>
                                    {s.vlanId != null && (
                                       <VlanChip
                                          title={
                                             s.vlanSubnet
                                                ? `VLAN ${s.vlanId} · ${s.vlanSubnet}${
                                                     s.vlanPort ? ` · ${s.vlanPort}` : ''
                                                  }`
                                                : `VLAN ${s.vlanId}`
                                          }
                                       >
                                          VLAN {s.vlanId}
                                       </VlanChip>
                                    )}
                                 </TypeCell>
                              </Td>
                              <Td>
                                 <DeviceStatusBadge
                                    status={s.status}
                                    routerStatus={s.routerStatus}
                                 />
                              </Td>
                              {/* Connection — blank for a sub. It has no router
                                  of its own; it rides the main's, so its uptime
                                  IS the main's and repeating it on every sub row
                                  would read as independent evidence. Without this
                                  cell every column after it shifts left by one. */}
                              <Td />
                              <Td>
                                 <LicensePill
                                    $status={s.licenseStatus}
                                    onClick={
                                       s.isCurrentOwner && !isLicensed(s.licenseStatus)
                                          ? () => onOpenActivate(s)
                                          : undefined
                                    }
                                 >
                                    {licenseChipText(s.licenseStatus, s.trialDaysLeft)}
                                 </LicensePill>
                              </Td>
                              <Td>₱{money(s.todayCoins)}</Td>
                              <Td>{s.activeSessions}</Td>
                              <Td>{s.totalSessions}</Td>
                              <Td>₱{money(s.lifetimeCoins)}</Td>
                              <Td>
                                 {/* Sub portals live on the PARENT's slug —
                                     ?sub= renders this sub's name/theme, so
                                     the operator can verify the design. */}
                                 <PortalLink
                                    href={`${PORTAL_BASE}/${encodeURIComponent(d.portalSlug)}?sub=${encodeURIComponent(s.id ?? '')}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Open this sub-vendo's portal in a new tab"
                                 >
                                    Open ↗
                                 </PortalLink>
                              </Td>
                              <Td $mono>{s.localIp}</Td>
                              <Td $mono>{s.mac}</Td>
                              <Td $muted>—</Td>
                              <Td $muted>—</Td>
                              <Td><OnlineBadge status={s.routerStatus} /></Td>
                              <Td $muted>{s.lastHeartbeat}</Td>
                              <Td $muted style={{ whiteSpace: 'nowrap' }}>{s.createdAt ? formatRelativeDate(s.createdAt) : '—'}</Td>
                              {/* A sub HAS its own NodeMCU, so unlike Connection
                                  these are real per-sub readings, not the main's
                                  repeated. */}
                              <Td><SignalCell signal={s.signal} /></Td>
                              <Td><RejoinCell rate={s.rejoins} /></Td>
                              <Td><AntennaCell antenna={s.antenna} /></Td>
                              <Td $mono>{hasVer(s.routerFirmware) ? fwBase(s.routerFirmware) : '—'}</Td>
                              <Td $mono>{s.firmware}</Td>
                              <Td>
                                 {s.isCurrentOwner && (
                                    <TableActions>
                                       {isLicensed(s.licenseStatus) && (
                                          <SmallBtn onClick={() => onManageLicense?.(s)}>
                                             Move License
                                          </SmallBtn>
                                       )}
                                       <SmallBtn $danger onClick={() => onOpenDelete(s)}>Delete</SmallBtn>
                                    </TableActions>
                                 )}
                              </Td>
                              <Td $stickyRight>
                                 {s.isCurrentOwner && (
                                    <TableActions>
                                       <SmallBtn onClick={() => onOpenSettings(s)}>Settings</SmallBtn>
                                    </TableActions>
                                 )}
                              </Td>
                           </tr>
                        ))}
                  </React.Fragment>
               );
            })}
         </tbody>
      </StyledTable>
   </TableWrapper>
);

/* ─── Router reachability badge ────────────────────────────── */
/*
 * Thin wrapper to keep callsites short. `routerStatus` is server-derived
 * from the WG handshake (~3-min freshness; REST probe only backs up dev
 * routers with no handshake data), with sub-vendos inheriting from their
 * cluster root. Independent signal from the NodeMCU heartbeat — see
 * attachRouterStatus in server/utils/cluster.ts.
 */
const OnlineBadge = ({
   status,
   compact = false,
}: {
   status: 'connected' | 'agent-down' | 'disconnected' | 'unprovisioned';
   compact?: boolean;
}) => <RouterPill status={status} compact={compact} />;

/* ─── Compact Device Card (used on Overview) ──────────────── */
/*  Matches the Devices-tab main card 1:1 — same LicenseBanner,
    same DeviceDetailRow with action buttons. Sub count + expand
    toggle render alongside the actions; clicking the subs pill opens
    a per-card sub list rendered by the parent. */

interface CompactDeviceCardProps {
   device: VendoDevice;
   subs: VendoDevice[];
   isOpen: boolean;
   onToggleExpand: (id: string) => void;
   onOpenSettings: (d: VendoDevice) => void;
   onOpenAddSub: (parent: VendoDevice) => void;
   onOpenActivate: (d: VendoDevice) => void;
   onManageLicense?: (d: VendoDevice) => void;
   onRefresh: (d: VendoDevice) => void;
   refreshingId: string | null;
   onResetStats: (d: VendoDevice) => void;
   onOpenDelete: (d: VendoDevice) => void;
}

export const CompactDeviceCard = ({
   device: d,
   subs,
   isOpen,
   onToggleExpand,
   onOpenSettings,
   onOpenActivate,
   onManageLicense,
   onResetStats,
   onOpenDelete,
}: CompactDeviceCardProps) => {
   // Own money only (see CardView): the sub-vendo rows below carry theirs.
   const totalToday = todayTotal(d);
   const totalActive = d.activeSessions + subs.reduce((sum, s) => sum + s.activeSessions, 0);
   const totalRevenue = ownRevenue(d);
   // 🔴 The Overview ("Device Status") is where operators actually look, and
   // this card had no System Info at all — the flip only went on the Devices-tab
   // card. Carlo (CRT NETWORK, 2026-09-17) photographed his RB4011 card on the
   // Overview: prod already held a fresh reading, the served bundle had the
   // button, and the card still showed nothing because it was THIS one. Same
   // DeviceCardFlip + SystemInfoFace as the Devices tab, so the two cannot
   // drift apart again.
   return (
      <DeviceCardFlip
         $online={d.status === 'online'}
         back={(unflip, active) => (
            <SystemInfoFace
               deviceId={d.id}
               sys={d.sys}
               diag={d.diag}
               lastSeenAt={d.routerLastSeenAt}
               platform={d.platform}
               onBack={unflip}
               active={active}
            />
         )}
      >
         {(flip) => (
            <>
         {d.isCurrentOwner && d.markedStolen && (
            <OwnershipLockBanner $stolen={true}>
               🚫 Marked lost/stolen — portal is locked. Remove the flag in
               Settings to re-enable.
            </OwnershipLockBanner>
         )}
         {d.isCurrentOwner &&
            !isLicensed(d.licenseStatus) &&
            !d.markedStolen && (
            <LicenseBanner $expired={d.licenseStatus === 'expired'}>
               <LicenseBannerText>
                  {d.licenseStatus === 'expired'
                     ? '⚠ License expired — portal is locked'
                     : d.licenseStatus === 'trial'
                     ? `⏱ Trial · ${d.trialDaysLeft ?? 0} day${d.trialDaysLeft === 1 ? '' : 's'} left`
                     : '🔒 License not activated — portal is locked'}
               </LicenseBannerText>
               <LicenseBannerBtn onClick={() => onOpenActivate(d)}>
                  {d.licenseStatus === 'expired' ? 'Renew Now' : 'Activate'}
               </LicenseBannerBtn>
            </LicenseBanner>
         )}
         <DeviceCardHeader>
            <DeviceHeaderLeft>
               <DeviceName>{d.name}</DeviceName>
               <TypePill $main>Main</TypePill>
               <ModelBadgeFor d={d} />
               <VariantBadge $hybrid={runningModeLabel(d) === 'Hybrid'}>
                  {runningModeLabel(d)}
               </VariantBadge>
               {!d.isCurrentOwner && (
                  <SoldPill
                     title={
                        d.soldTransfer?.toEmail
                           ? `Sold to ${d.soldTransfer.toEmail}`
                           : 'Sold'
                     }
                  >
                     SOLD
                  </SoldPill>
               )}
               {isLicensed(d.licenseStatus) && d.isCurrentOwner && (
                  <ActiveLicensePill
                     title="Manage license — reclaim or move"
                     style={{ cursor: 'pointer' }}
                     onClick={() => onManageLicense?.(d)}
                  >
                     ✓ Active
                  </ActiveLicensePill>
               )}
            </DeviceHeaderLeft>
            <CardHeaderRight>
               {(d.updatesAvailable?.count ?? 0) > 0 &&
                  d.routerStatus === 'connected' && (
                  <UpdateCountBadge
                     title={[
                        d.updatesAvailable?.router
                           ? `Router update available: ${d.updatesAvailable.router}`
                           : null,
                        d.updatesAvailable?.coinBox
                           ? `NodeMCU update available: ${d.updatesAvailable.coinBox}`
                           : null,
                     ]
                        .filter(Boolean)
                        .join('\n')}
                  >
                     {d.updatesAvailable?.count}
                  </UpdateCountBadge>
               )}
               <RouterPill status={d.routerStatus} />               <DeviceStatusBadge
                  status={d.status}
                  routerStatus={d.routerStatus}
               />
               {/* Gear opens on a SOLD vendo too — see the main card above. */}
               <SettingsIconBtn
                  label={d.name}
                  onClick={() => onOpenSettings(d)}
               />
               {d.isCurrentOwner && (
                  <DeviceKebab
                     id={d._id}
                     onMoveLicense={
                        isLicensed(d.licenseStatus) && d.isCurrentOwner
                           ? () => onManageLicense?.(d)
                           : undefined
                     }
                     onPreviewPortal={() => openPortal(d.portalSlug)}
                     onResetStats={() => onResetStats(d)}
                     onDelete={() => onOpenDelete(d)}
                  />
               )}
            </CardHeaderRight>
         </DeviceCardHeader>
         <MetaLine>
            <MetaChip>{d.id}</MetaChip>
            <MetaDot>·</MetaDot>
            <MetaChip>{d.lastHeartbeat}</MetaChip>
            {d.createdAt && (
               <>
                  <MetaDot>·</MetaDot>
                  <MetaChip title={`Registered ${new Date(d.createdAt).toLocaleString('en-PH')}`}>
                     Registered {formatRelativeDate(d.createdAt)}
                  </MetaChip>
               </>
            )}
            {hasVer(d.routerFirmware) && (
               <VerChip
                  title={
                     effectiveRouterVersion(d.routerFirmware, d.routerBundle).detail ??
                     'Router firmware / config version'
                  }
               >
                  Router {effectiveRouterVersion(d.routerFirmware, d.routerBundle).shown}
               </VerChip>
            )}
            {hasVer(d.firmware) && (
               <VerChip title="NodeMCU firmware version">
                  NodeMCU {fwBase(d.firmware)}
               </VerChip>
            )}
         </MetaLine>
         <DeviceStatsRow>
            <DeviceStat>
               <DeviceStatLabel>Total Today</DeviceStatLabel>
               {d.isCurrentOwner ? (
                  <DeviceStatValue>₱{money(totalToday)}</DeviceStatValue>
               ) : (
                  <ActivityBadge $active={totalToday > 0}>
                     <ActivityDot $active={totalToday > 0} />
                     {totalToday > 0 ? 'Earning' : 'Quiet'}
                  </ActivityBadge>
               )}
            </DeviceStat>
            <DeviceStat>
               <DeviceStatLabel>Revenue</DeviceStatLabel>
               {d.isCurrentOwner ? (
                  <DeviceStatValue>
                     ₱{money(totalRevenue)}
                  </DeviceStatValue>
               ) : (
                  <Redacted>—</Redacted>
               )}
            </DeviceStat>
            <DeviceStat>
               <DeviceStatLabel>Active</DeviceStatLabel>
               {d.isCurrentOwner ? (
                  <DeviceStatValue>{totalActive}</DeviceStatValue>
               ) : (
                  <ActivityBadge $active={totalActive > 0}>
                     <ActivityDot $active={totalActive > 0} />
                     {totalActive > 0 ? 'In use' : 'Idle'}
                  </ActivityBadge>
               )}
            </DeviceStat>
         </DeviceStatsRow>
         <DeviceDetailRow>
            <SubsPill onClick={() => onToggleExpand(d.id)} $open={isOpen}>
               {subs.length} Sub-Vendo{subs.length === 1 ? '' : 's'}
               <SubsArrow $open={isOpen}>▾</SubsArrow>
            </SubsPill>
            {/* Shown on every router, like the Devices tab: the face behind
                explains itself when a router cannot report. */}
            <SubsPill
               as="button"
               onClick={flip}
               $open={false}
               title="What this router says about its own condition"
            >
               System Info ›
            </SubsPill>
         </DeviceDetailRow>
            </>
         )}
      </DeviceCardFlip>
   );
};

/* ─── Sessions Tab ─────────────────────────────────────────── */

/** Hybrid (local-first) MikroTik mains whose router we cannot reach right now. */
export const hybridMikrotiksOutOfContact = (
   devices: Array<{
      name: string;
      parent?: unknown;
      routerStatus?: string;
      router?: { platform?: string; variant?: string; localMode?: boolean };
   }>
): string[] =>
   devices
      .filter(
         (d) =>
            !d.parent &&
            d.router?.variant === 'hybrid' &&
            d.router?.platform !== 'openwrt' &&
            !!d.routerStatus &&
            d.routerStatus !== 'connected'
      )
      .map((d) => d.name);

const OutOfContactNote = styled.div`
   margin: 0 0 14px;
   padding: 12px 14px;
   border-radius: 10px;
   background: #fff4e5;
   border: 1px solid #f5b971;
   color: #7a4500;
   font-size: 14px;
   line-height: 1.5;
`;

export const SessionsTab = () => {
   const initialFilter = useMemo(() => loadSessionsFilter(), []);
   const [statusFilter, setStatusFilter] = useState<string>(initialFilter.statusFilter);
   const [deviceFilter, setDeviceFilter] = useState<string>(initialFilter.deviceFilter);
   const [macSearch, setMacSearch] = useState('');
   // Normalize to lowercase hex so "1a58" matches "12:0c:c9:c5:1a:58".
   const macNeedle = macSearch.toLowerCase().replace(/[^0-9a-z]/g, '');

   useEffect(() => {
      try {
         localStorage.setItem(
            SESSIONS_FILTER_STORAGE_KEY,
            JSON.stringify({ statusFilter, deviceFilter })
         );
      } catch {}
   }, [statusFilter, deviceFilter]);
   const { handleShow, handleClose } = useUIStore();

   const apiDevices = useVendoDevicesStore((s) => s.devices);
   const apiSessions = useVendoSessionsStore((s) => s.sessions);
   const historyRows = useVendoSessionsStore((s) => s.historyRows);
   const historyTotal = useVendoSessionsStore((s) => s.historyTotal);
   const setHistory = useVendoSessionsStore((s) => s.setHistory);
   const dataUsageBySessionId = useVendoSessionsStore((s) => s.dataUsageBySessionId);
   const timeUsageBySessionId = useVendoSessionsStore((s) => s.timeUsageBySessionId);
   const pauseWindowBySessionId = useVendoSessionsStore((s) => s.pauseWindowBySessionId);
   const sessionsFetched = useVendoSessionsStore((s) => s.fetched);
   const sessionsLoading = useVendoSessionsStore((s) => s.loading);

   const deviceNameById = useMemo(() => {
      const map = new Map<string, string>();
      apiDevices.forEach((d) => map.set(d._id, d.name));
      return map;
   }, [apiDevices]);

   const sessions = useMemo(
      () => apiSessions.map((s) => mapApiSessionToDisplay(s, deviceNameById)),
      [apiSessions, deviceNameById]
   );
   const historySessions = useMemo(
      () => historyRows.map((s) => mapApiSessionToDisplay(s, deviceNameById)),
      [historyRows, deviceNameById]
   );

   // A Hybrid MikroTik keeps its customers' time on the router and tells us
   // about it when it can. While we cannot reach it, the times on this screen
   // are the last ones it reported, and it may be counting (or have stopped
   // counting, if its internet is down) without us. Say so instead of showing
   // those rows as if they were live (Ray, bench 2026-10-02).
   const outOfContact = useMemo(() => hybridMikrotiksOutOfContact(apiDevices), [apiDevices]);

   // Sub-vendo hierarchy isn't in the schema yet, so every device is a "main"
   // with no subs. Structure kept so UI stays consistent for when subs land.
   const deviceTree = useMemo(
      () =>
         apiDevices.map((d) => ({
            _id: d._id,
            name: d.name,
            type: 'main' as DeviceType,
            subs: [] as string[],
         })),
      [apiDevices]
   );

   const parentOfSub: Record<string, string> = {};

   const matchesDeviceFilter = (sessionDevice: string) => {
      if (deviceFilter === 'all') return true;
      if (sessionDevice === deviceFilter) return true;
      if (parentOfSub[sessionDevice] === deviceFilter) return true;
      return false;
   };

   const filtered = sessions.filter((s) => {
      // 'unlimited' = the free/whitelist rows; 'active_paused' = either live
      // state in one tab; otherwise match the status literally.
      if (statusFilter === 'unlimited') {
         if (!s.isWhitelist) return false;
      } else if (statusFilter === 'active_paused') {
         if (s.status !== 'active' && s.status !== 'paused') return false;
      } else if (statusFilter !== 'all' && s.status !== statusFilter) {
         return false;
      }
      if (!matchesDeviceFilter(s.device)) return false;
      if (macNeedle && !s.mac.toLowerCase().replace(/[^0-9a-z]/g, '').includes(macNeedle))
         return false;
      return true;
   });

   // 15 per page. Live sessions (filtered above) come first; finished ones
   // follow them, and only the rows on THIS page are fetched — filtered,
   // paged and counted on the server, and only for "All" and "Expired".
   // Reset to page 1 when the filters change or the pages shrink.
   const SESSIONS_PER_PAGE = 15;
   const [sessPage, setSessPage] = useState(1);
   const wantsHistory = statusFilter === 'all' || statusFilter === 'expired';
   const totalCount = filtered.length + (wantsHistory ? historyTotal : 0);
   const sessPageCount = Math.max(1, Math.ceil(totalCount / SESSIONS_PER_PAGE));
   useEffect(() => {
      if (sessPage > sessPageCount) setSessPage(1);
   }, [sessPage, sessPageCount]);
   useEffect(() => {
      setSessPage(1);
   }, [statusFilter, deviceFilter, macNeedle]);
   const pageStart = (sessPage - 1) * SESSIONS_PER_PAGE;
   const liveOnPage = filtered.slice(pageStart, pageStart + SESSIONS_PER_PAGE);
   const historySkip = Math.max(0, pageStart - filtered.length);
   const historyLimit = SESSIONS_PER_PAGE - liveOnPage.length;
   // The device picker holds a vendo NAME; the server filters by id.
   const historyVendo =
      deviceFilter === 'all'
         ? undefined
         : apiDevices.filter((d) => d.name === deviceFilter).map((d) => d._id).join(',') ||
           '000000000000000000000000'; // a saved name no vendo has any more: match nothing
   useEffect(() => {
      setHistory(
         wantsHistory
            ? { skip: historySkip, limit: historyLimit, vendo: historyVendo, mac: macNeedle || undefined }
            : null
      );
   }, [wantsHistory, historySkip, historyLimit, historyVendo, macNeedle, setHistory]);
   // Leaving the screen stops the finished-sessions fetch on the 10 s poll.
   useEffect(() => () => setHistory(null), [setHistory]);
   const pagedSessions =
      wantsHistory && historyLimit > 0
         ? [...liveOnPage, ...historySessions.slice(0, historyLimit)]
         : liveOnPage;

   const [kickingId, setKickingId] = useState<string | null>(null);
   const refetchSessions = useVendoSessionsStore((s) => s.fetch);

   const handleSetPauseLeft = async (id: string, minutesLeft: number | null) => {
      try {
         await setSessionPauseLeftApi(id, minutesLeft);
         toast.success(minutesLeft === null ? 'Paused time is now kept for ever' : 'Pause left updated');
         await useVendoSessionsStore.getState().fetch();
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Could not save';
         toast.error(msg);
      }
   };

   const handleSetNote = async (id: string, note: string) => {
      try {
         // An Unlimited (whitelist) row's note lives on the phone's MAC, not a
         // session — write it per-phone so it shows on every vendo that phone
         // is Unlimited on.
         if (isWhitelistRowId(id)) {
            const parsed = parseWhitelistRowId(id);
            if (!parsed) return;
            await setUnlimitedNoteApi(parsed.mac, note);
         } else {
            await setSessionNoteApi(id, note);
         }
         await useVendoSessionsStore.getState().fetch();
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Could not save the note';
         toast.error(msg);
      }
   };

   const handleKick = (id: string) => {
      if (kickingId) return;

      // Whitelist rows are virtual — their Kick removes the MAC from the
      // device whitelist (free internet ends), not a session kick.
      if (isWhitelistRowId(id)) {
         const parsed = parseWhitelistRowId(id);
         if (!parsed) return;
         // The row now stands for the phone, not one vendo, so Kick means
         // "free nowhere". Name the count in the prompt — removing free
         // internet from 16 shops with an unqualified "Remove?" would be a
         // nasty surprise.
         const row = sessions.find((s) => s.id === id);
         const count = row?.whitelistDeviceIds.length ?? 1;
         handleShow({
            template: 'verification',
            title: 'Remove Unlimited',
            windowType: 'desktop',
            data: {
               data: { id },
               msg:
                  count > 1
                     ? `Remove ${parsed.mac} from Unlimited on all ${count} vendos? It loses its free internet immediately everywhere.`
                     : `Remove ${parsed.mac} from Unlimited? This device loses its free internet immediately.`,
            },
            actions: {
               onConfirm: async () => {
                  setKickingId(id);
                  try {
                     await setWhitelistScope(parsed.mac, []);
                     toast.success(`${parsed.mac} is no longer unlimited`, {
                        autoClose: 2000,
                     });
                     await refetchSessions();
                     void useVendoDevicesStore.getState().fetch();
                  } catch (err: unknown) {
                     const msg =
                        (err as { response?: { data?: { error?: string } } })
                           ?.response?.data?.error ||
                        (err as Error)?.message ||
                        'Failed to remove Unlimited';
                     toast.error(msg);
                  } finally {
                     handleClose();
                     setKickingId(null);
                  }
               },
            },
         });
         return;
      }

      const target = sessions.find((s) => s.id === id);
      if (!target) return;

      handleShow({
         template: 'verification',
         title: 'Kick Session',
         windowType: 'desktop',
         data: {
            data: { id },
            msg: `Kick session for ${target.mac}? This ends their Wi-Fi immediately.`,
         },
         actions: {
            onConfirm: async () => {
               setKickingId(id);
               try {
                  await apiKickSession(id);
                  toast.success(`Kicked ${target.mac}`, { autoClose: 2000 });
                  await refetchSessions();
               } catch (err: unknown) {
                  const msg =
                     (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                     (err as Error)?.message ||
                     'Failed to kick session';
                  toast.error(msg);
               } finally {
                  handleClose();
                  setKickingId(null);
               }
            },
         },
      });
   };

   const handlePause = (id: string) =>
      void runSessionControl('pause', id, {
         notify: { success: (m) => toast.success(m, { autoClose: 2000 }), error: (m) => toast.error(m) },
         refetch: refetchSessions,
      });
   const handleResume = (id: string) =>
      void runSessionControl('resume', id, {
         notify: { success: (m) => toast.success(m, { autoClose: 2000 }), error: (m) => toast.error(m) },
         refetch: refetchSessions,
      });

   // ── Awaiting devices: connected to WiFi (have a DHCP lease) but no
   // live session and not whitelisted — e.g. a smart TV that can't run
   // the captive flow. The owner grants time / whitelists them by hand.
   const awaitingEnabled = statusFilter === 'awaiting';

   // Only devices whose router we can actually read leases from.
   const reachableDevices = useMemo(
      () =>
         apiDevices.filter(
            (d) =>
               d.routerStatus === 'connected' ||
               (d.routerStatus == null && d.status === 'online')
         ),
      [apiDevices]
   );

   const { data: awaitingLeasesData, isFetching: awaitingLoading } = useQuery({
      queryKey: [
         'vendo-awaiting-leases',
         reachableDevices.map((d) => d._id).sort().join(','),
      ],
      enabled: awaitingEnabled,
      refetchOnWindowFocus: false,
      queryFn: async (): Promise<DeviceLeases[]> =>
         Promise.all(
            reachableDevices.map(async (d) => {
               try {
                  const { leases } = await listDhcpLeases(d._id);
                  return { deviceId: d._id, deviceName: d.name, leases };
               } catch {
                  // Offline / unreachable router → no awaiting devices for it.
                  return { deviceId: d._id, deviceName: d.name, leases: [] };
               }
            })
         ),
   });

   // Live (active/paused) session MACs and whitelisted MACs to subtract,
   // keyed by `${vendoDeviceId}|${MAC}` so a MAC reused across devices is
   // matched per-device.
   const liveKeys = useMemo(() => {
      const s = new Set<string>();
      for (const sess of sessions) {
         if ((sess.status === 'active' || sess.status === 'paused') && sess.deviceId) {
            s.add(`${sess.deviceId}|${normalizeMac(sess.mac)}`);
         }
      }
      return s;
   }, [sessions]);

   const whitelistKeys = useMemo(() => {
      const s = new Set<string>();
      for (const d of apiDevices) {
         for (const w of d.whitelistedMacs ?? []) {
            s.add(`${d._id}|${normalizeMac(w.mac)}`);
         }
      }
      return s;
   }, [apiDevices]);

   // Known infrastructure MACs to hide — the NodeMCU per device.
   const infraKeys = useMemo(() => {
      const s = new Set<string>();
      for (const d of apiDevices) {
         if (d.nodeMcu?.mac) s.add(`${d._id}|${normalizeMac(d.nodeMcu.mac)}`);
      }
      return s;
   }, [apiDevices]);

   const awaitingDevices = useMemo(() => {
      // The router reports every VLAN's phones under its main vendo; show the
      // sub-vendo each one is really on. deviceId stays the main: Give Internet
      // resolves the VLAN from the phone's address.
      const rows = computeAwaitingDevices(awaitingLeasesData ?? [], liveKeys, whitelistKeys, infraKeys);
      return rows
         .map((a) => {
            const main = apiDevices.find((d) => d._id === a.deviceId);
            const vendo = main
               ? vendoNameForLease(
                    a.ip,
                    { name: main.name, vlans: main.router?.vlans },
                    apiDevices.filter((d) => d.parent === main._id)
                 )
               : a.deviceName;
            return { ...a, deviceName: vendo, mainName: a.deviceName };
         })
         .filter(
            (a) => deviceFilter === 'all' || a.deviceName === deviceFilter || a.mainName === deviceFilter
         );
   }, [awaitingLeasesData, liveKeys, whitelistKeys, infraKeys, deviceFilter, apiDevices]);

   // Phone names for the Sessions list. Sessions are saved with a blank name, so
   // read it from the routers of the rows on THIS page only: a big owner must not
   // call every router just to open the list. Subs report under their main.
   const nameRouterIds = useMemo(() => {
      const ids = new Set<string>();
      for (const s of pagedSessions) {
         if (s.hostname || !s.deviceId) continue;
         const dev = apiDevices.find((d) => d._id === s.deviceId);
         const mainId = dev?.parent || dev?._id;
         if (mainId && reachableDevices.some((d) => d._id === mainId)) ids.add(mainId);
      }
      return [...ids].sort();
   }, [pagedSessions, apiDevices, reachableDevices]);
   const { data: nameLeases } = useQuery({
      queryKey: ['vendo-session-phone-names', nameRouterIds.join(',')],
      enabled: !awaitingEnabled && nameRouterIds.length > 0,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      queryFn: async (): Promise<DeviceLeases[]> =>
         Promise.all(
            nameRouterIds.map(async (id) => {
               try {
                  const { leases } = await listDhcpLeases(id);
                  return { deviceId: id, deviceName: '', leases };
               } catch {
                  return { deviceId: id, deviceName: '', leases: [] };
               }
            })
         ),
   });
   const phoneNames = useMemo(() => phoneNamesFromLeases(nameLeases ?? []), [nameLeases]);

   // Opens the same Manage Access modal as a live session, but with an
   // empty session id → "Add time" grants a fresh operator session.
   const handleManageAwaiting = (a: AwaitingDevice) => {
      handleShow({
         template: 'manage-access',
         title: 'Give Internet',
         windowType: 'desktop',
         data: {
            session: {
               id: '',
               mac: a.mac,
               deviceId: a.deviceId,
               deviceName: a.deviceName,
               ip: a.ip,
            },
         },
      });
   };

   // "+Time" opens the Manage Access modal: extend the session by a chosen
   // amount, OR grant the device free permanent internet (whitelist).
   const handleAddTime = (id: string) => {
      const target = sessions.find((s) => s.id === id);
      if (!target) return;
      // An Unlimited row has no time to extend — the same modal opens on the
      // Permanent tab instead, pre-ticked with the vendos it is free on today
      // so the operator edits reality rather than re-picking from scratch.
      handleShow({
         template: 'manage-access',
         title: target.isWhitelist ? 'Edit Unlimited' : 'Manage Access',
         windowType: 'desktop',
         data: {
            session: {
               id: target.isWhitelist ? '' : target.id,
               mac: target.mac,
               deviceId: target.deviceId,
               deviceName: target.device,
               // Carry the phone's IP through, the same way the Awaiting tab
               // does. Without it a session created from here has no IP: only
               // cosmetic on OpenWRT, where access is granted by address, but a
               // MikroTik binding genuinely wants it. '—' is the table's empty
               // marker, not an address.
               ...(target.ip && target.ip !== '—' ? { ip: target.ip } : {}),
               ...(target.isWhitelist
                  ? { whitelistDeviceIds: target.whitelistDeviceIds }
                  : {}),
               isDataSession: target.isDataSession,
               liveDataSession: target.isDataSession && (target.status === 'active' || target.status === 'paused'),
            },
            ...(target.isWhitelist ? { initialMode: 'permanent' as const } : {}),
         },
      });
   };

   return (
      <TabContent>
         {outOfContact.length > 0 && (
            <OutOfContactNote role="status" data-testid="hybrid-out-of-contact">
               <strong>{outOfContact.join(', ')}</strong>{' '}
               {outOfContact.length === 1 ? 'is' : 'are'} out of contact right now. The vendo
               keeps its customers&apos; time by itself; the times shown here are the last it
               reported and will update when it is back online.
            </OutOfContactNote>
         )}
         <SectionRow>
            <SectionHeader>Active Sessions</SectionHeader>
            <FilterBar>
               <MacSearchInput
                  value={macSearch}
                  placeholder="Search MAC…"
                  onChange={(e) => setMacSearch(e.target.value)}
               />
               <DeviceSelect
                  value={deviceFilter}
                  onChange={(e) => setDeviceFilter(e.target.value)}
               >
                  <option value="all">All devices</option>
                  {deviceTree.map((main) => (
                     <React.Fragment key={main._id}>
                        <option value={main.name}>
                           {main.name} {main.subs.length > 0 ? `(+ ${main.subs.length} sub${main.subs.length === 1 ? '' : 's'})` : ''}
                        </option>
                        {main.subs.map((subName, idx) => (
                           <option key={`${main._id}-${idx}-${subName}`} value={subName}>
                              {'    ↳ '}{subName}
                           </option>
                        ))}
                     </React.Fragment>
                  ))}
               </DeviceSelect>
               {['all', 'active', 'paused', 'active_paused', 'unlimited', 'expired', 'awaiting'].map((f) => (
                  <FilterChip key={f} $active={statusFilter === f} onClick={() => setStatusFilter(f)}>
                     {FILTER_LABELS[f] ?? f.charAt(0).toUpperCase() + f.slice(1)}
                  </FilterChip>
               ))}
            </FilterBar>
         </SectionRow>
         {deviceFilter !== 'all' &&
            parentOfSub[deviceFilter] === undefined &&
            deviceTree.find((m) => m.name === deviceFilter)?.subs.length ? (
            <FilterNote>
               Showing sessions from <strong>{deviceFilter}</strong> and its sub-vendos.
            </FilterNote>
         ) : null}
         {awaitingEnabled ? (
            awaitingLoading && !awaitingLeasesData ? (
               <EmptyNote>Checking which devices are connected…</EmptyNote>
            ) : reachableDevices.length === 0 ? (
               <EmptyNote>
                  No online router to check. A device must be online before we can
                  see what’s connected to it.
               </EmptyNote>
            ) : awaitingDevices.length === 0 ? (
               <EmptyNote>
                  No devices waiting. When something joins the WiFi but hasn’t paid
                  (e.g. a TV), it shows up here so you can give it time or whitelist it.
               </EmptyNote>
            ) : (
               <AwaitingTable devices={awaitingDevices} onManage={handleManageAwaiting} />
            )
         ) : !sessionsFetched && sessionsLoading ? (
            <TableSkeleton cols={8} />
         ) : totalCount === 0 ? (
            <EmptyNote>
               {/* Only "All" with no device or MAC filter counts every
                   session; any other view is a filter that matched nothing. */}
               {statusFilter === 'all' && deviceFilter === 'all' && !macNeedle
                  ? 'No sessions yet. When a customer logs into a vendo device, it will appear here.'
                  : 'No sessions match the current filters.'}
            </EmptyNote>
         ) : (
            <>
               <SessionTable
                  sessions={pagedSessions}
                  phoneNames={phoneNames}
                  showActions
                  onKick={handleKick}
                  onPause={handlePause}
                  onResume={handleResume}
                  onAddTime={handleAddTime}
                  dataUsageBySessionId={dataUsageBySessionId}
                  timeUsageBySessionId={timeUsageBySessionId}
                  pauseWindowBySessionId={pauseWindowBySessionId}
                  onSetNote={handleSetNote}
                  onSetPauseLeft={handleSetPauseLeft}
               />
               <Pagination
                  page={sessPage}
                  pageCount={sessPageCount}
                  onChange={setSessPage}
                  totalCount={totalCount}
                  pageSize={SESSIONS_PER_PAGE}
               />
            </>
         )}
      </TabContent>
   );
};

/* ─── Settings Tab (owner-level defaults) ─────────────────── */

export const SettingsTab = () => (
   <TabContent>
      <InfoBox style={{ marginBottom: 16 }}>
         <strong>Account-level settings.</strong> Per-device settings (coin rate, bandwidth,
         session limits, coin slot) live on each device card under the <em>Devices</em> tab.
         Firmware updates are managed from the Admin Panel → Vendo page.
      </InfoBox>

      <SettingsGrid>
         <SettingsSection>
            <SectionHeader>Defaults for New Devices</SectionHeader>
            <SettingRow>
               <SettingLabel>Default coin rate</SettingLabel>
               <SettingValue>₱1 = 6 minutes</SettingValue>
            </SettingRow>
            <SettingRow>
               <SettingLabel>Default bandwidth</SettingLabel>
               <SettingValue>5 Mbps / 5 Mbps</SettingValue>
            </SettingRow>
            <SettingRow>
               <SettingLabel>Default max session</SettingLabel>
               <SettingValue>480 min (8 hrs)</SettingValue>
            </SettingRow>
            <SettingRow>
               <SettingLabel>Default idle timeout</SettingLabel>
               <SettingValue>10 min</SettingValue>
            </SettingRow>
            <InfoBox style={{ marginTop: 8 }}>
               Applied automatically when you register a new vendo device. Each device can
               override these values independently.
            </InfoBox>
         </SettingsSection>

         <SettingsSection>
            <SectionHeader>Notifications</SectionHeader>
            <SettingRow>
               <SettingLabel>Alert email</SettingLabel>
               <SettingValue>owner@example.com</SettingValue>
            </SettingRow>
            <SettingRow>
               <SettingLabel>Device offline alerts</SettingLabel>
               <SettingChip $on>Enabled</SettingChip>
            </SettingRow>
            <SettingRow>
               <SettingLabel>Low balance alerts</SettingLabel>
               <SettingChip $on>Enabled</SettingChip>
            </SettingRow>
            <SettingRow>
               <SettingLabel>Weekly summary email</SettingLabel>
               <SettingChip $on={false}>Disabled</SettingChip>
            </SettingRow>
         </SettingsSection>

         <SettingsSection>
            <SectionHeader>Locale</SectionHeader>
            <SettingRow>
               <SettingLabel>Timezone</SettingLabel>
               <SettingValue>Asia/Manila (UTC+8)</SettingValue>
            </SettingRow>
            <SettingRow>
               <SettingLabel>Currency</SettingLabel>
               <SettingValue>PHP (₱)</SettingValue>
            </SettingRow>
         </SettingsSection>

      </SettingsGrid>
   </TabContent>
);

/* ─── Shared sub-components ────────────────────────────────── */

interface AwaitingTableProps {
   devices: AwaitingDevice[];
   onManage: (a: AwaitingDevice) => void;
}

/**
 * Devices connected to the WiFi but with no live session and not
 * whitelisted (the Sessions → "Awaiting" tab). "Give Internet" opens the
 * Manage Access modal where the owner grants time OR whitelists — used
 * for a smart TV / console that can't run the captive flow.
 */
const AwaitingTable = ({ devices, onManage }: AwaitingTableProps) => (
   <TableWrapper>
      <StyledTable>
         <thead>
            <tr>
               <Th>Device (vendo)</Th>
               <Th>Name</Th>
               <Th>MAC Address</Th>
               <Th>IP</Th>
               <Th>Actions</Th>
            </tr>
         </thead>
         <tbody>
            {devices.map((a) => (
               <tr key={`${a.deviceId}|${a.mac}`}>
                  <Td>{a.deviceName}</Td>
                  <Td>{a.name}</Td>
                  <Td $mono>{a.mac}</Td>
                  <Td $mono>{a.ip || '—'}</Td>
                  <Td>
                     <TableActions>
                        <SmallBtn onClick={() => onManage(a)}>Give Internet</SmallBtn>
                     </TableActions>
                  </Td>
               </tr>
            ))}
         </tbody>
      </StyledTable>
   </TableWrapper>
);

interface SessionTableProps {
   sessions: DisplaySession[];
   /** MAC -> phone name from the routers. Given = the Name column shows. */
   phoneNames?: Record<string, string>;
   showActions?: boolean;
   onKick?: (id: string) => void;
   onPause?: (id: string) => void;
   onResume?: (id: string) => void;
   onAddTime?: (id: string) => void;
   // Server-provided live byte counters keyed by session id, for
   // active data-rate sessions only. Time sessions / inactive data
   // sessions render the time column unchanged.
   dataUsageBySessionId?: Record<string, { totalUsedBytes: number; capBytes: number; percentUsed: number }>;
   // Server-provided live byte counters + throughput for active TIME
   // sessions. Read from /queue/simple per MAC. Missing keys = router
   // unreachable, queue not yet created, or session is data-rate.
   /** Pause window per session in minutes; null = frozen time kept for ever. */
   pauseWindowBySessionId?: Record<string, number | null>;
   timeUsageBySessionId?: Record<string, {
      bytesIn: number;
      bytesOut: number;
      totalUsedBytes: number;
      currentDownloadBps: number;
      currentUploadBps: number;
   }>;
   /** Save an operator note on a row. Absent = the Note column is read-only. */
   onSetNote?: (id: string, note: string) => Promise<void> | void;
   /** Set how much pause budget a row has LEFT (minutes; null = for ever). */
   onSetPauseLeft?: (id: string, minutesLeft: number | null) => Promise<void> | void;
   /** What went into each combined session. Absent = read from the sessions store. */
   entriesBySessionId?: Record<string, SessionEntry[]>;
}

/** "coin ₱20", "operator 30m", "voucher 2 GB" — one entry, as a short label. */
const topUpAdded = (e: SessionEntry): string =>
   e.capMb ? formatBytes(e.capMb * 1024 * 1024) : formatHM(e.minutes);
// An online entry is named by the channel the customer paid with — a Maya
// payment labelled "online" tells the operator nothing (Ray, 2026-09-30).
const CHANNEL_NAMES: Record<string, string> = { MAYA: 'Maya', QRPH: 'QR Ph', GCASH: 'GCash' };
const topUpType = (e: SessionEntry): string => {
   if (e.type === 'gcash') return 'GCash';
   if (e.type === 'online') {
      const code = (e.channel || '').toUpperCase();
      return CHANNEL_NAMES[code] || e.channel || 'online';
   }
   return e.type;
};
const topUpLabel = (e: SessionEntry): string =>
   `${topUpType(e)} ${e.amount > 0 ? `₱${money(e.amount)}` : topUpAdded(e)}`;

export const SessionTable = ({ sessions, phoneNames, showActions, onKick, onPause, onResume, onAddTime, dataUsageBySessionId, timeUsageBySessionId, pauseWindowBySessionId, onSetNote, onSetPauseLeft, entriesBySessionId }: SessionTableProps) => {
   const storedEntries = useVendoSessionsStore((st) => st.entriesBySessionId);
   const entriesById = entriesBySessionId ?? storedEntries;
   // Which rows have their top-ups list open.
   const [openTopUps, setOpenTopUps] = useState<Set<string>>(() => new Set());
   const toggleTopUps = (id: string) =>
      setOpenTopUps((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   // A data-rate vendo's operator gives DATA, whatever the row is (an expired
   // time row on a data vendo still gets "+Data" — Ray 2026-09-15).
   // Select the stable array and derive inside useMemo — a selector that
   // filters/maps returns a NEW array every render and re-renders for ever.
   const allDevices = useVendoDevicesStore((st) => st.devices);
   const dataVendoIds = useMemo(
      () => new Set(allDevices.filter((d) => d.activeRateMode === 'data').map((d) => d._id)),
      [allDevices]
   );
   const isDataVendo = (id: string | null) => !!id && dataVendoIds.has(id);
   const vendoNameById = useMemo(
      () => new Map(allDevices.map((d) => [d._id, d.name])),
      [allDevices]
   );
   const [now, setNow] = useState(() => Date.now());
   useEffect(() => {
      const id = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(id);
   }, []);

   // Only one row per (device, mac) carries "+Time" — the live row if one
   // exists, else the newest expired row. Whitelisted MACs get none.
   const extendableIds = useMemo(
      () => extendableSessionIds(sessions),
      [sessions]
   );

   return (
   <TableWrapper>
      <StyledTable>
         <thead>
            <tr>
               <Th>MAC Address</Th>
               {phoneNames && <Th>Name</Th>}
               <Th>IP</Th>
               <Th>Method</Th>
               <Th>Amount</Th>
               <Th>Time</Th>
               <Th>Pause left</Th>
               <Th>Usage</Th>
               <Th>Status</Th>
               <Th>Device</Th>
               <Th>Date</Th>
               <Th>Note</Th>
               {showActions && <Th>Actions</Th>}
            </tr>
         </thead>
         <tbody>
            {sessions.map((s) => {
            const secLeft = computeSecondsRemaining(s, now);
            const topUps = s.isWhitelist ? [] : entriesById?.[s.id] ?? [];
            const combined = topUps.length > 1;
            const topUpsOpen = combined && openTopUps.has(s.id);
            return (
               <React.Fragment key={s.id}>
               <tr>
                  <Td $mono>{s.mac}</Td>
                  {phoneNames && (
                     <Td>{s.hostname || friendlyDeviceName(s.mac, phoneNames[normalizeMac(s.mac)] ?? '')}</Td>
                  )}
                  <Td $mono>{s.ip}</Td>
                  <Td>
                     {s.isWhitelist ? (
                        <WhitelistBadge>∞ unlimited</WhitelistBadge>
                     ) : combined ? (
                        // One customer = one session. The row shows how the
                        // session STARTED; "Show N top-ups" lists every entry,
                        // each with its own type. One chip per entry made a
                        // long session a tall row (Ray, 2026-10-05).
                        <TopUpChips>
                           {topUps.slice(0, 1).map((e, i) => (
                              <MethodBadge key={i} $method={e.type}>{topUpLabel(e)}</MethodBadge>
                           ))}
                        </TopUpChips>
                     ) : (
                        <MethodBadge $method={s.method}>{s.isPromo ? 'free promo' : s.method}</MethodBadge>
                     )}
                     {!combined && s.method === 'voucher' && s.voucherCode && (
                        <VoucherCodeTag title="Voucher code">{s.voucherCode}</VoucherCodeTag>
                     )}
                     {combined && (
                        <TopUpToggle
                           type="button"
                           aria-expanded={topUpsOpen}
                           onClick={() => toggleTopUps(s.id)}
                        >
                           {topUpsOpen ? 'Hide' : 'Show'} {topUps.length} top-ups
                        </TopUpToggle>
                     )}
                  </Td>
                  <Td>{s.isWhitelist ? '—' : `₱${money(s.amount)}`}</Td>
                  <Td>
                     {s.isWhitelist ? (
                        // Whitelist row: time = device license runway, not a
                        // purchased session. A lifetime licence has no end: 'Lifetime' (Ray 2026-10-18).
                        <TimeDisplay>
                           <TimeRemaining $low={false}>
                              {s.expiresAt
                                 ? `${Math.max(
                                      0,
                                      Math.ceil(
                                         (toLocalDeadline(s.expiresAt) - now) /
                                            86_400_000
                                      )
                                   )}d left`
                                 : 'Lifetime'}
                           </TimeRemaining>
                        </TimeDisplay>
                     ) : s.isDataSession ? (
                        // Data-rate session: show usage / cap bar instead
                        // of time. Live counters from the router land in
                        // dataUsageBySessionId; if missing (router was
                        // unreachable when listSessions ran), show just
                        // the static cap so the row isn't blank.
                        (() => {
                           const usage = dataUsageBySessionId?.[s.id];
                           const cap = (s.capMb || 0) * 1024 * 1024;
                           const used = usage?.totalUsedBytes ?? 0;
                           const pct = usage?.percentUsed ?? 0;
                           return (
                              <DataCellWrap>
                                 <DataCellTop>
                                    <DataCellUsed $low={pct >= 90 && s.status === 'active'}>
                                       {formatBytes(used)}
                                    </DataCellUsed>
                                    <DataCellCap> / {formatBytes(cap)}</DataCellCap>
                                 </DataCellTop>
                                 <DataCellBar>
                                    <DataCellBarFill $pct={pct} $low={pct >= 90} />
                                 </DataCellBar>
                              </DataCellWrap>
                           );
                        })()
                     ) : (
                        <TimeDisplay>
                           <TimeRemaining $low={secLeft < 15 * 60 && s.status === 'active'}>
                              {formatHMS(secLeft)}
                           </TimeRemaining>
                           <TimeTotal> / {formatHM(s.purchased)}</TimeTotal>
                        </TimeDisplay>
                     )}
                  </Td>
                  <Td>
                     {(() => {
                        // Unlimited is unlimited — it was never bought, so it
                        // has no pause budget to spend.
                        if (s.isWhitelist) return <ValidityMuted>—</ValidityMuted>;
                        const windowMin = pauseWindowBySessionId?.[s.id] ?? null;
                        // Banked pauses PLUS the one open right now. Reading the
                        // banked figure alone would show a customer who paused a
                        // week ago and never returned as having spent nothing.
                        const openMs = s.pausedAt
                           ? Math.max(0, now - toLocalDeadline(s.pausedAt))
                           : 0;
                        const spent = (s.pausedTotalMs || 0) + openMs;
                        // 0 / unset = frozen time is kept for ever.
                        const msLeft = windowMin ? windowMin * 60_000 - spent : null;
                        return (
                           <PauseLeftCell
                              id={s.id}
                              msLeft={msLeft}
                              windowMin={windowMin}
                              spentMin={Math.round(spent / 60_000)}
                              editable={s.status !== 'expired' && !!onSetPauseLeft}
                              onSave={onSetPauseLeft}
                           />
                        );
                     })()}
                  </Td>
                  <Td>
                     {(() => {
                        // Data session: usage is gated by capBytes (rendered
                        // as a progress bar in the Time column already), so
                        // here we just echo total used in absolute terms.
                        if (s.isDataSession) {
                           const u = dataUsageBySessionId?.[s.id];
                           return u
                              ? <UsageCell>{formatBytes(u.totalUsedBytes)}</UsageCell>
                              : <UsageMuted>—</UsageMuted>;
                        }
                        // Time session: total bytes consumed via the queue
                        // plus a tiny live throughput line so admins can spot
                        // who's hammering the upstream.
                        const u = timeUsageBySessionId?.[s.id];
                        if (!u) return <UsageMuted>—</UsageMuted>;
                        return (
                           <UsageCell>
                              {formatBytes(u.totalUsedBytes)}
                              {(u.currentDownloadBps > 0 || u.currentUploadBps > 0) && (
                                 <UsageRate>
                                    ↓{formatBps(u.currentDownloadBps)}
                                    {' '}
                                    ↑{formatBps(u.currentUploadBps)}
                                 </UsageRate>
                              )}
                           </UsageCell>
                        );
                     })()}
                  </Td>
                  <Td>
                     <StatusBadge $status={s.status}>{s.status}</StatusBadge>
                  </Td>
                  <Td>
                     {s.device}
                     {/* Carrying only means anything once a phone is free on
                         MORE than one vendo. Until it has collected its pass it
                         works where it was granted and nowhere else — and the
                         two look identical to the operator unless we say so. */}
                     {s.isWhitelist && s.whitelistDeviceIds.length > 1 && (
                        <CarriedTag $ok={s.carried}>
                           {s.carried ? 'carried' : 'not yet carried'}
                        </CarriedTag>
                     )}
                  </Td>
                  <Td $muted>{formatRelativeDate(s.createdAt)}</Td>
                  <Td>
                     {/* Unlimited rows carry a note too — keyed by the phone's
                        MAC on the server, so it follows the phone across every
                        vendo it is Unlimited on. */}
                     <SessionNoteCell id={s.id} note={s.note} onSave={onSetNote} />
                  </Td>
                  {showActions && (
                     <Td>
                        <TableActions>
                           {/* Whitelist rows: Kick is the only action —
                               it removes the MAC from the whitelist
                               (handled by the caller via the wl: id). */}
                           {/* Pause/Resume work for data sessions too (Ray
                               2026-09-15): the gate shuts, the router banks
                               the byte counter, and the data rate's Validity
                               says how long the megabytes keep. */}
                           {!s.isWhitelist && s.status === 'active' && (
                              <SmallBtn onClick={() => onPause?.(s.id)}>Pause</SmallBtn>
                           )}
                           {!s.isWhitelist && s.status === 'paused' && (
                              <SmallBtn onClick={() => onResume?.(s.id)}>Resume</SmallBtn>
                           )}
                           {/* Unlimited: Edit re-opens the vendo tick list.
                               Without it the only way to change where a phone
                               is free would be Kick and start over. */}
                           {s.isWhitelist && (
                              <SmallBtn onClick={() => onAddTime?.(s.id)}>Edit</SmallBtn>
                           )}
                           {(s.isWhitelist || s.status !== 'expired') && (
                              <SmallBtn $danger onClick={() => onKick?.(s.id)}>Kick</SmallBtn>
                           )}
                           {extendableIds.has(s.id) && (
                              <SmallBtn onClick={() => onAddTime?.(s.id)}>{isDataVendo(s.deviceId) ? '+Data' : '+Time'}</SmallBtn>
                           )}
                        </TableActions>
                     </Td>
                  )}
               </tr>
               {topUpsOpen && (
                  <tr>
                     <TopUpsCell colSpan={(showActions ? 12 : 11) + (phoneNames ? 1 : 0)}>
                        <TopUpsTitle>Top-ups on this session</TopUpsTitle>
                        <TopUpsList>
                           <thead>
                              <tr>
                                 <th>When</th>
                                 <th>Type</th>
                                 <th>Paid</th>
                                 <th>Added</th>
                                 <th>Where</th>
                                 <th>Detail</th>
                              </tr>
                           </thead>
                           <tbody>
                              {topUps.map((e, i) => (
                                 <tr key={i}>
                                    <td>{formatRelativeDate(e.at)}</td>
                                    <td><MethodBadge $method={e.type}>{topUpType(e)}</MethodBadge></td>
                                    <td>₱{money(e.amount)}</td>
                                    <td>+{topUpAdded(e)}</td>
                                    <td>{(e.vendoDevice && vendoNameById.get(e.vendoDevice)) || '—'}</td>
                                    <td>
                                       {e.voucherCode ? (
                                          <TopUpCode>{e.voucherCode}</TopUpCode>
                                       ) : e.type === 'operator' ? (
                                          'Added by you'
                                       ) : (
                                          '—'
                                       )}
                                    </td>
                                 </tr>
                              ))}
                           </tbody>
                        </TopUpsList>
                     </TopUpsCell>
                  </tr>
               )}
               </React.Fragment>
            );
            })}
            {sessions.length === 0 && (
               <tr>
                  <Td colSpan={(showActions ? 12 : 11) + (phoneNames ? 1 : 0)} style={{ textAlign: 'center', padding: '2rem', color: colors.GRAY5 }}>
                     No sessions match these filters.
                  </Td>
               </tr>
            )}
         </tbody>
      </StyledTable>
   </TableWrapper>
   );
};

/* ─── styles ───────────────────────────────────────────────── */

export const Wrapper = styled.div`
   padding: 1.5rem;
   max-width: 1200px;
   margin: 0 auto;
   @media (max-width: 768px) {
      padding: 1rem 0.75rem;
   }
`;

export const PageHeader = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   margin-bottom: 8px;
`;

export const PageTitle = styled.h1`
   font-size: 24px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const TabContent = styled.div``;

/* ─── Stats ────────────────────────────────────────────────── */

const StatsGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   gap: 12px;
   margin-bottom: 24px;
   @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
   }
`;

const cardFlash = keyframes`
   0%   { background: ${colors.WHITE}; }
   20%  { background: rgba(56, 148, 255, 0.10); }
   100% { background: ${colors.WHITE}; }
`;

const StatCard = styled.div<{ $flash?: boolean }>`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   padding: 16px;
   ${({ $flash }) => $flash && css`animation: ${cardFlash} 0.8s ease-out;`}
`;

const StatLabel = styled.p`
   font-size: 12px;
   color: ${colors.GRAY5};
   margin-bottom: 4px;
`;

const statPulse = keyframes`
   0%   { transform: scale(1);    text-shadow: 0 0 0 currentColor; letter-spacing: 0; }
   25%  { transform: scale(1.22); text-shadow: 0 0 18px currentColor; letter-spacing: 1px; }
   60%  { transform: scale(1.05); text-shadow: 0 0 8px currentColor; letter-spacing: 0.2px; }
   100% { transform: scale(1);    text-shadow: 0 0 0 currentColor; letter-spacing: 0; }
`;

const StatValue = styled.p<{ $color: string; $flash?: boolean }>`
   font-size: 22px;
   font-weight: 700;
   color: ${({ $color }) => $color};
   transform-origin: left center;
   font-variant-numeric: tabular-nums;
   ${({ $flash }) => $flash && css`animation: ${statPulse} 0.8s ease-out;`}
`;

/* ─── Section ──────────────────────────────────────────────── */

const SectionHeader = styled.h3`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin-bottom: 12px;
`;

const RecentSessionsBlock = styled.div`
   margin-bottom: 24px;
`;






const SectionRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-bottom: 12px;
   flex-wrap: wrap;
   gap: 8px;
   h3 { margin-bottom: 0; }
`;

const FocusBar = styled.div`
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 10px;
   margin-bottom: 14px;
`;
const FocusLabel = styled.span`
   font-size: 12px;
   font-weight: 700;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   color: ${colors.GRAY5};
`;
const FocusSelect = styled.select`
   background: ${colors.WHITE};
   color: ${colors.MAIN_DARK};
   border: 1px solid ${colors.GRAY12};
   padding: 7px 28px 7px 10px;
   border-radius: 8px;
   font-size: 13px;
   font-weight: 600;
   max-width: 260px;
   cursor: pointer;
`;
const FocusNote = styled.span`
   font-size: 12px;
   color: ${colors.GRAY3};
   strong {
      color: ${colors.MAIN_DARK};
   }
`;
const FocusClear = styled.button`
   background: transparent;
   border: none;
   color: ${colors.PRIMARY};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   padding: 0;
`;

/** Row 1 of the device list toolbar: Cards / List on the left, the Register
 *  button on the right. */
const ToolbarTop = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   margin-bottom: 10px;
   button {
      white-space: nowrap;
   }
   @media (max-width: 768px) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
   }
`;

const HeaderActions = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   /* Seven controls do not fit one line below ~1400px. They wrap onto a
      second line, right-aligned, instead of squeezing: squeezed, "Show
      sub-vendos" broke over three lines and "+ Register Device" ran off the
      edge (Ray, 2026-09-30). */
   flex-wrap: wrap;
   justify-content: flex-start;
   width: 100%;
   margin-bottom: 14px;
   > * {
      flex-shrink: 0;
   }
   button,
   label {
      white-space: nowrap;
   }

   /* Mobile: stack into a 2x2 grid so all four controls fit without
      overflowing the viewport. SortSelect + ViewToggle on row 1
      (compact controls), Show-subs checkbox + Register button on
      row 2 (the most-used action gets visual prominence on the
      right). gap drops to 6px because the buttons themselves are
      smaller. */
   @media (max-width: 768px) {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
   }
`;

/* Credits widget */

const CreditsBar = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding: 12px 16px;
   margin-bottom: 16px;
   background: linear-gradient(135deg, ${colors.PRIMARY}10, ${colors.PRIMARY}05);
   border: 1px solid ${colors.PRIMARY}30;
   border-radius: 12px;
   flex-wrap: wrap;
`;

const CreditsLeft = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   flex: 1;
   min-width: 200px;
`;

const CreditsIcon = styled.span`
   font-size: 28px;
`;

const CreditsBody = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

const CreditsLabel = styled.p`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const CreditsValue = styled.div`
   display: flex;
   align-items: baseline;
   gap: 4px;
`;

const CreditsBigNum = styled.span`
   font-size: 22px;
   font-weight: 800;
   color: ${colors.PRIMARY};
   line-height: 1;
`;

const CreditsUnit = styled.span`
   font-size: 13px;
   color: ${colors.GRAY3};
`;

const CreditsUsed = styled.span`
   font-size: 13px;
   color: ${colors.GRAY3};
   margin-left: 4px;
`;

const CreditsHint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const CreditsActions = styled.div`
   display: flex;
   gap: 8px;
`;

const ViewToggle = styled.div`
   display: flex;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   overflow: hidden;
   @media (max-width: 768px) {
      width: 100%;
   }
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   color: ${({ $active }) => ($active ? 'white' : colors.GRAY3)};
   border: none;
   padding: 6px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   transition: all 0.15s;
   &:hover {
      background: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY14)};
   }
   @media (max-width: 768px) {
      flex: 1;
   }
`;

const ActionBtn = styled.button`
   background: ${colors.PRIMARY};
   color: white;
   border: none;
   padding: 8px 16px;
   border-radius: 8px;
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;
   &:hover { opacity: 0.9; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
   @media (max-width: 768px) {
      width: 100%;
      padding: 10px 12px;
   }
`;

const DeviceSearchInput = styled.input`
   background: ${colors.WHITE};
   color: ${colors.MAIN_DARK};
   border: 1px solid ${colors.GRAY12};
   padding: 7px 10px;
   border-radius: 8px;
   font-size: 13px;
   min-width: 180px;
   /* Takes whatever the filters leave, so the row ends at the right edge
      instead of in empty space (Ray, 2026-09-30). Doubled selector: it has to
      win over the row's own "children do not shrink" rule. */
   && {
      flex: 1 1 200px;
   }
   @media (max-width: 768px) {
      /* The phone layout is a two-column grid; the search box takes a full row. */
      grid-column: 1 / -1;
      min-width: 0;
      width: 100%;
   }
   &::placeholder {
      color: ${colors.GRAY5};
   }
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const SortSelect = styled.select`
   background: ${colors.WHITE};
   color: ${colors.MAIN_DARK};
   border: 1px solid ${colors.GRAY12};
   padding: 7px 28px 7px 10px;
   border-radius: 8px;
   font-size: 13px;
   font-weight: 500;
   cursor: pointer;
   appearance: none;
   background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%23666' d='M5 6 0 0h10z'/%3e%3c/svg%3e");
   background-repeat: no-repeat;
   background-position: right 10px center;
   &:focus { outline: none; border-color: ${colors.PRIMARY}; }
   @media (max-width: 768px) {
      width: 100%;
   }
`;

const SubsToggleLabel = styled.label`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   font-size: 12px;
   font-weight: 500;
   color: ${colors.GRAY3};
   cursor: pointer;
   user-select: none;
   input { cursor: pointer; }
   @media (max-width: 768px) {
      padding: 8px 10px;
      border: 1px solid ${colors.GRAY12};
      border-radius: 8px;
      justify-content: center;
      background: ${colors.WHITE};
   }
`;

const PortalLink = styled.a`
   color: ${colors.PRIMARY};
   text-decoration: none;
   font-size: 12px;
   font-weight: 600;
   &:hover { text-decoration: underline; }
`;

const InfoBox = styled.div`
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 12px 14px;
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.6;
`;

const EmptyNote = styled.div`
   background: ${colors.GRAY14};
   border: 1px dashed ${colors.GRAY12};
   border-radius: 8px;
   padding: 24px 16px;
   font-size: 13px;
   color: ${colors.GRAY3};
   text-align: center;
   line-height: 1.6;
`;

/* ─── Devices ──────────────────────────────────────────────── */

const DeviceCardGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 12px;
   margin-bottom: 24px;
   @media (max-width: 768px) {
      grid-template-columns: 1fr;
   }
`;

const MainCardGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 12px;
   align-items: start;
   @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
   }
   @media (max-width: 768px) {
      grid-template-columns: 1fr;
   }
`;

const MainColumn = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   min-width: 0;
`;

const CardHeaderRight = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   /* Wrap the status pills + action icons within the card instead of
      overflowing past its right edge on narrow (mobile / sub-vendo) cards. */
   flex-wrap: wrap;
   justify-content: flex-end;
   min-width: 0;
`;

const IconActionBtn = styled.button`
   width: 28px;
   height: 28px;
   border-radius: 6px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   display: inline-flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   padding: 0;
   transition: background 0.12s, border-color 0.12s;
   &:hover {
      background: ${colors.GRAY14};
      border-color: ${colors.GRAY10};
   }
`;

const KebabWrap = styled.div`
   position: relative;
`;

const KebabBtn = styled(IconActionBtn)``;

const KebabMenu = styled.div`
   position: absolute;
   top: calc(100% + 4px);
   right: 0;
   z-index: 10;
   min-width: 140px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
   padding: 4px;
   display: flex;
   flex-direction: column;
`;

const KebabItem = styled.button<{ $danger?: boolean }>`
   text-align: left;
   padding: 8px 10px;
   border: none;
   background: transparent;
   border-radius: 6px;
   font-size: 12px;
   font-weight: 600;
   color: ${({ $danger }) => ($danger ? colors.DANGER : colors.GRAY_DARK)};
   cursor: pointer;
   &:hover {
      background: ${({ $danger }) =>
         $danger ? `${colors.DANGER}10` : colors.GRAY14};
   }
`;

const CompactIp = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   font-size: 11px;
   color: ${colors.GRAY3};
   padding: 4px 0;
`;

const SubStack = styled.div`
   margin-left: 12px;
   padding-left: 12px;
   border-left: 2px dashed ${colors.GRAY12};
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const SubCardHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
   flex-wrap: wrap;
   margin-bottom: 6px;
`;

const SubCardBody = styled.div`
   display: flex;
   gap: 12px;
   flex-wrap: wrap;
`;

const SubStat = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;
`;

const SubStatValue = styled.p`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
`;

const InheritNote = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   font-style: italic;
`;

const TdSubtext = styled.span`
   font-size: 10px;
   color: ${colors.GRAY5};
   font-weight: 400;
`;

/* License banners & pills */

/**
 * The banner across the top of a device card.
 *
 * 🔴 A brand-new vendo that has not powered on yet used to read
 * "🔒 License inactive — portal is locked", and an operator concluded the
 * 14-day free trial was no longer offered — he asked Ray about it. The trial
 * has not been withdrawn; it has not STARTED, because it starts on the first
 * heartbeat. utils/firstHeartbeat.ts holds the rule and the wording.
 */
/**
 * Does this card get the licence banner (Activate / Renew)? Owned cards
 * that are not yet active, and cards the viewer SOLD — the seller may still
 * activate for the buyer, on the seller's credits. Never on a locked or
 * stolen vendo.
 */
export function showLicenseBanner(d: {
   isCurrentOwner?: boolean;
   licenseStatus: LicenseStatus;
   ownershipLockReason?: string | null;
   markedStolen?: boolean;
}): boolean {
   return !isLicensed(d.licenseStatus) && !d.ownershipLockReason && !d.markedStolen;
}

/** The NodeMCU was skipped at registration (or never flashed): no NodeMCU
 *  has ever reported for this vendo. Only boards that HAVE a NodeMCU step —
 *  OrangePi reads coins on-board and never shows it. Owner's own cards only. */
export function showCoinBoxBanner(d: {
   isCurrentOwner?: boolean;
   coinbox: 'none' | 'online' | 'offline';
   nodeMcuNeverFlashed?: boolean;
   markedStolen?: boolean;
   deviceId: string;
   hwBoard?: string;
   portalMode?: 'coin' | 'voucher' | 'both';
}): boolean {
   if (!d.isCurrentOwner || d.markedStolen) return false;
   // A NodeMCU MAC typed at registration but never flashed reads 'offline',
   // and the card said "waiting for your NodeMCU to power on" — the operator
   // waited on a board that had nothing on it (RJE-676CAE18, 2026-10-30).
   if (d.coinbox !== 'none' && !(d.coinbox === 'offline' && d.nodeMcuNeverFlashed)) return false;
   if (d.portalMode === 'voucher') return false; // Voucher only: no NodeMCU needed
   if (isOrangePiDeviceId(d.deviceId) || isOrangePiBoard(d.hwBoard)) return false;
   return true;
}

/** Voucher-only vendo with no NodeMCU: a calm note, not a warning (Ray 2026-10-15). */
export function showVoucherOnlyNote(d: {
   isCurrentOwner?: boolean;
   coinbox: 'none' | 'online' | 'offline';
   markedStolen?: boolean;
   portalMode?: 'coin' | 'voucher' | 'both';
}): boolean {
   return !!d.isCurrentOwner && !d.markedStolen && d.coinbox === 'none' && d.portalMode === 'voucher';
}

/** Not yet heartbeated: the licence row is the "waiting for power" one. */
export function isWaitingForPower(d: { licenseStatus: LicenseStatus; trialStartedAt?: string | null; hwBoard?: string; coinbox?: 'none' | 'online' | 'offline'; status?: string }): boolean {
   return licenseBanner({ licenseStatus: d.licenseStatus, trialStartedAt: d.trialStartedAt ?? null, hwBoard: d.hwBoard, nodeMcuOnline: d.coinbox === 'online', routerOnline: d.status === 'online' })?.ctaPrimary === false;
}

function VoucherOnlyNote() {
   return (
      <VoucherOnlyBox role="status">
         <LicenseBannerText>🎟 Voucher only — no NodeMCU needed on this vendo.</LicenseBannerText>
      </VoucherOnlyBox>
   );
}

function CoinBoxBanner({ onFlash, trialWaiting = false }: { onFlash: () => void; trialWaiting?: boolean }) {
   return (
      <CoinBoxBannerBox role="status">
         <LicenseBannerText>
            {trialWaiting
               ? '🔌 NodeMCU not flashed for this vendo yet — flash it and your free trial starts by itself the moment it connects.'
               : "🔌 NodeMCU not flashed yet — coins can't be inserted. Vouchers and online payments still work."}
         </LicenseBannerText>
         <LicenseBannerBtn onClick={onFlash}>Flash NodeMCU</LicenseBannerBtn>
      </CoinBoxBannerBox>
   );
}

function DeviceLicenseBanner({
   licenseStatus,
   trialStartedAt,
   trialDaysLeft,
   autoRenewLicense,
   soldToEmail,
   onActivate,
   voucherOnly = false,
   hwBoard,
   nodeMcuOnline = false,
   routerOnline = false,
}: {
   licenseStatus: LicenseStatus;
   trialStartedAt: string | null;
   /** The board: an OrangePi has no NodeMCU to wait for. */
   hwBoard?: string;
   /** The NodeMCU is reporting: an unactivated vendo then needs a credit. */
   nodeMcuOnline?: boolean;
   /** The router is checking in: on an OrangePi that is the hardware proof. */
   routerOnline?: boolean;
   trialDaysLeft?: number;
   autoRenewLicense?: boolean;
   /** Set on a card the viewer SOLD — the banner says who it is for. */
   soldToEmail?: string;
   onActivate: () => void;
   /** Voucher-only vendo: no NodeMCU will ever report, so there is no
    *  "waiting for power" — the owner activates with a credit (Ray 2026-10-15). */
   voucherOnly?: boolean;
}) {
   const banner = voucherOnly
      ? licenseBanner({ licenseStatus, trialStartedAt: trialStartedAt ?? new Date(0).toISOString(), hwBoard })
      : licenseBanner({ licenseStatus, trialStartedAt, hwBoard, nodeMcuOnline, routerOnline });
   const waiting = !voucherOnly && banner?.ctaPrimary === false;
   const disabled = !voucherOnly && banner?.ctaDisabled === true;
   const ownText =
      licenseStatus === 'trial'
         ? autoRenewLicense
            ? `⏱ Trial · ${trialDaysLeft ?? 0} day${trialDaysLeft === 1 ? '' : 's'} left · auto-activates at end`
            : `⏱ Trial · ${trialDaysLeft ?? 0} day${trialDaysLeft === 1 ? '' : 's'} left`
         : banner?.text;
   const text = soldToEmail
      ? `${ownText} · sold to ${soldToEmail} — you can activate it for them, 1 of your credits`
      : ownText;
   return (
      <LicenseBanner $expired={licenseStatus === 'expired'} $waiting={waiting}>
         <LicenseBannerText>{text}</LicenseBannerText>
         {waiting ? (
            /* Nothing to press yet — the guide is "power it on" (Ray 2026-10-15). */
            <WaitingPlug aria-hidden title={isOrangePiBoard(hwBoard) ? "Power on the OrangePi" : "Power on the NodeMCU"}>
               <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 2v6M15 2v6M7 8h10v4a5 5 0 0 1-10 0V8zM12 17v5" />
               </svg>
            </WaitingPlug>
         ) : (
            <LicenseBannerBtn onClick={disabled ? undefined : onActivate} disabled={disabled}>
               {licenseStatus === 'expired' ? 'Renew Now' : 'Activate'}
            </LicenseBannerBtn>
         )}
      </LicenseBanner>
   );
}

const VoucherOnlyBox = styled.div`
   display: flex;
   align-items: center;
   margin: 0 0 10px;
   padding: 8px 12px;
   border-radius: 10px;
   border: 1px solid ${colors.GRAY13};
   background: ${colors.GRAY14};
   color: ${colors.GRAY3};
`;
const CoinBoxBannerBox = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   flex-wrap: wrap;
   margin: 0 0 10px;
   padding: 10px 12px;
   border-radius: 10px;
   /* theme tokens: the text is var(--app-text), so a hardcoded light box
      went white-on-white in dark mode (Ray 2026-10-25) */
   border: 1px solid color-mix(in srgb, ${colors.PRIMARY} 35%, transparent);
   background: var(--app-brand-tint);
   color: #1e2a6b;
`;
const WaitingPlug = styled.span`
   display: inline-grid;
   place-items: center;
   width: 38px;
   height: 38px;
   border-radius: 10px;
   background: ${colors.GRAY14};
   color: ${colors.GRAY3};
   flex-shrink: 0;
`;
const LicenseBanner = styled.div<{ $expired: boolean; $waiting?: boolean }>`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   margin: -4px -4px 10px -4px;
   padding: 8px 12px;
   border-radius: 8px;
   /* A vendo waiting for its first power-on is not a fault, so it does not
      get a warning colour. Amber here is what made a brand-new device read as
      broken on the shelf. */
   background: ${({ $expired, $waiting }) =>
      $waiting
         ? `${colors.GRAY13}55`
         : $expired
           ? `${colors.DANGER}15`
           : `${colors.WARNING}15`};
   border: 1px solid
      ${({ $expired, $waiting }) =>
         $waiting
            ? colors.GRAY13
            : $expired
              ? `${colors.DANGER}40`
              : `${colors.WARNING}40`};
`;

const LicenseBannerText = styled.span`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
   flex: 1;
`;

const LicenseBannerBtn = styled.button<{ $secondary?: boolean }>`
   /* On a vendo that has not powered on, the operator's job is to plug it
      in — not to buy a licence — so the waiting state renders this disabled. */
   background: ${({ $secondary }) => ($secondary ? 'transparent' : colors.PRIMARY)};
   color: ${({ $secondary }) => ($secondary ? colors.GRAY5 : 'white')};
   border: ${({ $secondary }) =>
      $secondary ? `1px solid ${colors.GRAY12}` : 'none'};
   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
   }
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover { opacity: 0.9; }
`;

const StolenBanner = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   margin: -4px -4px 10px -4px;
   padding: 8px 12px;
   border-radius: 8px;
   background: ${colors.DANGER}12;
   border: 1px solid ${colors.DANGER}55;
`;

const StolenBannerText = styled.span`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.DANGER};
   flex: 1;
`;

const StolenBannerBtn = styled.button`
   background: ${colors.DANGER};
   color: white;
   border: none;
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover { opacity: 0.9; }
`;

const StolenChip = styled.button`
   margin-left: 8px;
   background: ${colors.DANGER}12;
   color: ${colors.DANGER};
   border: 1px solid ${colors.DANGER}55;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover { background: ${colors.DANGER}20; }
`;

const NoRouterBanner = styled.div`
   margin: -4px -4px 10px -4px;
   padding: 8px 12px;
   border-radius: 8px;
   background: ${colors.GRAY}10;
   border: 1px solid ${colors.GRAY}40;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY};
`;

const NoRouterChip = styled.span`
   margin-left: 8px;
   background: ${colors.GRAY}12;
   color: ${colors.GRAY};
   border: 1px solid ${colors.GRAY}40;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 600;
   white-space: nowrap;
`;

const SettingUpBanner = styled.div`
   margin: -4px -4px 10px -4px;
   padding: 8px 12px;
   border-radius: 8px;
   background: ${colors.PRIMARY}10;
   border: 1px solid ${colors.PRIMARY}40;
   font-size: 11px;
   font-weight: 600;
   line-height: 1.5;
   color: ${colors.PRIMARY};
`;

const SettingUpChip = styled.span`
   margin-left: 8px;
   background: ${colors.PRIMARY}12;
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY}40;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 600;
   white-space: nowrap;
`;

// Firmware-update pill on the device card. Neutral (blue) while updating, red
// when the agent reports the flash failed after its retries.
const OtaPill = styled.span<{ $failed: boolean }>`
   margin-left: 8px;
   background: ${({ $failed }) => ($failed ? colors.DANGER : colors.PRIMARY)}12;
   color: ${({ $failed }) => ($failed ? colors.DANGER : colors.PRIMARY)};
   border: 1px solid
      ${({ $failed }) => ($failed ? colors.DANGER : colors.PRIMARY)}40;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 600;
   white-space: nowrap;
`;

// "Online but the cloud can't reach in" warning (inbound probe cron).
// Orange, not red: customers are unaffected — only instant push and the
// Awaiting tab degrade. The tooltip carries the full explanation.
const ReachPill = styled.span`
   margin-left: 8px;
   background: ${colors.WARNING}1f;
   color: ${colors.WARNING};
   border: 1px solid ${colors.WARNING}55;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 600;
   white-space: nowrap;
   cursor: help;
`;

// Friendly update/reboot pill (2d) — driven by server updateState so it
// stays visible through the reboot gap where the agent (otaStatus) is off.
const UpdatePill = styled.span<{ $rebooting: boolean }>`
   margin-left: 8px;
   background: ${colors.WARNING}1f;
   color: ${colors.WARNING};
   border: 1px solid ${colors.WARNING}55;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 600;
   white-space: nowrap;
`;

const OwnershipLockBanner = styled.div<{ $stolen: boolean }>`
   margin: -4px -4px 10px -4px;
   padding: 8px 12px;
   border-radius: 8px;
   font-size: 11px;
   font-weight: 600;
   line-height: 1.5;
   background: ${({ $stolen }) =>
      $stolen ? `${colors.DANGER}12` : `${colors.WARNING}15`};
   border: 1px solid
      ${({ $stolen }) => ($stolen ? `${colors.DANGER}55` : `${colors.WARNING}40`)};
   color: ${({ $stolen }) => ($stolen ? colors.DANGER : colors.MAIN_DARK)};
`;

const ActiveLicensePill = styled.span`
   display: inline-block;
   padding: 1px 7px;
   border-radius: 10px;
   font-size: 9px;
   font-weight: 700;
   background: ${colors.SUCCESS}15;
   color: ${colors.SUCCESS};
`;

const LicensePill = styled.span<{ $status: LicenseStatus }>`
   display: inline-block;
   padding: 2px 10px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 600;
   cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
   background: ${({ $status }) =>
      isLicensed($status)
         ? `${colors.SUCCESS}18`
         : $status === 'trial'
         ? `${colors.WARNING}18`
         : `${colors.DANGER}18`};
   color: ${({ $status }) =>
      isLicensed($status)
         ? colors.SUCCESS
         : $status === 'trial'
         ? colors.WARNING
         : colors.DANGER};
   &:hover {
      opacity: ${({ onClick }) => (onClick ? 0.8 : 1)};
   }
`;

const SubDeviceCard = styled.div<{ $online: boolean }>`
   /* White like the main card — the grey read as "disabled" (Ray, 2026-09-29). */
   background: ${colors.WHITE};
   border: 1px solid ${({ $online }) => ($online ? `${colors.SUCCESS}30` : colors.GRAY12)};
   border-radius: 10px;
   padding: 12px 14px;
`;

const AddSubBtn = styled.button`
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px dashed ${colors.PRIMARY}60;
   border-radius: 8px;
   padding: 8px 12px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   transition: all 0.15s;
   &:hover:not(:disabled) {
      background: ${colors.PRIMARY}10;
   }
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      border-style: solid;
      color: ${colors.GRAY5};
      border-color: ${colors.GRAY12};
   }
`;

const DeviceCardHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
   flex-wrap: wrap;
   margin-bottom: 4px;
`;

const DeviceHeaderLeft = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   flex-wrap: wrap;
   min-width: 0;
   flex: 1 1 auto;
`;

const DeviceName = styled.p`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   word-break: break-word;
   min-width: 0;
`;

const SubName = styled.p`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
`;

const TypePill = styled.span<{ $main?: boolean }>`
   display: inline-block;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   background: ${({ $main }) => ($main ? `${colors.PRIMARY}15` : `${colors.ORANGE}22`)};
   color: ${({ $main }) => ($main ? colors.PRIMARY : colors.ORANGE)};
`;

// Router-model chip on the main device (hEX / hAP lite / CCR…). Neutral
// styling so it reads as hardware info, not a status.
/* Product type, beside the board badge. Quiet by design — it identifies the
   device, it does not report health — and always shown, so cloud is stated
   rather than implied by absence. */
const VariantBadge = styled.span<{ $hybrid: boolean }>`
   display: inline-block;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0.02em;
   background: ${({ $hybrid }) => ($hybrid ? `${colors.ORANGE}1f` : `${colors.PRIMARY}12`)};
   color: ${({ $hybrid }) => ($hybrid ? colors.ORANGE : colors.PRIMARY)};
`;

/* Shown ONLY when something is actually waiting — an always-present "0" trains
   people to ignore the corner. Count, not a dot, because a box can have two
   independent updates (router and coin board) and one is not the other. */
const UpdateCountBadge = styled.span`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   min-width: 20px;
   height: 20px;
   padding: 0 6px;
   border-radius: 10px;
   background: ${colors.ORANGE};
   color: ${colors.WHITE};
   font-size: 11px;
   font-weight: 800;
   line-height: 1;
   cursor: default;
`;

const ModelBadge = styled.span`
   display: inline-block;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0.02em;
   background: ${colors.GRAY12};
   color: ${colors.GRAY3};
`;

const TypeCell = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   flex-wrap: wrap;
`;

const VlanChip = styled.span`
   display: inline-block;
   padding: 1px 7px;
   border-radius: 8px;
   font-size: 10px;
   font-weight: 700;
   font-family: 'SF Mono', Menlo, monospace;
   letter-spacing: 0.02em;
   background: ${colors.CYAN}1f;
   color: ${colors.CYAN};
   cursor: help;
`;

const SoldPill = styled.span`
   display: inline-block;
   padding: 1px 7px;
   border-radius: 8px;
   font-size: 9px;
   font-weight: 800;
   letter-spacing: 0.06em;
   background: ${colors.GRAY10}55;
   color: ${colors.GRAY3};
`;

const SoldHint = styled.span`
   display: block;
   font-size: 10px;
   font-weight: 500;
   color: ${colors.GRAY5};
   margin-top: 2px;
   cursor: help;
`;

const Redacted = styled.span`
   color: ${colors.GRAY8};
   font-style: italic;
   cursor: help;
`;

/* Activity pulse for sold rows — actual earnings stay hidden, but the
 * seller can see at a glance whether their old device is producing
 * transactions today. Useful for "is the buyer running it?" check. */
const pulse = keyframes`
   0%, 100% { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
   50% { box-shadow: 0 0 0 4px transparent; opacity: 0.7; }
`;

const ActivityBadge = styled.span<{ $active: boolean }>`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 3px 8px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 600;
   cursor: help;
   background: ${({ $active }) =>
      $active ? `${colors.SUCCESS}1f` : `${colors.GRAY12}`};
   color: ${({ $active }) => ($active ? colors.SUCCESS : colors.GRAY5)};
`;

const ActivityDot = styled.span<{ $active: boolean }>`
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background: ${({ $active }) =>
      $active ? colors.SUCCESS : colors.GRAY8};
   ${({ $active }) =>
      $active &&
      css`
         animation: ${pulse} 1.6s ease-in-out infinite;
      `};
`;

const StatusDot = styled.div<{ $online: boolean }>`
   width: 8px;
   height: 8px;
   border-radius: 50%;
   background: ${({ $online }) => ($online ? colors.SUCCESS : colors.DANGER)};
`;

const MetaLine = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   margin-bottom: 8px;
   flex-wrap: wrap;
`;

const MetaChip = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   font-family: monospace;
`;

const MetaDot = styled.span`
   color: ${colors.GRAY10};
`;

// Subtle version pill for the router-firmware / coin-box-firmware chips on the
// device cards. Sits inline in the MetaLine next to the id + heartbeat.
const VerChip = styled.span`
   font-size: 10px;
   font-family: monospace;
   color: ${colors.GRAY5};
   background: ${colors.GRAY13};
   border-radius: 6px;
   padding: 1px 6px;
   white-space: nowrap;
`;

// Column count follows the number of stats actually rendered. It was hardcoded
// to three, so the optional Uptime stat wrapped onto a second row BY ITSELF and
// left a band of empty space down every card. A stat that is present only
// sometimes cannot share a fixed grid.
const DeviceStatsRow = styled.div<{ $cols?: number }>`
   display: grid;
   grid-template-columns: repeat(${({ $cols }) => $cols ?? 3}, minmax(0, 1fr));
   gap: 12px;
`;

const DeviceStat = styled.div`
   min-width: 0;
`;

const DeviceStatLabel = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const DeviceStatValue = styled.p`
   font-size: 14px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
`;

const DeviceStatNote = styled.p`
   font-size: 10px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const NetworkRow = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 12px;
   margin-top: 8px;
   padding-top: 8px;
   border-top: 1px solid ${colors.GRAY13};
`;

const NetworkCell = styled.div`
   min-width: 0;
`;

const NetworkLabel = styled.p`
   font-size: 10px;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const NetworkValue = styled.p`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
   font-family: monospace;
`;

const SubNetworkRow = styled.div`
   display: flex;
   gap: 12px;
   margin-top: 8px;
   padding-top: 8px;
   border-top: 1px dashed ${colors.GRAY12};
`;

const SubActionsRow = styled.div`
   display: flex;
   gap: 6px;
   margin-top: 8px;
   padding-top: 8px;
   border-top: 1px dashed ${colors.GRAY12};
`;

const SubNetworkItem = styled.div``;

const SubNetworkValue = styled.p`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
   font-family: monospace;
`;

const DeviceDetailRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-top: 10px;
   padding-top: 10px;
   border-top: 1px solid ${colors.GRAY12};
   gap: 8px;
   flex-wrap: wrap;
`;

const DeviceActions = styled.div`
   display: flex;
   gap: 6px;
`;

const SubsPill = styled.button<{ $open: boolean }>`
   background: ${({ $open }) => ($open ? `${colors.PRIMARY}15` : colors.GRAY14)};
   color: ${({ $open }) => ($open ? colors.PRIMARY : colors.GRAY3)};
   border: 1px solid ${({ $open }) => ($open ? `${colors.PRIMARY}30` : colors.GRAY12)};
   padding: 4px 10px;
   border-radius: 20px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   display: flex;
   align-items: center;
   gap: 4px;
   white-space: nowrap;
   &:hover {
      background: ${colors.PRIMARY}12;
      color: ${colors.PRIMARY};
   }
`;

const SubsArrow = styled.span<{ $open: boolean }>`
   display: inline-block;
   transition: transform 0.15s;
   transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
`;

// Quiet by design: this sits in a dense table and is information, not an
// alarm. "not yet carried" is a normal state right after granting — the phone
// collects its pass on its next portal refresh.
const CarriedTag = styled.span<{ $ok?: boolean }>`
   display: block;
   margin-top: 2px;
   font-size: 11px;
   color: ${({ $ok }) => ($ok ? colors.GRAY5 : colors.WARNING)};
`;

const SmallBtn = styled.button<{ $danger?: boolean }>`
   background: ${({ $danger }) => ($danger ? `${colors.DANGER}12` : colors.GRAY14)};
   color: ${({ $danger }) => ($danger ? colors.DANGER : colors.GRAY1)};
   border: 1px solid ${({ $danger }) => ($danger ? `${colors.DANGER}30` : colors.GRAY12)};
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 500;
   cursor: pointer;
   white-space: nowrap;
   &:hover:not(:disabled) { opacity: 0.8; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* ─── Transfer Credit modal ────────────────────────────────────────── */

const TModalOverlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0, 0, 0, 0.5);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 1000;
   padding: 16px;
   /* On phones the centered card overflowed with no scroll, pushing the title
      and the close button off-screen. Dock it to the bottom as a slide-up
      sheet instead. */
   @media (max-width: 640px) {
      align-items: flex-end;
      padding: 0;
   }
`;
const sheetUp = keyframes`
   from { transform: translateY(100%); }
   to { transform: translateY(0); }
`;
const TModalCard = styled.div`
   background: ${colors.WHITE};
   border-radius: 14px;
   padding: 22px;
   width: 100%;
   max-width: 460px;
   max-height: 88vh;
   overflow-y: auto;
   box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
   @media (max-width: 640px) {
      max-width: 100%;
      max-height: 90vh;
      border-radius: 18px 18px 0 0;
      padding: 20px 16px calc(20px + env(safe-area-inset-bottom));
      animation: ${sheetUp} 0.24s ease both;
   }
`;
const TModalCloseX = styled.button`
   position: absolute;
   top: 14px;
   right: 14px;
   width: 28px;
   height: 28px;
   border: none;
   border-radius: 50%;
   background: ${colors.GRAY14};
   color: ${colors.GRAY4};
   font-size: 13px;
   line-height: 1;
   cursor: pointer;
   &:hover {
      background: ${colors.GRAY13};
      color: ${colors.MAIN_DARK};
   }
`;
const TModalTitle = styled.h3`
   margin: 0 0 4px;
   font-size: 18px;
   color: ${colors.MAIN_DARK};
`;
const TModalSub = styled.p`
   margin: 0 0 14px;
   font-size: 13px;
   color: ${colors.GRAY5};
   line-height: 1.5;
`;
const TModalLabel = styled.label`
   display: block;
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY3};
   margin: 12px 0 4px;
`;
const TModalRow = styled.div`
   display: flex;
   gap: 8px;
`;
const TModalInput = styled.input`
   width: 100%;
   font-family: inherit;
   font-size: 14px;
   padding: 9px 11px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   box-sizing: border-box;
   &:focus { outline: none; border-color: ${colors.PRIMARY}; }
`;
const TModalCheckBtn = styled.button`
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   padding: 0 16px;
   border-radius: 8px;
   border: 1px solid ${colors.PRIMARY};
   background: ${colors.WHITE};
   color: ${colors.PRIMARY};
   cursor: pointer;
   white-space: nowrap;
   &:disabled { opacity: 0.5; cursor: default; }
`;
const TModalFound = styled.div`
   margin-top: 8px;
   font-size: 13px;
   font-weight: 600;
   color: ${colors.SUCCESS};
`;
const TModalErr = styled.div`
   margin-top: 4px;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.DANGER};
`;
const TModalFooter = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 20px;
`;
const TModalCancel = styled.button`
   font-family: inherit;
   font-size: 13px;
   font-weight: 600;
   padding: 9px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   color: ${colors.GRAY3};
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: default; }
`;
const TModalSend = styled.button`
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   padding: 9px 18px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: default; }
`;

const ExpandBtn = styled.button`
   background: transparent;
   border: none;
   color: ${colors.GRAY3};
   font-size: 14px;
   cursor: pointer;
   padding: 4px;
   &:hover { color: ${colors.PRIMARY}; }
`;

const SubIndent = styled.span`
   padding-left: 16px;
   color: ${colors.GRAY3};
   font-size: 13px;
`;

/* ─── Filters ──────────────────────────────────────────────── */

const FilterBar = styled.div`
   display: flex;
   gap: 6px;
   align-items: center;
   flex-wrap: wrap;
`;

const FilterChip = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY14)};
   color: ${({ $active }) => ($active ? 'white' : colors.GRAY3)};
   border: 1px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   padding: 4px 12px;
   border-radius: 20px;
   font-size: 12px;
   font-weight: 500;
   cursor: pointer;
   white-space: nowrap;
`;

const DeviceSelect = styled.select`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 20px;
   padding: 4px 10px;
   font-size: 12px;
   color: ${colors.GRAY1};
   cursor: pointer;
   font-family: inherit;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;
const MacSearchInput = styled.input`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 20px;
   padding: 4px 12px;
   font-size: 12px;
   color: ${colors.GRAY1};
   font-family: inherit;
   width: 140px;
   &::placeholder {
      color: ${colors.GRAY5};
   }
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const FilterNote = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   padding: 6px 10px;
   background: ${colors.PRIMARY}08;
   border-left: 3px solid ${colors.PRIMARY};
   border-radius: 4px;
   margin-bottom: 10px;
   strong { color: ${colors.MAIN_DARK}; }
`;

/* ─── Table ────────────────────────────────────────────────── */

const TableWrapper = styled.div`
   overflow-x: auto;
   margin-bottom: 24px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   background: ${colors.WHITE};
`;

const StyledTable = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
`;

/** Loading placeholders for the device and session lists (Ray 2026-10-18:
 *  "Loading…" text read as a blank page). */
const DeviceCardsSkeleton = () => (
   <MainCardGrid aria-busy="true" aria-label="Loading devices">
      {Array.from({ length: 6 }, (_, i) => (
         <SkeletonCard key={i}>
            <Skeleton $block $w="55%" $h="16px" />
            <Skeleton $block $w="35%" $h="11px" />
            <Skeleton $block $w="100%" $h="64px" $radius="8px" />
            <Skeleton $block $w="70%" $h="11px" />
         </SkeletonCard>
      ))}
   </MainCardGrid>
);
const TableSkeleton = ({ cols }: { cols: number }) => (
   <TableWrapper aria-busy="true" aria-label="Loading">
      <StyledTable>
         <tbody>
            <SkeletonRows rows={8} cols={cols} Cell={Td} />
         </tbody>
      </StyledTable>
   </TableWrapper>
);
const SkeletonCard = styled.div`
   display: flex; flex-direction: column; gap: 10px;
   padding: 16px; border: 1px solid ${colors.GRAY13}; border-radius: 8px; background: ${colors.WHITE};
`;

/* Phones: the pinned column is Settings only — adding a sub-vendo is a
   desktop job (Ray 2026-10-18). */
const ListAddSubBtn = styled(SmallBtn)`
   @media (max-width: 768px) { display: none; }
`;

const NameBtn = styled.button`
   border: 0; background: none; padding: 0;
   font: inherit; font-weight: inherit; color: inherit; text-align: left; cursor: pointer;
   &:hover { text-decoration: underline; }
`;

const Th = styled.th<{ $stickyRight?: boolean }>`
   text-align: left;
   padding: 10px 12px;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   background: ${colors.GRAY14};
   white-space: nowrap;
   &:first-child { border-top-left-radius: 10px; }
   &:last-child { border-top-right-radius: 10px; }
   /* The Actions column stays pinned to the right so its buttons are always
      visible — no horizontal scroll needed to reach them (Ray 2026-10-11). */
   ${({ $stickyRight }) =>
      $stickyRight &&
      `
      position: sticky;
      right: 0;
      z-index: 3;
      box-shadow: inset 1px 0 0 ${colors.GRAY12};
   `}
`;

/**
 * Operator note on a session row. Click to edit, Enter or click away saves,
 * Escape cancels. Read-only (plain text) when the table has no saver — the
 * sub-login portal, for instance.
 */
export const SessionNoteCell = ({
   id,
   note,
   onSave,
}: {
   id: string;
   note: string;
   onSave?: (id: string, note: string) => Promise<void> | void;
}) => {
   const [editing, setEditing] = useState(false);
   const [draft, setDraft] = useState(note);
   const [saving, setSaving] = useState(false);
   useEffect(() => {
      if (!editing) setDraft(note);
   }, [note, editing]);

   if (!onSave) {
      return note ? <NoteText title={note}>{note}</NoteText> : <NoteEmpty>—</NoteEmpty>;
   }

   const commit = async () => {
      const next = draft.trim().slice(0, 200);
      setEditing(false);
      if (next === note) return;
      setSaving(true);
      try {
         await onSave(id, next);
      } finally {
         setSaving(false);
      }
   };

   if (editing) {
      return (
         <NoteInput
            autoFocus
            value={draft}
            maxLength={200}
            placeholder="e.g. Juan, room 3"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void commit()}
            onKeyDown={(e) => {
               if (e.key === 'Enter') {
                  e.preventDefault();
                  void commit();
               } else if (e.key === 'Escape') {
                  setDraft(note);
                  setEditing(false);
               }
            }}
            aria-label="Session note"
         />
      );
   }
   return (
      <NoteButton
         type="button"
         onClick={() => setEditing(true)}
         disabled={saving}
         title={note || 'Add a note'}
         $empty={!note}
      >
         {saving ? 'Saving…' : note || 'Add note'}
      </NoteButton>
   );
};

const NoteText = styled.span`
   display: inline-block;
   max-width: 180px;
   overflow: hidden;
   text-overflow: ellipsis;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;
const NoteEmpty = styled.span`
   color: ${colors.GRAY5};
`;
const NoteButton = styled.button<{ $empty: boolean }>`
   background: transparent;
   border: 1px dashed ${({ $empty }) => ($empty ? colors.GRAY12 : 'transparent')};
   border-radius: 4px;
   padding: 2px 6px;
   max-width: 180px;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   font-size: 12px;
   color: ${({ $empty }) => ($empty ? colors.GRAY5 : colors.MAIN_DARK)};
   cursor: text;
   text-align: left;
   &:hover {
      border-color: ${colors.GRAY12};
   }
`;
const NoteInput = styled.input`
   width: 180px;
   font-size: 12px;
   padding: 3px 6px;
   border: 1px solid ${colors.BLUE};
   border-radius: 4px;
   outline: none;
`;

const TopUpChips = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 4px;
   max-width: 240px;
`;

const TopUpToggle = styled.button`
   display: block;
   margin-top: 6px;
   background: none;
   border: 1px solid ${colors.GRAY12};
   color: ${colors.BLUE};
   font-size: 11px;
   font-weight: 500;
   padding: 2px 8px;
   border-radius: 6px;
   cursor: pointer;
   &:hover { opacity: 0.8; }
`;

const TopUpsCell = styled.td`
   background: ${colors.GRAY14};
   border-top: 1px solid ${colors.GRAY13};
   padding: 12px 16px 14px 40px;
`;

const TopUpsTitle = styled.div`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   margin-bottom: 6px;
`;

const TopUpsList = styled.table`
   border-collapse: collapse;
   font-size: 13px;
   th {
      text-align: left;
      font-size: 11px;
      font-weight: 600;
      color: ${colors.GRAY5};
      padding: 4px 16px 4px 0;
   }
   td {
      padding: 4px 16px 4px 0;
      color: ${colors.MAIN_DARK};
      white-space: nowrap;
   }
`;

const TopUpCode = styled.span`
   font-family: monospace;
   font-size: 12px;
   letter-spacing: 0.04em;
`;

const VoucherCodeTag = styled.div`
   margin-top: 2px;
   font-family: monospace;
   font-size: 11px;
   letter-spacing: 0.04em;
   color: ${colors.GRAY3};
`;

const Td = styled.td<{ $mono?: boolean; $bold?: boolean; $muted?: boolean; $indent?: boolean; $stickyRight?: boolean }>`
   padding: 10px 12px;
   color: ${({ $muted }) => ($muted ? colors.GRAY5 : colors.MAIN_DARK)};
   border-top: 1px solid ${colors.GRAY13};
   font-family: ${({ $mono }) => ($mono ? 'monospace' : 'inherit')};
   font-size: ${({ $mono }) => ($mono ? '12px' : '13px')};
   font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
   white-space: nowrap;
   /* Pin the Actions cell to the right edge (matches the header above). */
   ${({ $stickyRight }) =>
      $stickyRight &&
      `
      position: sticky;
      right: 0;
      z-index: 2;
      background: ${colors.WHITE};
      box-shadow: inset 1px 0 0 ${colors.GRAY13};
   `}
`;

const MethodBadge = styled.span<{ $method: string }>`
   display: inline-block;
   padding: 2px 8px;
   border-radius: 4px;
   font-size: 11px;
   font-weight: 600;
   text-transform: uppercase;
   background: ${({ $method }) =>
      $method === 'coin' ? `${colors.WARNING}18` :
      $method === 'gcash' || $method === 'online' ? `${colors.BLUE}18` :
      `${colors.TEAL}18`};
   color: ${({ $method }) =>
      $method === 'coin' ? colors.WARNING :
      $method === 'gcash' || $method === 'online' ? colors.BLUE :
      colors.TEAL};
`;

// Virtual whitelist rows ("permanent free internet") — purple to stand
// apart from every payment method.
const WhitelistBadge = styled.span`
   display: inline-block;
   padding: 2px 8px;
   border-radius: 4px;
   font-size: 11px;
   font-weight: 600;
   text-transform: uppercase;
   background: ${colors.PURPLE}18;
   color: ${colors.PURPLE};
`;

const StatusBadge = styled.span<{ $status: string }>`
   display: inline-block;
   padding: 2px 8px;
   border-radius: 4px;
   font-size: 11px;
   font-weight: 600;
   background: ${({ $status }) =>
      $status === 'active' || $status === 'online' ? `${colors.SUCCESS}18` :
      $status === 'paused' ? `${colors.WARNING}18` :
      $status === 'offline' ? `${colors.DANGER}18` :
      `${colors.GRAY8}`};
   color: ${({ $status }) =>
      $status === 'active' || $status === 'online' ? colors.SUCCESS :
      $status === 'paused' ? colors.WARNING :
      $status === 'offline' ? colors.DANGER :
      colors.GRAY5};
   text-transform: capitalize;
`;

const TimeDisplay = styled.div`
   display: flex;
   align-items: baseline;
`;

const TimeRemaining = styled.span<{ $low: boolean }>`
   font-weight: 700;
   color: ${({ $low }) => ($low ? colors.DANGER : colors.MAIN_DARK)};
`;

const UsageCell = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   font-variant-numeric: tabular-nums;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;

const UsageRate = styled.span`
   font-size: 10px;
   color: ${colors.GRAY8};
`;

const UsageMuted = styled.span`
   color: ${colors.GRAY5};
   font-size: 12px;
`;

/* Validity column. Quiet by default — the operator scans this column only when
   a customer is complaining, and a table where every row shouts is a table
   nobody reads. It goes amber inside the last hour and red once the deadline
   has passed, because those are the two rows worth finding. */
/**
 * "Pause left" — click to change how much pause budget the customer has left,
 * or keep it for ever (Ray, 2026-09-15). Read-only on expired rows and where
 * the table has no saver.
 */
export const PauseLeftCell = ({
   id,
   msLeft,
   windowMin,
   spentMin,
   editable,
   onSave,
}: {
   id: string;
   msLeft: number | null;
   windowMin: number | null;
   spentMin: number;
   editable: boolean;
   onSave?: (id: string, minutesLeft: number | null) => Promise<void> | void;
}) => {
   const [editing, setEditing] = useState(false);
   const [draft, setDraft] = useState<number | null>(null);
   const [saving, setSaving] = useState(false);

   const label = msLeft === null ? '—' : formatPauseLeft(msLeft);
   const title =
      msLeft === null
         ? 'Paused time is kept for ever. Click to set a limit.'
         : `Pause validity ${formatHM(windowMin ?? 0)} · ${formatHM(spentMin)} used. ` +
           `Counts only while the customer is away, and coming back does not reset it. Click to change.`;

   const commit = async (minutesLeft: number | null) => {
      if (!onSave) return;
      setSaving(true);
      try {
         await onSave(id, minutesLeft);
         setEditing(false);
      } finally {
         setSaving(false);
      }
   };

   if (editing) {
      return (
         <PauseLeftEditor onClick={(e) => e.stopPropagation()}>
            <DurationUnitInput
               value={draft}
               onChange={(mins) => setDraft(mins)}
               min={1}
               compact
            />
            <SmallBtn
               onClick={() => draft && draft > 0 && void commit(draft)}
               disabled={saving || !draft || draft <= 0}
            >
               {saving ? 'Saving…' : 'Set'}
            </SmallBtn>
            <SmallBtn onClick={() => void commit(null)} disabled={saving}>
               Keep for ever
            </SmallBtn>
            <SmallBtn onClick={() => setEditing(false)} disabled={saving}>
               Cancel
            </SmallBtn>
         </PauseLeftEditor>
      );
   }
   if (!editable) {
      return msLeft === null ? (
         <ValidityMuted title={title}>—</ValidityMuted>
      ) : (
         <ValidityCell $soon={msLeft > 0 && msLeft < 60 * 60_000} $gone={msLeft <= 0} title={title}>
            {label}
         </ValidityCell>
      );
   }
   return (
      <PauseLeftButton
         type="button"
         title={title}
         onClick={() => {
            setDraft(msLeft !== null && msLeft > 0 ? Math.max(1, Math.round(msLeft / 60_000)) : 1440);
            setEditing(true);
         }}
      >
         {msLeft === null ? (
            <ValidityMuted>—</ValidityMuted>
         ) : (
            <ValidityCell $soon={msLeft > 0 && msLeft < 60 * 60_000} $gone={msLeft <= 0}>
               {label}
            </ValidityCell>
         )}
         <PencilIcon aria-hidden>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
               <path d="M12 20h9" />
               <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
         </PencilIcon>
      </PauseLeftButton>
   );
};

const PauseLeftButton = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   background: transparent;
   border: 1px dashed transparent;
   border-radius: 4px;
   padding: 2px 6px;
   cursor: pointer;
   &:hover {
      border-color: ${colors.GRAY12};
   }
`;
const PencilIcon = styled.span`
   display: inline-flex;
   color: ${colors.GRAY5};
   line-height: 0;
`;
const PauseLeftEditor = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   flex-wrap: wrap;
`;

const ValidityCell = styled.span<{ $soon?: boolean; $gone?: boolean }>`
   font-size: 12px;
   font-weight: ${({ $soon, $gone }) => ($soon || $gone ? 700 : 500)};
   color: ${({ $soon, $gone }) =>
      $gone ? colors.DANGER : $soon ? colors.WARNING : colors.GRAY8};
   white-space: nowrap;
`;

/* No deadline: a session bought before validity existed, or an Unlimited
   whitelist row. A plain dash, not "no expiry" — spelling it out on every old
   row reads like a setting the operator got wrong. */
const ValidityMuted = styled.span`
   color: ${colors.GRAY5};
   font-size: 12px;
`;

const TimeTotal = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-left: 2px;
`;

// Data-rate session cell — replaces TimeDisplay for rows where
// payment.dataPlan is set. Shows "X used / Y cap" with a progress bar.
const DataCellWrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   min-width: 110px;
`;

const DataCellTop = styled.div`
   display: flex;
   align-items: baseline;
`;

const DataCellUsed = styled.span<{ $low: boolean }>`
   font-weight: 700;
   color: ${({ $low }) => ($low ? colors.DANGER : colors.MAIN_DARK)};
`;

const DataCellCap = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-left: 2px;
`;

const DataCellBar = styled.div`
   height: 5px;
   background: ${colors.GRAY14};
   border-radius: 3px;
   overflow: hidden;
`;

const DataCellBarFill = styled.div<{ $pct: number; $low: boolean }>`
   height: 100%;
   width: ${(p) => Math.min(100, Math.max(0, p.$pct))}%;
   background: ${(p) => p.$low ? colors.DANGER : colors.PRIMARY};
   border-radius: 3px;
   transition: width 0.5s ease-out;
`;

const TableActions = styled.div`
   display: flex;
   gap: 4px;
`;

/* ─── Settings ─────────────────────────────────────────────── */

const SettingsGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, 1fr);
   gap: 16px;
   @media (max-width: 768px) {
      grid-template-columns: 1fr;
   }
`;

const SettingsSection = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   padding: 16px;
`;

const SettingRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 8px 0;
   border-bottom: 1px solid ${colors.GRAY13};
   &:last-of-type { border-bottom: none; }
`;

const SettingLabel = styled.p`
   font-size: 13px;
   color: ${colors.GRAY3};
`;

const SettingValue = styled.p`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
`;

const SettingChip = styled.span<{ $on: boolean }>`
   font-size: 11px;
   font-weight: 600;
   padding: 2px 10px;
   border-radius: 10px;
   background: ${({ $on }) => ($on ? `${colors.SUCCESS}18` : `${colors.GRAY8}`)};
   color: ${({ $on }) => ($on ? colors.SUCCESS : colors.GRAY5)};
`;

/* ─── "Setup in progress" section ─────────────────────────── */

const IncompleteSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 12px 14px;
   background: ${colors.WARNING}08;
   border: 1px solid ${colors.WARNING}40;
   border-radius: 8px;
   margin-bottom: 12px;
`;

const OwnershipSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 12px 14px;
   background: ${colors.DANGER}08;
   border: 1px solid ${colors.DANGER}40;
   border-radius: 8px;
   margin-bottom: 12px;
`;
const OwnershipHeader = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 13px;
   font-weight: 700;
   color: ${colors.DANGER};
`;
const OwnershipCount = styled.span`
   font-size: 10px;
   font-weight: 700;
   padding: 2px 7px;
   border-radius: 10px;
   background: ${colors.DANGER}20;
   color: ${colors.DANGER};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;
const OwnershipHint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   margin: 0;
`;
const OwnershipRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
   padding: 10px 12px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.DANGER}30;
   border-radius: 8px;
`;
const OwnershipMain = styled.div`
   display: flex;
   flex-direction: column;
   gap: 3px;
   min-width: 200px;
`;
const OwnershipSerial = styled.span`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const OwnershipMeta = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const OwnershipActions = styled.div`
   display: flex;
   gap: 8px;
   flex-shrink: 0;
`;
const OwnershipGrant = styled.button`
   border: none;
   border-radius: 8px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 600;
   color: #fff;
   background: ${colors.PRIMARY};
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const OwnershipDeny = styled.button`
   border: 1px solid #e57373;
   border-radius: 8px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 600;
   color: #c62828;
   background: #fff;
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const IncompleteHeader = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const IncompleteCount = styled.span`
   font-size: 10px;
   font-weight: 700;
   padding: 2px 7px;
   border-radius: 10px;
   background: ${colors.WARNING}25;
   color: ${colors.MAIN_DARK};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const IncompleteHint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
`;

const IncompleteRow = styled.div`
   display: grid;
   grid-template-columns: 200px 1fr auto;
   align-items: center;
   gap: 12px;
   padding: 8px 12px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   @media (max-width: 720px) {
      grid-template-columns: 1fr;
      gap: 8px;
   }
`;

const IncompleteRowName = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const SetupSteps = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
`;

const SetupDot = styled.span<{ $status: 'done' | 'current' | 'pending' }>`
   width: 8px;
   height: 8px;
   border-radius: 50%;
   flex-shrink: 0;
   ${({ $status }) => {
      if ($status === 'done') return `background: ${colors.SUCCESS};`;
      if ($status === 'current')
         return `background: ${colors.PRIMARY}; box-shadow: 0 0 0 3px ${colors.PRIMARY}25;`;
      return `background: ${colors.GRAY12};`;
   }}
`;

const SetupLabel = styled.span`
   font-size: 11px;
   color: ${colors.GRAY3};
   margin-left: 8px;
`;

const IncompleteActions = styled.div`
   display: flex;
   gap: 6px;
   align-items: center;
`;

/* Compact sub-vendo card rows (2026-10-09). */
const SubPillRow = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   flex-wrap: nowrap;
   margin: 6px 0 4px;
   min-width: 0;
`;

const SubSpacer = styled.span`
   flex: 1;
`;

const SubStatsRow = styled.div`
   display: flex;
   align-items: baseline;
   flex-wrap: wrap;
   gap: 4px 12px;
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const SubInlineStat = styled.span`
   white-space: nowrap;
   b {
      color: ${colors.MAIN_DARK};
      font-size: 12px;
      font-weight: 600;
      margin-left: 3px;
   }
`;
